import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  SubModuleComponent,
  ValidationRule,
  ComponentType,
  InputType,
} from "../../services/types/ComponentTypes";
import { TextField } from "./TextField";
import { SelectDropdown } from "./SelectDropdown";
import { SearchableDropdown } from "./SearchableDropdown";
import { DateField } from "./DateField";
import { BatchAllocationField } from "./BatchAllocationField";
import { useAppSelector, useAppDispatch } from "../../state/hooks";
import {
  fetchLookup,
  fetchDependentLookup,
  fetchSubModuleRawData,
  clearLookup,
  addLookupItem,
} from "../../state/slices/lookupsSlice";
import { LookupOption } from "../../services/api";
import { apiService } from "../../services/api";
import {
  getParentIdFromFormData,
  hasPersistedSubModuleRows,
  mergePurchaseReturnSavedRows,
  mergeSalesReturnSavedRows,
  applyPurchaseReturnInvoiceTemplateRow,
  normalizePurchaseReturnSubmoduleRow,
  isSubModuleRowSoftDeleted,
  isPersistedSubModuleRowForEdit,
  mergeSubmoduleRowsById,
} from "../../state/utils/configUtils";
import {
  INVOICE_SERVICE_PARENT_LOOKUPS,
  resolveInvoiceLookupParentId,
  enrichInvoiceSpareRowsWithDiscounts,
} from "../../utils/invoiceSpareDiscountUtils";
import {
  applyPurchaseSupplierVatToRow,
  applyPurchaseSupplierVatToRows,
  getSupplierVatExemptionFromFormData,
  isPurchaseDetailsSubmodule,
} from "../../utils/supplierVatUtils";
import {
  hasCreditDebitNoteSplitRows,
  isCreditDebitNoteSplitSubmoduleLookup,
  mergeCreditDebitNoteSplitSavedRows,
  normalizeCreditDebitNoteSplitRow,
  resolveCreditDebitNoteSavedSplitRows,
} from "../../utils/creditDebitNoteUtils";

/** Submodules that auto-fill one party (customer/supplier) ledger row when parent is selected. */
const PARTY_LEDGER_SUBMODULE_LOOKUPS = new Set([
  "credit_note_ledgers_with_parent_id",
  "debit_note_ledgers_with_parent_id",
]);

function isPartyLedgerPlaceholderRow(
  row: Record<string, any> | null | undefined
): boolean {
  if (!row || typeof row !== "object") return true;
  const ledger = row.drp_ledger ?? row.ledger_id ?? "";
  return String(ledger).trim() === "";
}

function submoduleRowsAreOnlyPartyLedgerPlaceholders(
  rows: Record<string, any>[] | undefined
): boolean {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.every((r) => isPartyLedgerPlaceholderRow(r));
}

function enrichPartyLedgerSubmoduleRow(
  row: Record<string, any>,
  lookupItem: Record<string, any>
): Record<string, any> {
  const r = { ...row };
  const ledgerId =
    lookupItem.ledger_id ??
    lookupItem.ledgerid ??
    lookupItem.id ??
    lookupItem.value ??
    r.ledger_id ??
    r.drp_ledger;
  const label =
    lookupItem.ledger_name ??
    lookupItem.ledger_id_desc ??
    lookupItem.suppliername ??
    lookupItem.customername ??
    lookupItem.text ??
    lookupItem.name ??
    lookupItem.label ??
    "";
  if (ledgerId !== undefined && ledgerId !== null && String(ledgerId).trim() !== "") {
    const lid = String(ledgerId);
    r.drp_ledger = lid;
    r.ledger_id = lid;
  }
  if (label !== undefined && label !== null && String(label).trim() !== "") {
    const desc = String(label);
    r.drp_ledger_desc = desc;
    r.ledger_id_desc = desc;
  }
  return r;
}

function buildPartyLedgerFallbackRow(
  parentValue: string,
  formData: Record<string, any>,
  lookupName: string
): Record<string, any> {
  const isSupplier = lookupName === "debit_note_ledgers_with_parent_id";
  const selected = isSupplier
    ? formData.drp_supplier_selectedData || formData.supplier_id_selectedData || {}
    : formData.drp_customer_selectedData || formData.customer_id_selectedData || {};
  const label =
    selected.suppliername ??
    selected.customername ??
    selected.name ??
    selected.label ??
    (isSupplier ? formData.drp_supplier_desc : formData.drp_customer_desc) ??
    "";
  return enrichPartyLedgerSubmoduleRow(
    {
      txt_credit: "0",
      credit: "0",
      txt_remarks: "",
      remarks: "",
    },
    {
      id: parentValue,
      ledger_id: parentValue,
      ledger_name: label,
      suppliername: label,
      customername: label,
    }
  );
}

function seedPartyLedgerDropdownOption(
  dispatch: ReturnType<typeof useAppDispatch>,
  childControls: any[] | undefined,
  row: Record<string, any>
): void {
  if (!childControls?.length) return;
  const ledgerId = row.drp_ledger ?? row.ledger_id;
  const desc = row.drp_ledger_desc ?? row.ledger_id_desc ?? "";
  if (!ledgerId || !String(desc).trim()) return;
  for (const control of childControls) {
    const c = control as any;
    const bind = c.BindValue || c.name;
    if (bind !== "drp_ledger") continue;
    const columnLookup = c.LookupName || c.lookupName;
    if (!columnLookup) continue;
    dispatch(
      addLookupItem({
        lookupKey: columnLookup,
        item: {
          id: ledgerId,
          value: ledgerId,
          label: String(desc),
          name: String(desc),
        },
      })
    );
  }
}

const PURCHASE_PENDING_PARTS_LOOKUP = "purchase_pending_parts";

const resolvePurchasePendingParentId = (
  formData: Record<string, any>
): string | number | null => {
  const purchaseRecordId = String(
    formData.id ?? formData.purchase_id ?? ""
  ).trim();

  const selected = formData.job_card_id_selected ?? formData.job_card_id;
  if (
    selected !== undefined &&
    selected !== null &&
    String(selected).trim() !== "" &&
    (!purchaseRecordId || String(selected).trim() !== purchaseRecordId)
  ) {
    return selected;
  }

  const rowSources = [
    formData.jobcard_details,
    formData.purchase_details,
    formData.job_card_details,
  ];
  for (const rows of rowSources) {
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      if (!row || typeof row !== "object") continue;
      const jc = row.job_card_id_item ?? row.job_card_id;
      if (
        jc !== undefined &&
        jc !== null &&
        String(jc).trim() !== "" &&
        (!purchaseRecordId || String(jc).trim() !== purchaseRecordId)
      ) {
        return jc;
      }
    }
  }
  return null;
};

const resolveSubmoduleParentValue = (
  formData: Record<string, any>,
  parentField: string | undefined,
  lookupName?: string
): string | number | null => {
  if (lookupName === PURCHASE_PENDING_PARTS_LOOKUP) {
    return resolvePurchasePendingParentId(formData);
  }
  if (lookupName && INVOICE_SERVICE_PARENT_LOOKUPS.has(lookupName)) {
    let value = resolveInvoiceLookupParentId(formData, parentField);
    if (!value && (parentField === "parentid" || parentField === "parentid1")) {
      value = formData.estimate_id ?? formData.estimation_id ?? null;
    }
    return value;
  }
  let value = getParentIdFromFormData(formData, parentField);
  if (!value && (parentField === "parentid" || parentField === "parentid1")) {
    value = formData.estimate_id ?? formData.estimation_id ?? null;
  }
  return value;
};
import { showConfirmDialog } from "../../state/utils/confirmationDialogUtils";
import { getSubmoduleShowEditDelete } from "../../services/userPreferences";
import {
  runConditionalValidations,
  isConditionallyRequired,
  resolveNumericInputMin,
  allowsNegativeNumericInput,
  resolveNumericInputMax,
} from "../../state/utils/conditionalValidationUtils";
import {
  applyOptionLock,
  getAccountMasterKeyFromConfig,
  getOptionLockKey,
  resolveControlLookupKey,
} from "../../utils/lookupUtils";
import {
  formatNumericDecimal,
  roundNumericDecimal,
} from "../../utils/numberFormatUtils";
import {
  openPDFViewer,
  setPDFViewerLoading,
  setPDFViewerError,
} from "../../state/slices/pdfViewerSlice";
import {
  openHTMLDrawer,
  setHTMLDrawerLoading,
  setHTMLDrawerError,
} from "../../state/slices/htmlDrawerSlice";
import { authService } from "../../services/auth";
import {
  applyReceiptSelectionToInvoiceRow,
  BILL_ALLOC_RECEIPT_FIELD,
  findUnregisteredBaseRow,
  parseAllocationNumber,
  getLedgerIdFromInvoiceRow,
  isBillAllocationInvoiceSubmodule,
  isBillAllocationUnregisteredSubmodule,
  recalcBillAllocationInvoiceGrid,
  resolveBillAllocationModuleKeys,
  resolveBillAllocationReceiptField,
  resolveReceiptRawItem,
  stampUnregisteredBaseRows,
  syncBillAllocationUnregisteredPreview,
  validateBillAllocationReceiptPools,
  VENDOR_BILL_ALLOC_PAYMENT_FIELD,
} from "../../utils/billAllocationUtils";
import {
  applyInvoiceSelectionToReceiptRow,
  BILL_WISE_RELEASE_ALLOCATE_FIELD,
  BILL_WISE_RELEASE_INVOICE_BASE_KEY,
  BILL_WISE_RELEASE_INVOICE_BIND,
  BILL_WISE_RELEASE_INVOICE_FIELD,
  BILL_WISE_RELEASE_RECEIPT_BIND,
  VENDOR_BILL_WISE_RELEASE_PURCHASE_BASE_KEY,
  VENDOR_BILL_WISE_RELEASE_PURCHASE_BIND,
  VENDOR_BILL_WISE_RELEASE_PURCHASE_FIELD,
  VENDOR_BILL_WISE_RELEASE_RECEIPT_BIND,
  resolveBillWiseReleaseDocumentField,
  capAllocateAmountOnReceiptRow,
  clearBillWiseReleaseInvoiceSelection,
  findInvoiceRowInGrid,
  getInvoiceIdFromRow,
  BILL_WISE_RELEASE_RELEASE_FIELD,
  isBillWiseReleaseInvoiceSubmodule,
  isBillWiseReleaseReceiptSubmodule,
  recalcBillWiseReleaseInvoiceGrid,
  recalcBillWiseReleasePaymentSourcesGrid,
  recalcBillWiseReleaseSubmoduleByConfig,
  recalcBillWiseReleaseReceiptGrid,
  resolveInvoiceRawItem,
  stampInvoiceOriginalAmounts,
  syncBillWiseReleaseInvoicePreview,
} from "../../utils/billWiseReleaseUtils";

/** Lookup key for a sub-module column. Submodule parentField applies only when the column declares Parent_Field. */
function childControlLookupKey(
  lookupName: string,
  control: Record<string, any> | null | undefined,
  formData: Record<string, any>
): string {
  return resolveControlLookupKey(lookupName, control, formData);
}

function childControlParentField(
  control: Record<string, any> | null | undefined
): string | undefined {
  const pf = control?.Parent_Field || control?.parentField;
  return pf ? String(pf) : undefined;
}

/** Align prefilled/API subform rows with table BindValues (drp_*, txt_*) so cells render before lookups load. */
function normalizeSubModuleRowForDisplay(row: Record<string, any>): Record<string, any> {
  if (!row || typeof row !== "object") return row;
  const r: Record<string, any> = { ...row };

  const syncPair = (bindKey: string, jsonKey: string) => {
    const b = r[bindKey];
    const j = r[jsonKey];
    if ((b === undefined || b === null || b === "") && j !== undefined && j !== null && j !== "") {
      r[bindKey] = j;
    }
    if ((j === undefined || j === null || j === "") && b !== undefined && b !== null && b !== "") {
      r[jsonKey] = b;
    }
  };

  syncPair("drp_ledger", "to_ledger");
  syncPair("drp_ledger", "ledger_id");
  syncPair("drp_to_ledger", "to_ledger");
  syncPair("drp_from_ledger", "ledger_id");
  syncPair("drp_vat", "vat_id");
  syncPair("txt_to_narration", "to_narration");
  syncPair("txt_remarks", "remarks");
  syncPair("txt_unit_discount", "unit_discount");
  syncPair("txt_line_discount", "line_discount");
  syncPair("txt_unregistered_amount", "unregistered_amount");
  syncPair("txt_receipt_amount", "receipt_amount");
  syncPair("txt_reciept_no", "reciept_no");
  syncPair("txt_receipt_date", "receipt_date");
  syncPair("txt_amount_paid", "amount_paid");
  syncPair("txt_allocate_amount", "amount");
  syncPair("txt_credit", "credit");

  if (
    (r.txt_amount === undefined || r.txt_amount === null || r.txt_amount === "") &&
    r.amount !== undefined &&
    r.amount !== null &&
    r.amount !== ""
  ) {
    r.txt_amount = String(r.amount);
  }
  if (
    (r.amount === undefined || r.amount === null || r.amount === "") &&
    r.txt_amount !== undefined &&
    r.txt_amount !== null &&
    r.txt_amount !== ""
  ) {
    r.amount = r.txt_amount;
  }

  const salesReturnPairs: [string, string][] = [
    ["txt_src_line_id", "src_line_id"],
    ["drp_code", "codeid"],
    ["txt_narration", "narration"],
    ["txt_inv_qty", "inv_qty"],
    ["txt_rate", "rate"],
    ["txt_unit_discount", "unit_discount"],
    ["txt_line_discount", "line_discount"],
    ["txt_amount", "amount"],
    ["drp_vat", "vat_id"],
    ["txt_vat_amt", "vat_amt"],
    ["txt_net_amt", "net_amt"],
    ["txt_return_qty", "return_qty"],
    ["txt_return_net_amt", "return_net_amt"],
    ["drp_department", "department_id"],
    ["drp_jobtype", "jobtypeid"],
    ["txt_item_type", "item_type"],
    ["txt_item_name", "item_name"],
  ];
  for (const [bindKey, jsonKey] of salesReturnPairs) {
    syncPair(bindKey, jsonKey);
  }

  const purchaseLinePairs: [string, string][] = [
    ["job_card_id_item", "job_card_id"],
    ["drp_product", "product_id"],
    ["txt_itemname", "item_name"],
    ["txt_rate", "rate"],
    ["txt_qty", "qty"],
    ["txt_unit_discount", "unit_discount"],
    ["txt_line_discount", "line_discount"],
    ["txt_amount", "amount"],
    ["drp_vat", "vat_id"],
    ["txt_vat_amt", "vat_amt"],
    ["txt_net_amt", "net_amt"],
    ["txt_part_name", "item_name"],
    ["txt_request_qty", "request_qty"],
    ["txt_issued_qty", "issued_qty"],
    ["txt_balance_qty", "bal_qty"],
  ];
  for (const [bindKey, jsonKey] of purchaseLinePairs) {
    syncPair(bindKey, jsonKey);
  }

  return r;
}

/** Lookup-backed dropdown/select controls in subform grids. */
function isSubModuleLookupDropdown(control: any): boolean {
  const lookup = control?.lookupName || control?.LookupName;
  if (!lookup) return false;
  return (
    control.componentType === ComponentType.SELECT_DROPDOWN ||
    control.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
    control.componentType === "selectDropdown" ||
    control.componentType === "searchableDropdown" ||
    control.ControlType === "Search_Select" ||
    control.ControlType === "Select"
  );
}

/**
 * When auto-populating subform rows from a parent lookup, never inject the source
 * row's `id` into dropdown fields unless Json_Attribute is literally "id".
 * Prevents e.g. parts_request_details.id appearing in a product code (codeid) column.
 */
function applySubModuleDropdownValueFromLookupRow(
  control: any,
  jsonAttribute: string,
  lookupItem: Record<string, any>,
  value: any
): any {
  const isBatchAllocationControl =
    control?.componentType === ComponentType.BATCH_ALLOCATION ||
    control?.componentType === "batchAllocation";

  // Keep batch-allocation payload as structured data (array/object).
  // Converting it to string breaks source-mode parsing in BatchAllocationField.
  if (isBatchAllocationControl) {
    return value ?? "";
  }

  if (!isSubModuleLookupDropdown(control)) {
    if (value === undefined || value === null) return "";
    return String(value);
  }

  const isEmpty =
    value === undefined || value === null || String(value).trim() === "";

  if (isEmpty) {
    if (jsonAttribute === "id") {
      const fallback = lookupItem.id ?? lookupItem.value ?? "";
      return fallback !== undefined && fallback !== null && String(fallback).trim() !== ""
        ? String(fallback)
        : "";
    }
    return "";
  }

  return String(value);
}

/** Parse a tax rate from lookup label/description (e.g. "5%", "5", "VAT 5%"). */
function parseTaxRatePercent(desc: unknown): string | null {
  if (desc === undefined || desc === null) return null;
  const s = String(desc).trim();
  if (!s) return null;
  const withPct = s.match(/(\d+(?:\.\d+)?)\s*%/);
  if (withPct) return withPct[1];
  if (/^\d+(?:\.\d+)?$/.test(s)) return s;
  return null;
}

const LOOKUP_ROW_JOIN_FIELDS = ["taxrate", "sale_rate", "purchase_rate"] as const;
const LOOKUP_ROW_METADATA_FIELDS = ["product_id"] as const;

/** Preserve *_desc and joined lookup fields (taxrate, etc.) from API rows. */
function copyLookupMetadataToRow(
  row: Record<string, any>,
  lookupItem: Record<string, any>
): void {
  if (!lookupItem || typeof lookupItem !== "object") return;
  Object.keys(lookupItem).forEach((key) => {
    if (!key.endsWith("_desc")) return;
    const desc = lookupItem[key];
    if (desc === undefined || desc === null || String(desc).trim() === "") return;
    if (
      row[key] === undefined ||
      row[key] === null ||
      String(row[key]).trim() === ""
    ) {
      row[key] = desc;
    }
  });
  for (const key of LOOKUP_ROW_JOIN_FIELDS) {
    const joined = lookupItem[key];
    if (joined === undefined || joined === null || String(joined).trim() === "") {
      continue;
    }
    if (
      row[key] === undefined ||
      row[key] === null ||
      String(row[key]).trim() === ""
    ) {
      row[key] = joined;
    }
  }
  for (const key of LOOKUP_ROW_METADATA_FIELDS) {
    const meta = lookupItem[key];
    if (meta === undefined || meta === null || String(meta).trim() === "") {
      continue;
    }
    if (
      row[key] === undefined ||
      row[key] === null ||
      String(row[key]).trim() === ""
    ) {
      row[key] = meta;
    }
  }
}

const AMOUNT_COLUMN_MIN_WIDTH = "120px";
const QTY_RATE_COLUMN_MIN_WIDTH = "100px";
const CODE_COLUMN_MIN_WIDTH = "210px";
const NARRATION_COLUMN_MIN_WIDTH = "300px";
const SUBMODULE_ACTIONS_COLUMN_CLASS =
  "w-11 min-w-11 max-w-11 p-1 text-center whitespace-nowrap";

/** Column widths for estimation/invoice line grids so amount totals stay visible without zooming out. */
function getSubModuleColumnCellStyle(control: any): React.CSSProperties {
  if (!control) return {};

  const controlWidth = control.ControlWidth || control.controlWidth;
  if (controlWidth) {
    return { minWidth: controlWidth, width: controlWidth };
  }

  const label = String(control.Label || control.label || "").toLowerCase().trim();
  const fieldName = String(control.name || control.BindValue || "").toLowerCase();

  const isCodeColumn =
    label === "code" &&
    (control.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
      control.componentType === "searchableDropdown");

  if (isCodeColumn) {
    return { minWidth: CODE_COLUMN_MIN_WIDTH };
  }

  const isQtyColumn =
    label === "qty" ||
    label === "return qty" ||
    fieldName === "txt_qty" ||
    fieldName === "txt_return_qty" ||
    fieldName === "txt_inv_qty" ||
    fieldName.endsWith("_qty");
  const isRateColumn = label === "rate" || fieldName === "txt_rate";
  if (isQtyColumn || isRateColumn) {
    return { minWidth: QTY_RATE_COLUMN_MIN_WIDTH, width: QTY_RATE_COLUMN_MIN_WIDTH };
  }

  if (
    control.componentType === ComponentType.TEXT_AREA ||
    control.componentType === "textArea" ||
    label === "narration"
  ) {
    return { minWidth: NARRATION_COLUMN_MIN_WIDTH };
  }

  const amountLabels = new Set([
    "amount",
    "total discount",
    "unit discount",
    "vat amount",
    "net amount",
    "return net",
    "return vat",
    "vat%",
  ]);
  const isAmountField =
    amountLabels.has(label) ||
    /txt_(amount|line_discount|unit_discount|vat_amt|net_amt|return_net_amt|sub_amount)/.test(
      fieldName
    ) ||
    /_(amount|vat_amount|net_amount|return_net_amt|line_discount_total)$/.test(
      fieldName
    );

  if (isAmountField) {
    return { minWidth: AMOUNT_COLUMN_MIN_WIDTH, width: AMOUNT_COLUMN_MIN_WIDTH };
  }

  return {};
}

/** Invoice-line lookups used by sales return add screen (not persisted return lines). */
function isSalesReturnInvoiceSubmoduleLookup(lookupName?: string): boolean {
  return (
    typeof lookupName === "string" &&
    lookupName.startsWith("sales_return_invoice_")
  );
}

function isRecordEditScreen(formData?: Record<string, any>): boolean {
  const rid = formData?.id;
  return rid !== undefined && rid !== null && String(rid).trim() !== "";
}

function isPurchaseReturnSubmoduleLookup(lookupName?: string): boolean {
  return lookupName === "purchase_details";
}

const subModuleCellValueEmpty = (v: unknown): boolean =>
  v === undefined || v === null || v === "";

const SubModuleEmptyIcon: React.FC = () => (
  <div className="bg-primary-50 dark:bg-primary-900/30 rounded-full p-3 mb-3">
    <svg
      className="h-8 w-8 text-primary-600 dark:text-primary-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
      />
    </svg>
  </div>
);

const SubModuleEmptyState: React.FC<{
  title?: string;
  message: string;
  showAddButton?: boolean;
  onAdd?: () => void;
  addDisabled?: boolean;
}> = ({
  title = "No records found",
  message,
  showAddButton = false,
  onAdd,
  addDisabled = false,
}) => (
  <div className="p-8 md:p-10 flex flex-col items-center justify-center text-center border-t border-gray-100 dark:border-gray-700/80">
    <SubModuleEmptyIcon />
    <h3 className="text-gray-900 dark:text-gray-100 font-medium text-lg">{title}</h3>
    <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 max-w-sm">{message}</p>
    {showAddButton && onAdd && (
      <button
        type="button"
        className="inline-flex items-center mt-6 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-md transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-70 disabled:cursor-not-allowed"
        onClick={onAdd}
        disabled={addDisabled}
      >
        <svg
          className="h-5 w-5 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
          />
        </svg>
        Add New
      </button>
    )}
  </div>
);

interface SubModuleProps {
  config: SubModuleComponent;
  value?: Record<string, any>[];
  onChange?: (name: string, value: Record<string, any>[]) => void;
  formData?: Record<string, any>; // Add formData prop to access parent field values
  onFormDataChange?: (name: string, value: any) => void; // Add callback to update parent form data
  onRefreshAllIndependentLookups?: () => void; // Callback to refresh all independent lookups on the screen
  externalError?: string;
  /** Form edit mode — used with EnableEditForControl on child fields */
  isEditMode?: boolean;
}

/**
 * Garage360-compatible flag: when EnableEditForControl is false, persisted submodule
 * rows keep this field read-only in edit mode (e.g. journal voucher ledger; amounts still editable).
 */
export function isSubModuleControlLockedInEditMode(
  control: { EnableEditForControl?: boolean },
  row: Record<string, any>,
  isEditMode: boolean
): boolean {
  if (!isEditMode) return false;
  if (control.EnableEditForControl !== false) return false;
  const rowId = row?.id ?? row?.Id;
  if (rowId === undefined || rowId === null || rowId === "") return false;
  const rowIdStr = String(rowId);
  if (rowIdStr.startsWith("new_") || rowIdStr.startsWith("temp_")) return false;
  return true;
}


interface AutoResizeTextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> { }

const AutoResizeTextArea: React.FC<AutoResizeTextAreaProps> = (props) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // 1. Reset height to 'auto' to correctly calculate scrollHeight (shrink if needed)
      textarea.style.height = "auto";

      // 2. Calculate new height: max(scrollHeight + buffer, minHeight of 100px)
      const newHeight = Math.max(textarea.scrollHeight + 2, 80);

      // 3. Set height with !important to override any other styles
      textarea.style.setProperty("height", `${newHeight}px`, "important");
    }
  };

  // Adjust on mount and value change
  React.useLayoutEffect(() => {
    adjustHeight();
  }, [props.value]);

  // Adjust on window resize as well, since width changes affect height
  useEffect(() => {
    window.addEventListener("resize", adjustHeight);
    return () => window.removeEventListener("resize", adjustHeight);
  }, []);

  return (
    <textarea
      ref={textareaRef}
      {...props}
      rows={1} // Force rows to 1 so it starts small and grows via JS
      onChange={(e) => {
        props.onChange?.(e);
        adjustHeight();
      }}
      style={{
        ...props.style,
        overflow: "hidden", // Hide scrollbar to avoid flickering
        resize: "none",
        height: "auto", // Initial style, will be overridden by JS
      }}
    />
  );
};

export const SubModule: React.FC<SubModuleProps> = ({
  config,
  value = [],
  onChange,
  formData = {},
  onFormDataChange,
  onRefreshAllIndependentLookups,
  externalError,
  isEditMode = false,
}) => {
  const [error, setError] = useState<string>("");
  // Initialize data with value prop - this ensures edit mode data is loaded immediately
  const [data, setData] = useState<Record<string, any>[]>(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      return value.map((row) => normalizeSubModuleRowForDisplay({ ...row }));
    }
    return [];
  });
  // Track if table should be shown (when user clicks "Add New" or when data exists)
  const [showTable, setShowTable] = useState<boolean>(() => {
    // Show table if there's already data (edit mode)
    return value && Array.isArray(value) && value.length > 0;
  });
  // Search functionality
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Edit functionality is removed, rows are always editable

  const lookupsState = useAppSelector((state) => state.lookups);
  const dispatch = useAppDispatch();
  const previousParentValueRef = useRef<any>(undefined);
  const lastPopulatedDataRef = useRef<string>("");
  const isInitialMountRef = useRef(true);
  const lastLookupDataRef = useRef<any[]>([]);
  const lastRawDataPopulateKeyRef = useRef<string>("");
  const lastValueRef = useRef<string>("");
  /** Persisted sales-return lines from GET /submodule (edit) — merged onto invoice template rows. */
  const savedSalesReturnRowsRef = useRef<Record<string, any>[]>([]);
  /** Persisted purchase-return lines from GET /submodule (edit) — not purchase invoice template. */
  const savedPurchaseReturnRowsRef = useRef<Record<string, any>[]>([]);
  /** Persisted credit/debit note split lines from GET /submodule (edit). */
  const savedCreditDebitNoteSplitRowsRef = useRef<Record<string, any>[]>([]);
  const onChangeRef = useRef(onChange);
  const isUpdatingFromInternalRef = useRef(false);
  const previousFooterValuesRef = useRef<Record<string, number>>({});
  const onFormDataChangeRef = useRef(onFormDataChange);
  const requestedLookupsRef = useRef<Set<string>>(new Set());
  const searchableSearchDebounceRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});
  const previousChildControlsRef = useRef<string>("");
  const lastPurchaseSupplierVatKeyRef = useRef<string>("");
  const lastProcessedParentValueRef = useRef<string | undefined>(undefined);
  /** Tracks parent id only for clearing lookup request flags — must not update previousParentValueRef. */
  const previousParentForLookupRequestsRef = useRef<string | undefined>(undefined);
  const processedRawDataKeysRef = useRef<Set<string>>(new Set()); // Track which rawData keys we've already processed for ExpressionV2
  const submoduleLookupSeedRef = useRef<string>("");
  /** When true, skip lookup auto-populate after user explicitly cleared all rows. */
  const lookupAutoPopulateSuppressedRef = useRef(false);

  // Get global preference for showing Edit/Delete actions
  const globalShowEditDelete = getSubmoduleShowEditDelete();

  const isReadOnlyView = !!config.disabled;

  // Determine if Edit/Delete should be shown:
  // - Check config flags first (enableDeleteAction)
  // - If not set in config, fall back to global preference
  // - Default to true if nothing is set
  const showDeleteAction =
    !isReadOnlyView &&
    (config.enableDeleteAction !== undefined
      ? config.enableDeleteAction
      : globalShowEditDelete);
  const showRemoveAllAction =
    config.enableRemoveAllAction === true && showDeleteAction;
  const showViewAction = config.viewAction !== undefined;
  const showActionsColumn = showDeleteAction || showViewAction;

  // Keep onChange ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Keep onFormDataChange ref up to date
  useEffect(() => {
    onFormDataChangeRef.current = onFormDataChange;
  }, [onFormDataChange]);

  // Seed lookup option lists so SearchableDropdown can match prefilled ids (bank recon / PDC lines).
  useEffect(() => {
    if (!data?.length || !config.childControls?.length) return;
    const sig = JSON.stringify(
      data.map((r) => ({
        drp_ledger: r.drp_ledger,
        drp_from_ledger: r.drp_from_ledger,
        txt_amount: r.txt_amount,
      }))
    );
    if (sig === submoduleLookupSeedRef.current) return;
    submoduleLookupSeedRef.current = sig;

    for (const row of data) {
      for (const control of config.childControls) {
        const ct = (control as any).componentType;
        if (ct !== ComponentType.SEARCHABLE_DROPDOWN && ct !== "searchableDropdown") continue;
        const c = control as any;
        const fieldName = c.BindValue || c.name;
        const lookupName = c.LookupName || c.lookupName;
        if (!fieldName || !lookupName) continue;
        const idVal = row[fieldName];
        if (idVal === undefined || idVal === null || String(idVal).trim() === "") continue;

        let lookupKey = lookupName;
        const parentField = c.Parent_Field || c.parentField || (config as any).parentField;
        if (parentField && formData) {
          const pv = formData[parentField];
          if (pv !== undefined && pv !== null && String(pv).trim() !== "") {
            lookupKey = `${lookupName}_${pv}`;
          }
        }

        const jsonAttr = c.Json_Attribute;
        const desc =
          row[`${fieldName}_desc`] ||
          (jsonAttr ? row[`${jsonAttr}_desc`] : "") ||
          row.txn_type_label ||
          row.to_ledger_desc ||
          row.ledger_id_desc ||
          "";
        if (!desc || String(desc).trim() === "") continue;

        dispatch(
          addLookupItem({
            lookupKey,
            item: {
              id: idVal,
              value: idVal,
              label: String(desc),
              name: String(desc),
            },
          })
        );
      }
    }
  }, [data, config.childControls, config.name, dispatch, formData]);

  // Helper function to extract property from item with multiple variations
  // CRITICAL: Checks for !== undefined && !== null to handle 0 values correctly
  const extractPropertyValue = (item: any, property: string): any => {
    if (!item || !property) return null;

    const variations = [
      property,
      property.toLowerCase(),
      property.toUpperCase(),
      property.replace(/_/g, ""),
      property.replace(/_/g, "").toLowerCase(),
      property.replace(/_([a-z])/g, (g: string) => g[1].toUpperCase())
    ];

    for (const variation of variations) {
      if (item[variation] !== undefined && item[variation] !== null) {
        return item[variation];
      }
    }

    return null;
  };

  // Helper function to resolve field references (shared between updateRowField and evaluateAllExpressionsForRow)
  const resolveFieldReference = (
    reference: string,
    rowData: Record<string, any>
  ): string | null => {
    const refValue = reference.replace(/[\[\]]/g, "");

    // Handle property access: [field.property]
    if (refValue.includes(".")) {
      const [fieldName, property] = refValue.split(".");
      let fieldValue = rowData[fieldName];

      // CRITICAL FIX: If fieldName not found in rowData, try Json_Attribute
      if (fieldValue === undefined || fieldValue === null) {
        const refControl = config.childControls?.find(
          (c: any) => (c.name || c.BindValue) === fieldName
        );
        if (refControl && (refControl as any).Json_Attribute) {
          fieldValue = rowData[(refControl as any).Json_Attribute];
        }
      }

      // First, check if fieldValue is already an object with the property
      if (fieldValue && typeof fieldValue === "object") {
        const propertyValue =
          fieldValue[property] ||
          fieldValue[property.toLowerCase()] ||
          fieldValue[property.toUpperCase()];
        if (propertyValue !== undefined && propertyValue !== null) {
          return String(propertyValue);
        }
      }

      // CRITICAL: If fieldValue is an ID (string/number), look it up in rawData
      // This handles cases like [drp_code.sale_rate] where drp_code is just an ID
      // and we need to find the code in the lookup data to get sale_rate
      if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
        // Find the control to get lookup information
        const refControl = config.childControls?.find(
          (c: any) => (c.name || c.BindValue) === fieldName
        );

        if (refControl) {
          const refControlAny = refControl as any;
          const lookupName =
            refControlAny?.LookupName || refControlAny?.lookupName;

          if (lookupName) {
            const lookupKey = childControlLookupKey(
              lookupName,
              refControlAny,
              formData
            );

            // CRITICAL: Try rawData FIRST (has all properties like sale_rate, purchase_rate, etc.)
            let rawData = lookupsState.rawData[lookupKey] || [];
            let lookupOptions = lookupsState.lookups[lookupKey] || [];
            if (rawData.length === 0 && lookupKey !== lookupName) {
              rawData = lookupsState.rawData[lookupName] || [];
              lookupOptions = lookupsState.lookups[lookupName] || [];
            }



            // First try to find in rawData (has all properties)
            // CRITICAL: Also match by label/name/description since Default_Value might set the label, not ID
            let rawItem = rawData.find(
              (item: any) =>
                item.id === fieldValue ||
                String(item.id) === String(fieldValue) ||
                item.value === fieldValue ||
                String(item.value) === String(fieldValue) ||
                (item.product_id &&
                  String(item.product_id) === String(fieldValue)) ||
                (item.codeid && String(item.codeid) === String(fieldValue)) ||
                // Match by label/name (for cases like "5%" being the label)
                item.label === fieldValue ||
                String(item.label) === String(fieldValue) ||
                item.name === fieldValue ||
                String(item.name) === String(fieldValue) ||
                item.description === fieldValue ||
                String(item.description) === String(fieldValue) ||
                // Match by tax_name or similar for tax_rates lookup
                item.tax_name === fieldValue ||
                String(item.tax_name) === String(fieldValue)
            );

            // If not found in rawData, try lookup options
            const selectedOption = rawItem
              ? null
              : lookupOptions.find(
                (opt: any) =>
                  opt.value === fieldValue ||
                  String(opt.value) === String(fieldValue) ||
                  opt.id === fieldValue ||
                  String(opt.id) === String(fieldValue) ||
                  // Match by label (for cases like "5%" being the label)
                  opt.label === fieldValue ||
                  String(opt.label) === String(fieldValue)
              );

            // Try to get property value - prefer rawItem, fallback to selectedOption
            let propValue = extractPropertyValue(rawItem, property);
            if (propValue === null && selectedOption) {
              propValue = extractPropertyValue(selectedOption, property);
            }

            if (
              propValue !== null &&
              propValue !== undefined &&
              propValue !== ""
            ) {

              return String(propValue);
            } else {
            }
          }
        }
      }

      // Fallback: taxrate may arrive on the row from estimation/jobcard lookup joins
      if (property.toLowerCase() === "taxrate") {
        const rowTaxrate =
          rowData.taxrate ??
          rowData.vat_taxrate ??
          rowData[`${fieldName}_taxrate`];
        if (
          rowTaxrate !== undefined &&
          rowTaxrate !== null &&
          String(rowTaxrate).trim() !== ""
        ) {
          return String(rowTaxrate);
        }
        const desc =
          rowData[`${fieldName}_desc`] ??
          rowData.vat_id_desc ??
          rowData.drp_vat_desc;
        const parsedRate = parseTaxRatePercent(desc);
        if (parsedRate !== null) {
          return parsedRate;
        }
      }

      // CRITICAL: If property lookup failed, check if the field already has a value in rowData
      // This prevents overwriting correct values that were loaded from backend
      // Only return "0" if the field doesn't already have a value
      const controlWithExpression = config.childControls?.find(
        (c: any) => {
          const expr = (c as any).ExpressionV2;
          return expr && typeof expr === "string" && expr.includes(`[${fieldName}.${property}]`);
        }
      );

      if (controlWithExpression) {
        const expressionFieldName = (controlWithExpression as any).name || (controlWithExpression as any).BindValue;
        const expressionJsonAttr = (controlWithExpression as any).Json_Attribute;

        // Check if the field already has a value (from backend)
        const existingValue = rowData[expressionFieldName] || rowData[expressionJsonAttr];
        if (existingValue !== undefined && existingValue !== null && existingValue !== "" && existingValue !== "0" && existingValue !== 0) {
          // Field already has a value, preserve it instead of overwriting with "0"

          return null; // Return null to preserve existing value
        }
      }

      // If property lookup failed and no existing value, return "0" for numeric calculations
      return "0";
    }

    // Simple field reference: [field]
    // Handle 0, "0", empty string, and undefined/null
    let fieldValue = rowData[refValue];

    // CRITICAL FIX: If refValue not found in rowData, try Json_Attribute
    if (fieldValue === undefined || fieldValue === null) {
      const refControl = config.childControls?.find(
        (c: any) => (c.name || c.BindValue) === refValue
      );
      if (refControl && (refControl as any).Json_Attribute) {
        fieldValue = rowData[(refControl as any).Json_Attribute];
      }
    }

    if (fieldValue !== undefined && fieldValue !== null) {
      // Convert to string, but preserve 0 as "0" for calculations
      if (fieldValue === 0 || fieldValue === "0") {
        return "0";
      }
      if (fieldValue === "" || String(fieldValue).trim() === "") {
        return "0"; // Treat empty as 0 for numeric calculations
      }
      return String(fieldValue);
    }

    // If field doesn't exist, return "0" for numeric calculations
    return "0";
  };

  /** Format ExpressionV2 results using GLOBAL_DECIMAL_PLACES (same as parent form / footer totals). */
  const formatExpressionV2Result = (evaluatedValue: unknown): string =>
    formatNumericDecimal(evaluatedValue);

  // Helper function to evaluate all ExpressionV2 fields for a row
  const evaluateAllExpressionsForRow = (
    rowData: Record<string, any>,
    rowIndex: number
  ): Record<string, any> => {
    if (!config.childControls) return rowData;

    let updatedRowData = { ...rowData };

    // Evaluate all ExpressionV2 fields
    for (const childControl of config.childControls) {
      const childControlAny = childControl as any;
      const expressionV2 = childControlAny.ExpressionV2;

      if (expressionV2 && typeof expressionV2 === "string") {
        const childFieldName =
          childControlAny.name || childControlAny.BindValue;

        // Pattern Matching - Find all field references
        const fieldRefRegex = /\[[\w.()]*\]/g;
        const matches = expressionV2.match(fieldRefRegex) || [];

        if (matches.length === 0) continue;

        // Value Resolution - Replace all references with actual values
        let expression = expressionV2;
        let canEvaluate = true;

        for (const match of matches) {
          const resolvedValue = resolveFieldReference(match, updatedRowData);

          if (resolvedValue !== null) {
            expression = expression.replace(match, resolvedValue);
          } else {
            // CRITICAL: If resolveFieldReference returns null, it might mean:
            // 1. The lookup failed but we want to preserve existing value
            // 2. The field doesn't exist and we should use "0"
            // Check if this is a property lookup that should preserve existing value
            const refValue = match.replace(/[\[\]]/g, "");
            if (refValue.includes(".")) {
              const [fieldName, property] = refValue.split(".");
              const controlWithExpression = config.childControls?.find(
                (c: any) => {
                  const expr = (c as any).ExpressionV2;
                  return expr && typeof expr === "string" && expr.includes(`[${fieldName}.${property}]`);
                }
              );

              if (controlWithExpression) {
                const expressionFieldName = (controlWithExpression as any).name || (controlWithExpression as any).BindValue;
                const expressionJsonAttr = (controlWithExpression as any).Json_Attribute;
                const existingValue = updatedRowData[expressionFieldName] || updatedRowData[expressionJsonAttr];

                // If field has existing value, preserve it by skipping this expression evaluation
                if (existingValue !== undefined && existingValue !== null && existingValue !== "" && existingValue !== "0" && existingValue !== 0) {

                  canEvaluate = false; // Skip evaluation to preserve existing value
                  break; // Exit the match loop
                }
              }
            }

            // If we should continue, use "0" as fallback
            if (canEvaluate) {
              expression = expression.replace(match, "0");
            }
          }
        }

        // Expression Evaluation - Only if all references resolved
        if (canEvaluate) {
          try {
            const evaluatedValue = Function(
              `"use strict"; return (${expression})`
            )();

            if (evaluatedValue !== null && evaluatedValue !== undefined) {
              const resultValue = formatExpressionV2Result(evaluatedValue);
              // Don't overwrite editable fields that already have a value (user may have changed it)
              const isReadOnly =
                childControlAny.disabled || childControlAny.IsReadOnly;
              const currentValue =
                updatedRowData[childFieldName] ??
                updatedRowData[childControlAny.Json_Attribute];
              const hasUserValue =
                currentValue !== undefined &&
                currentValue !== null &&
                currentValue !== "" &&
                currentValue !== "0" &&
                Number(currentValue) !== 0;
              const evaluatedIsZero =
                resultValue === "" ||
                resultValue === "0" ||
                Number(resultValue) === 0;
              if (!isReadOnly && hasUserValue) {
                // Preserve user-edited value (e.g. Rate on purchase screen)
                continue;
              }
              // Read-only calculated fields (e.g. jobcard VAT from inspection): keep API value
              // when taxrate lookup has not loaded yet and expression would zero out vat_amt.
              if (isReadOnly && hasUserValue && evaluatedIsZero) {
                continue;
              }
              updatedRowData[childFieldName] = resultValue;
              if (childControlAny.Json_Attribute) {
                updatedRowData[childControlAny.Json_Attribute] = resultValue;
              }
            }
          } catch (error) {

          }
        }
      }
    }

    return updatedRowData;
  };

  const mergeSavedSalesReturnLines = (
    invoiceRows: Record<string, any>[],
    savedRows?: Record<string, any>[] | null
  ): Record<string, any>[] => {
    const saved =
      savedRows && savedRows.length > 0
        ? savedRows
        : getSavedSalesReturnRows();
    if (!saved?.length) {
      return invoiceRows.map((row, index) =>
        evaluateAllExpressionsForRow(normalizeSubModuleRowForDisplay({ ...row }), index)
      );
    }
    const merged = mergeSalesReturnSavedRows(invoiceRows, saved);
    return merged.map((row, index) =>
      evaluateAllExpressionsForRow(normalizeSubModuleRowForDisplay({ ...row }), index)
    );
  };

  const getSubmoduleLookupName = () =>
    configAny.LookupName || configAny.lookupName || config.lookupName;

  const getSavedSalesReturnRows = (): Record<string, any>[] => {
    if (savedSalesReturnRowsRef.current.length > 0) {
      return savedSalesReturnRowsRef.current;
    }
    if (hasPersistedSubModuleRows(value)) {
      return (value as Record<string, any>[]).map((row) =>
        normalizeSubModuleRowForDisplay({ ...row })
      );
    }
    if (hasPersistedSubModuleRows(data)) {
      return data.map((row) => normalizeSubModuleRowForDisplay({ ...row }));
    }
    return [];
  };

  const rememberSavedSalesReturnRows = (rows: Record<string, any>[]) => {
    if (!hasPersistedSubModuleRows(rows)) return;
    savedSalesReturnRowsRef.current = rows.map((row) =>
      normalizeSubModuleRowForDisplay({ ...row })
    );
  };

  const mergeSavedPurchaseReturnLines = (
    templateRows: Record<string, any>[],
    savedRows?: Record<string, any>[] | null
  ): Record<string, any>[] => {
    const saved =
      savedRows && savedRows.length > 0
        ? savedRows
        : getSavedPurchaseReturnRows();
    if (!saved?.length) {
      return templateRows.map((row, index) =>
        evaluateAllExpressionsForRow(
          normalizeSubModuleRowForDisplay(normalizePurchaseReturnSubmoduleRow({ ...row })),
          index
        )
      );
    }
    const merged = mergePurchaseReturnSavedRows(templateRows, saved);
    return merged.map((row, index) =>
      evaluateAllExpressionsForRow(
        normalizeSubModuleRowForDisplay(normalizePurchaseReturnSubmoduleRow(row)),
        index
      )
    );
  };

  const getSavedPurchaseReturnRows = (): Record<string, any>[] => {
    if (savedPurchaseReturnRowsRef.current.length > 0) {
      return savedPurchaseReturnRowsRef.current;
    }
    if (hasPersistedSubModuleRows(value)) {
      return (value as Record<string, any>[]).map((row) =>
        normalizePurchaseReturnSubmoduleRow(
          normalizeSubModuleRowForDisplay({ ...row })
        )
      );
    }
    if (hasPersistedSubModuleRows(data)) {
      return data.map((row) =>
        normalizePurchaseReturnSubmoduleRow(
          normalizeSubModuleRowForDisplay({ ...row })
        )
      );
    }
    return [];
  };

  const rememberSavedPurchaseReturnRows = (rows: Record<string, any>[]) => {
    if (!hasPersistedSubModuleRows(rows)) return;
    savedPurchaseReturnRowsRef.current = rows.map((row) =>
      normalizePurchaseReturnSubmoduleRow(
        normalizeSubModuleRowForDisplay({ ...row })
      )
    );
  };

  const getSavedCreditDebitNoteSplitRows = (): Record<string, any>[] => {
    const lookupName = getSubmoduleLookupName();
    const resolved = resolveCreditDebitNoteSavedSplitRows(
      formData,
      savedCreditDebitNoteSplitRowsRef.current,
      value,
      lookupName,
      formData?.id
    );
    return resolved.map((row) =>
      normalizeCreditDebitNoteSplitRow(
        normalizeSubModuleRowForDisplay({ ...row }),
        lookupName
      )
    );
  };

  const rememberSavedCreditDebitNoteSplitRows = (rows: Record<string, any>[]) => {
    if (!hasCreditDebitNoteSplitRows(rows, formData?.id)) return;
    const lookupName = getSubmoduleLookupName();
    savedCreditDebitNoteSplitRowsRef.current = rows.map((row) =>
      normalizeCreditDebitNoteSplitRow(
        normalizeSubModuleRowForDisplay({ ...row }),
        lookupName
      )
    );
  };

  const mergeSavedCreditDebitNoteSplitLines = (
    templateRows: Record<string, any>[],
    savedRows?: Record<string, any>[] | null
  ): Record<string, any>[] => {
    const lookupName = getSubmoduleLookupName();
    const saved =
      savedRows && savedRows.length > 0
        ? savedRows
        : getSavedCreditDebitNoteSplitRows();
    const merged = mergeCreditDebitNoteSplitSavedRows(
      templateRows,
      saved,
      lookupName
    );
    return merged.map((row, index) =>
      evaluateAllExpressionsForRow(
        normalizeSubModuleRowForDisplay(row),
        index
      )
    );
  };

  const incomingRowsLoseSavedCreditDebitSplitAmount = (
    incoming: Record<string, any>[],
    saved: Record<string, any>[]
  ): boolean => {
    if (!saved.length || !incoming.length) return false;
    const lookupName = getSubmoduleLookupName();
    const savedByKey = new Map<string, Record<string, any>>();
    for (const row of saved) {
      const key = isCreditDebitNoteSplitSubmoduleLookup(lookupName)
        ? String(row.purchase_no ?? row.txt_purchase_no ?? row.invoice_no ?? row.txt_invoice_no ?? "").trim()
        : "";
      if (key) savedByKey.set(key, row);
    }
    for (const row of incoming) {
      const key = String(
        row.purchase_no ?? row.txt_purchase_no ?? row.invoice_no ?? row.txt_invoice_no ?? ""
      ).trim();
      const baseline = key ? savedByKey.get(key) : undefined;
      if (!baseline) continue;
      const savedAmount = parseFloat(String(baseline.txt_amount ?? baseline.amount ?? 0)) || 0;
      const incomingAmount = parseFloat(String(row.txt_amount ?? row.amount ?? 0)) || 0;
      if (savedAmount > 0.001 && incomingAmount <= 0.001) {
        return true;
      }
    }
    return false;
  };

  const incomingRowsLoseSavedPurchaseReturnFields = (
    incoming: Record<string, any>[],
    saved: Record<string, any>[]
  ): boolean => {
    if (!saved.length || !incoming.length) return false;
    const savedByProduct = new Map<string, Record<string, any>>();
    for (const row of saved) {
      const key = String(row.product_id ?? row.drp_product ?? "").trim();
      if (key) savedByProduct.set(key, row);
    }
    for (const row of incoming) {
      const key = String(row.product_id ?? row.drp_product ?? "").trim();
      const baseline = key ? savedByProduct.get(key) : undefined;
      if (!baseline) continue;
      const baseQty = Number(baseline.qty ?? baseline.txt_qty);
      const inQty = Number(row.qty ?? row.txt_qty);
      if (!Number.isNaN(baseQty) && baseQty > 0 && (Number.isNaN(inQty) || inQty <= 0)) {
        return true;
      }
    }
    return false;
  };

  const incomingRowsLoseSavedReturnFields = (
    incoming: Record<string, any>[],
    saved: Record<string, any>[]
  ): boolean => {
    if (!saved.length || !incoming.length) return false;
    const savedBySrc = new Map<string, Record<string, any>>();
    for (const row of saved) {
      const key = String(row.src_line_id ?? row.txt_src_line_id ?? "").trim();
      if (key) savedBySrc.set(key, row);
    }
    for (const row of incoming) {
      const key = String(row.src_line_id ?? row.txt_src_line_id ?? "").trim();
      const baseline = key ? savedBySrc.get(key) : undefined;
      if (!baseline) continue;
      const baseQty = Number(baseline.return_qty ?? baseline.txt_return_qty);
      const inQty = Number(row.return_qty ?? row.txt_return_qty);
      if (!Number.isNaN(baseQty) && baseQty > 0 && (Number.isNaN(inQty) || inQty <= 0)) {
        return true;
      }
    }
    return false;
  };

  // Sync value prop changes to internal state
  useEffect(() => {
    if (isUpdatingFromInternalRef.current) {
      isUpdatingFromInternalRef.current = false;
      lastValueRef.current = JSON.stringify(value || []);
      return;
    }

    // Only sync if value prop actually changed and is different from current data
    const valueString = JSON.stringify(value || []);

    // CRITICAL FIX: Prevent data loss during screen resize
    // If we have existing data in local state, don't reset it if value prop becomes temporarily empty
    // This prevents data loss when components re-render during screen size changes
    const hasExistingData = data && Array.isArray(data) && data.length > 0;
    const hasValueData = value && Array.isArray(value) && value.length > 0;

    // Always sync value prop to internal state if it has data (for edit mode)
    // This ensures edit mode data is loaded properly
    if (valueString !== lastValueRef.current || (hasValueData && data.length === 0)) {
      const dataString = JSON.stringify(data);

      if (valueString !== dataString) {
        // If value prop has data, always use it (e.g., in edit mode)
        // CRITICAL FIX: Only sync if value is non-empty OR if both are empty.
        // This prevents transitional empty strings/arrays from parent from wiping out
        // valid internal data during screen initialization/edit mode loading or screen resize.
        // IMPORTANT: Don't reset data if we have existing data and value prop is empty (prevents data loss on resize)
        if (hasValueData) {
          const lookupNameForSync = getSubmoduleLookupName();
          const savedSalesRows = getSavedSalesReturnRows();
          const savedPurchaseRows = getSavedPurchaseReturnRows();

          if (isSalesReturnInvoiceSubmoduleLookup(lookupNameForSync)) {
            if (hasPersistedSubModuleRows(value)) {
              rememberSavedSalesReturnRows(value as Record<string, any>[]);
            } else if (
              savedSalesRows.length > 0 &&
              incomingRowsLoseSavedReturnFields(value as Record<string, any>[], savedSalesRows)
            ) {
              // Parent echoed invoice template rows — restore saved return qty/net
              const merged = mergeSavedSalesReturnLines(
                (value as Record<string, any>[]).map((row) =>
                  normalizeSubModuleRowForDisplay({ ...row })
                ),
                savedSalesRows
              );
              isUpdatingFromInternalRef.current = true;
              setData(merged);
              if (merged.length > 0) setShowTable(true);
              setError(validateData(merged));
              lastValueRef.current = JSON.stringify(merged);
              lastPopulatedDataRef.current = lastValueRef.current;
              dataLoadedRef.current = true;
              publishSubmoduleRows(merged);
              return;
            }
          }

          if (isPurchaseReturnSubmoduleLookup(lookupNameForSync)) {
            if (hasPersistedSubModuleRows(value)) {
              rememberSavedPurchaseReturnRows(value as Record<string, any>[]);
              const persistedOnly = (value as Record<string, any>[]).map((row, index) =>
                evaluateAllExpressionsForRow(
                  normalizeSubModuleRowForDisplay(
                    normalizePurchaseReturnSubmoduleRow({ ...row })
                  ),
                  index
                )
              );
              isUpdatingFromInternalRef.current = true;
              setData(persistedOnly);
              if (persistedOnly.length > 0) setShowTable(true);
              setError(validateData(persistedOnly));
              lastValueRef.current = JSON.stringify(persistedOnly);
              lastPopulatedDataRef.current = lastValueRef.current;
              dataLoadedRef.current = true;
              publishSubmoduleRows(persistedOnly);
              return;
            }
            if (
              savedPurchaseRows.length > 0 &&
              incomingRowsLoseSavedPurchaseReturnFields(
                value as Record<string, any>[],
                savedPurchaseRows
              )
            ) {
              const merged = mergeSavedPurchaseReturnLines(
                (value as Record<string, any>[]).map((row) =>
                  normalizeSubModuleRowForDisplay({ ...row })
                ),
                savedPurchaseRows
              );
              isUpdatingFromInternalRef.current = true;
              setData(merged);
              if (merged.length > 0) setShowTable(true);
              setError(validateData(merged));
              lastValueRef.current = JSON.stringify(merged);
              lastPopulatedDataRef.current = lastValueRef.current;
              dataLoadedRef.current = true;
              publishSubmoduleRows(merged);
              return;
            }
          }

          if (isCreditDebitNoteSplitSubmoduleLookup(lookupNameForSync)) {
            const savedSplitRows = getSavedCreditDebitNoteSplitRows();
            if (hasCreditDebitNoteSplitRows(value, formData?.id)) {
              rememberSavedCreditDebitNoteSplitRows(value as Record<string, any>[]);
              const persistedOnly = (value as Record<string, any>[]).map((row, index) =>
                evaluateAllExpressionsForRow(
                  normalizeSubModuleRowForDisplay(
                    normalizeCreditDebitNoteSplitRow(row, lookupNameForSync)
                  ),
                  index
                )
              );
              isUpdatingFromInternalRef.current = true;
              setData(persistedOnly);
              if (persistedOnly.length > 0) setShowTable(true);
              setError(validateData(persistedOnly));
              lastValueRef.current = JSON.stringify(persistedOnly);
              lastPopulatedDataRef.current = lastValueRef.current;
              dataLoadedRef.current = true;
              publishSubmoduleRows(persistedOnly);
              return;
            }
            if (
              savedSplitRows.length > 0 &&
              Array.isArray(value) &&
              value.length > 0 &&
              !hasCreditDebitNoteSplitRows(value, formData?.id) &&
              (isRecordEditScreen(formData) ||
                incomingRowsLoseSavedCreditDebitSplitAmount(
                  value as Record<string, any>[],
                  savedSplitRows
                ))
            ) {
              const merged = mergeSavedCreditDebitNoteSplitLines(
                (value as Record<string, any>[]).map((row) =>
                  normalizeSubModuleRowForDisplay({ ...row })
                ),
                savedSplitRows
              );
              isUpdatingFromInternalRef.current = true;
              setData(merged);
              if (merged.length > 0) setShowTable(true);
              setError(validateData(merged));
              lastValueRef.current = JSON.stringify(merged);
              lastPopulatedDataRef.current = lastValueRef.current;
              dataLoadedRef.current = true;
              publishSubmoduleRows(merged);
              return;
            }
          }

          // CRITICAL: Evaluate ExpressionV2 for all rows when data is loaded from value prop
          // This ensures calculated fields (like amount, vat_amt, net_amt) are computed
          // even if backend didn't return them or they need recalculation
          const rowsFromValue = mergeSubmoduleRowsById(
            value as Record<string, any>[],
            data.filter((r) => isSubModuleRowSoftDeleted(r))
          );
          let rowsWithExpressions = rowsFromValue.map((row: any, index: number) => {
            return evaluateAllExpressionsForRow(normalizeSubModuleRowForDisplay({ ...row }), index);
          });

          if (isBillWiseReleaseInvoiceSubmodule(config as any)) {
            if (
              (config as any).billWiseReleaseInvoiceMode ||
              (config as any).vendorBillWiseReleaseInvoiceMode
            ) {
              rowsWithExpressions = recalcBillWiseReleaseSubmoduleByConfig(
                config as any,
                rowsWithExpressions
              );
            } else {
              rowsWithExpressions = stampInvoiceOriginalAmounts(rowsWithExpressions);
            }
          }
          if (isBillWiseReleaseReceiptSubmodule(config as any)) {
            const invoiceRows =
              (formData[BILL_WISE_RELEASE_INVOICE_BIND] as Record<string, any>[]) ||
              [];
            rowsWithExpressions = rowsWithExpressions.map((row) => {
              const normalized = normalizeSubModuleRowForDisplay({ ...row });
              const unreg = parseAllocationNumber(
                normalized.unregistered_amount ??
                  normalized.txt_unregistered_amount ??
                  0
              );
              return { ...normalized, unregistered_amount_original: unreg };
            });
            rowsWithExpressions = recalcBillWiseReleaseReceiptGrid(
              rowsWithExpressions,
              invoiceRows
            );
            syncBillWiseReleaseInvoicePreview(
              rowsWithExpressions,
              formData,
              onFormDataChangeRef.current
            );
          }

          if (
            isSalesReturnInvoiceSubmoduleLookup(lookupNameForSync) &&
            savedSalesRows.length > 0 &&
            !hasPersistedSubModuleRows(rowsWithExpressions)
          ) {
            rowsWithExpressions = mergeSavedSalesReturnLines(
              rowsWithExpressions,
              savedSalesRows
            );
          }

          if (
            isPurchaseReturnSubmoduleLookup(lookupNameForSync) &&
            savedPurchaseRows.length > 0 &&
            !hasPersistedSubModuleRows(rowsWithExpressions)
          ) {
            rowsWithExpressions = mergeSavedPurchaseReturnLines(
              rowsWithExpressions,
              savedPurchaseRows
            );
          }

          if (
            isCreditDebitNoteSplitSubmoduleLookup(lookupNameForSync) &&
            getSavedCreditDebitNoteSplitRows().length > 0 &&
            !hasCreditDebitNoteSplitRows(rowsWithExpressions, formData?.id)
          ) {
            rowsWithExpressions = mergeSavedCreditDebitNoteSplitLines(
              rowsWithExpressions,
              getSavedCreditDebitNoteSplitRows()
            );
          }

          // CRITICAL: Proactively fetch rawData for lookups referenced in ExpressionV2 property lookups
          // This ensures rate fields (e.g., [drp_code.sale_rate]) can be calculated when rawData loads
          // This is a generic solution that works for all SubModules
          if (config.childControls) {
            for (const control of config.childControls) {
              const controlAny = control as any;
              const expression = controlAny.ExpressionV2;
              if (!expression || typeof expression !== "string") continue;

              // Check if expression contains property access (e.g., [field.property])
              if (/\[[\w]+\.[\w]+\]/.test(expression)) {
                // Extract field references with properties (e.g., [drp_code.sale_rate])
                const propertyRefRegex = /\[([\w]+)\.([\w]+)\]/g;
                const matches = expression.matchAll(propertyRefRegex);

                for (const match of matches) {
                  const fieldName = match[1]; // e.g., "drp_code"

                  // Find the control that this field references
                  const refControl = config.childControls?.find(
                    (c: any) => (c.name || c.BindValue) === fieldName
                  );

                  if (!refControl) continue;

                  const lookupName = (refControl as any).LookupName || (refControl as any).lookupName;
                  if (!lookupName) continue;

                  const lookupKey = childControlLookupKey(
                    lookupName,
                    refControl as any,
                    formData
                  );
                  const rawData = lookupsState.rawData[lookupKey] || [];
                  const isLoading = lookupsState.loading[lookupKey] || false;
                  const requestKey = `rawdata_proactive_${lookupKey}`;
                  // Fetch rawData if not loaded and not already requested
                  if (rawData.length === 0 && !isLoading && !requestedLookupsRef.current.has(requestKey)) {

                    requestedLookupsRef.current.add(requestKey);
                    const refParentField = childControlParentField(refControl as any);
                    dispatch(
                      fetchSubModuleRawData({
                        lookupName,
                        parentValue: refParentField
                          ? (formData[refParentField] || "")
                          : "",
                        lookuptype: "Select",
                      })
                    );
                  }
                }
              }
            }
          }

          isUpdatingFromInternalRef.current = true;
          setData(rowsWithExpressions);
          if (
            isSalesReturnInvoiceSubmoduleLookup(lookupNameForSync) &&
            hasPersistedSubModuleRows(rowsWithExpressions)
          ) {
            rememberSavedSalesReturnRows(rowsWithExpressions);
          }
          // Show table if data exists
          if (rowsWithExpressions.length > 0) {
            setShowTable(true);
          }
          const validationError = validateData(rowsWithExpressions);
          setError(validationError);
          lastValueRef.current = JSON.stringify(rowsWithExpressions);
          // Mark that we have existing data from value prop to prevent auto-population
          lastPopulatedDataRef.current = valueString;

          // CRITICAL: When edit mode data is loaded, mark data as loaded to trigger footer calculation
          // This ensures footer amounts are calculated when data is loaded from value prop
          // IMPORTANT: Set dataLoadedRef immediately so footer calculation knows data is ready
          dataLoadedRef.current = true;


          // Note: The useEffect that watches data/value changes will also set dataLoadedRef,
          // but setting it here ensures footer calculation is triggered immediately
        } else if (!hasValueData) {
          // CRITICAL FIX: If value prop is empty, preserve existing data to prevent loss during resize
          // Only update ref if we don't have existing data (both are empty)
          // This ensures data is not lost when formData temporarily becomes empty during re-render
          if (!hasExistingData) {
            // Both are empty, just update ref
            lastValueRef.current = valueString;
          } else {
            // We have existing data but value prop is empty - preserve existing data
            // This prevents data loss during screen resize when formData might temporarily be empty

            // Don't update lastValueRef to prevent sync loop, but also don't reset data
            // The data will be preserved in local state
          }
        }

        if (isInitialMountRef.current) {
          isInitialMountRef.current = false;
        }
      } else if (isInitialMountRef.current) {
        // On initial mount, just initialize refs
        lastValueRef.current = valueString;
        isInitialMountRef.current = false;
      }
    }
  }, [value, data, config.name]);

  const validateData = (dataToValidate: Record<string, any>[]): string => {
    const activeDataToValidate = dataToValidate.filter(r => r.soft_delete !== 1 && r.soft_delete !== "1");

    // Check for NotAllowEmpty
    if (config.NotAllowEmpty && activeDataToValidate.length === 0) {
      return config.errorText || "At least one entry is required";
    }

    if (config.validation) {
      for (const rule of config.validation) {
        switch (rule.rule) {
          case ValidationRule.REQUIRED:
            if (activeDataToValidate.length === 0)
              return rule.message || "At least one entry is required";
            break;
          case ValidationRule.MIN_VALUE:
            if (activeDataToValidate.length < (rule.value as number))
              return rule.message || `Minimum ${rule.value} entries required`;
            break;
          case ValidationRule.MAX_VALUE:
            if (activeDataToValidate.length > (rule.value as number))
              return rule.message || `Maximum ${rule.value} entries allowed`;
            break;
        }
      }
    }
    // Run expression-based conditional validations (e.g. [qty] >= [issued_qty]) per row
    const conditionalResults = runConditionalValidations(activeDataToValidate, config.childControls);
    const blockingError = conditionalResults.find((r) => !r.isWarning);
    if (blockingError) return `Row ${blockingError.rowIndex}: ${blockingError.message}`;

    if (isBillAllocationInvoiceSubmodule(config as any)) {
      const moduleKeys = resolveBillAllocationModuleKeys(
        config as { name?: string; BindValue?: string },
        formData
      );
      const poolError = validateBillAllocationReceiptPools(
        activeDataToValidate,
        formData,
        moduleKeys
      );
      if (poolError) return poolError;
    }

    return "";
  };

  const publishSubmoduleRows = (rows: Record<string, any>[]) => {
    const fieldName = config.name || (config as any).BindValue;
    const bindValue = (config as any).BindValue;
    let rowsToPublish = rows;
    const lookupNamePublish = getSubmoduleLookupName();
    if (
      isSalesReturnInvoiceSubmoduleLookup(lookupNamePublish) &&
      isRecordEditScreen(formData)
    ) {
      const saved = getSavedSalesReturnRows();
      if (saved.length > 0 && !hasPersistedSubModuleRows(rowsToPublish)) {
        rowsToPublish = mergeSavedSalesReturnLines(rowsToPublish, saved);
      } else if (hasPersistedSubModuleRows(rowsToPublish)) {
        rememberSavedSalesReturnRows(rowsToPublish);
      }
    }
    if (
      isCreditDebitNoteSplitSubmoduleLookup(lookupNamePublish) &&
      isRecordEditScreen(formData)
    ) {
      const saved = getSavedCreditDebitNoteSplitRows();
      if (saved.length > 0 && !hasCreditDebitNoteSplitRows(rowsToPublish, formData?.id)) {
        rowsToPublish = mergeSavedCreditDebitNoteSplitLines(rowsToPublish, saved);
      } else if (hasCreditDebitNoteSplitRows(rowsToPublish, formData?.id)) {
        rememberSavedCreditDebitNoteSplitRows(rowsToPublish);
      }
    }
    if (isBillAllocationUnregisteredSubmodule(config as any)) {
      const moduleKeys = resolveBillAllocationModuleKeys(
        config as any,
        formData
      );
      rowsToPublish = stampUnregisteredBaseRows(
        rows.map((row) => normalizeSubModuleRowForDisplay({ ...row }))
      );
      onFormDataChangeRef.current?.(
        moduleKeys.unregisteredBaseKey,
        rowsToPublish
      );
    }
    if (isBillWiseReleaseInvoiceSubmodule(config as any)) {
      const invoiceBaseKey =
        (config as { BindValue?: string }).BindValue ===
        VENDOR_BILL_WISE_RELEASE_PURCHASE_BIND
          ? VENDOR_BILL_WISE_RELEASE_PURCHASE_BASE_KEY
          : BILL_WISE_RELEASE_INVOICE_BASE_KEY;
      if (
        (config as any).billWiseReleaseInvoiceMode ||
        (config as any).vendorBillWiseReleaseInvoiceMode
      ) {
        rowsToPublish = recalcBillWiseReleaseSubmoduleByConfig(
          config as any,
          rows.map((row) => normalizeSubModuleRowForDisplay({ ...row }))
        );
        onFormDataChangeRef.current?.(invoiceBaseKey, rowsToPublish);
      } else {
        rowsToPublish = stampInvoiceOriginalAmounts(
          rows.map((row) => normalizeSubModuleRowForDisplay({ ...row }))
        );
        onFormDataChangeRef.current?.(invoiceBaseKey, rowsToPublish);
        const receiptBind =
          (config as { BindValue?: string }).BindValue ===
          VENDOR_BILL_WISE_RELEASE_PURCHASE_BIND
            ? VENDOR_BILL_WISE_RELEASE_RECEIPT_BIND
            : BILL_WISE_RELEASE_RECEIPT_BIND;
        const receiptRows =
          (formData[receiptBind] as Record<string, any>[]) || [];
        if (receiptRows.length > 0) {
          syncBillWiseReleaseInvoicePreview(
            receiptRows,
            {
              ...formData,
              [invoiceBaseKey]: rowsToPublish,
            },
            onFormDataChangeRef.current
          );
        }
      }
    }
    if (isBillWiseReleaseReceiptSubmodule(config as any)) {
      rowsToPublish = rows.map((row) => {
        const normalized = normalizeSubModuleRowForDisplay({ ...row });
        const unreg = parseAllocationNumber(
          normalized.unregistered_amount ??
            normalized.txt_unregistered_amount ??
            0
        );
        return {
          ...normalized,
          unregistered_amount_original: unreg,
        };
      });
    }
    onChangeRef.current?.(fieldName, rowsToPublish);
    if (bindValue && bindValue !== fieldName) {
      onFormDataChangeRef.current?.(bindValue, rowsToPublish);
    }
  };

  const handleDataChange = (newData: Record<string, any>[]) => {
    isUpdatingFromInternalRef.current = true;
    setData(newData);
    const validationError = validateData(newData);
    setError(validationError);
    lastValueRef.current = JSON.stringify(newData);
    // Use name first (matches ComponentRenderer's priority: name || BindValue)
    // ComponentRenderer normalizes config.name, so we should use that
    const fieldName = config.name || (config as any).BindValue;
    const bindValue = (config as any).BindValue || (config as any).bindValue;

    onChangeRef.current?.(fieldName, newData);
    // Save reads BindValue first; keep both keys in sync after row delete/edit.
    if (bindValue && bindValue !== fieldName) {
      onFormDataChangeRef.current?.(bindValue, newData);
    }
  };



  const addRow = () => {
    lookupAutoPopulateSuppressedRef.current = false;
    const activeRowsCount = data.filter((row) => row.soft_delete !== 1 && row.soft_delete !== "1").length;
    if (config.maxRows && activeRowsCount >= config.maxRows) return;

    // Show table when adding first row
    if (!showTable) {
      setShowTable(true);
    }

    const newRow: Record<string, any> = {};
    config.childControls?.forEach((control: any) => {
      // Handle both old format (BindValue) and new format (name)
      const fieldName = control.name || control.BindValue;
      if (fieldName) {
        let defaultValue = control.defaultValue || control.Default_Value || "";

        // CRITICAL FIX: If default value is provided for a lookup dropdown, ensure it's mapped to ID
        // This handles cases where defaultValue is "5%" (label) but we need the ID
        const isDropdown =
          control.componentType === ComponentType.SELECT_DROPDOWN ||
          control.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
          control.componentType === "selectDropdown" ||
          control.componentType === "searchableDropdown";

        if (isDropdown && (control.lookupName || control.LookupName) && defaultValue) {
          const lookupName = control.lookupName || control.LookupName;

          // Determine lookup key using the same logic as other functions
          const parentFieldForLookup = control.Parent_Field || control.parentField || config.parentField;
          let lookupKey = lookupName;

          if (parentFieldForLookup) {
            const parentValue = formData[parentFieldForLookup];
            if (parentValue !== undefined && parentValue !== null && parentValue !== "") {
              lookupKey = `${lookupName}_${parentValue}`;
            }
          }

          // Check lookupsState for matching option
          const lookupOptions = lookupsState.lookups[lookupKey] || [];

          if (lookupOptions.length > 0) {
            // Try to find by Label, Name, or Value
            const matchingOption = lookupOptions.find((opt: any) =>
              opt.label === defaultValue ||
              String(opt.label) === String(defaultValue) ||
              (opt.name && String(opt.name) === String(defaultValue)) ||
              (opt.value && String(opt.value) === String(defaultValue)) ||
              (opt.id && String(opt.id) === String(defaultValue))
            );

            if (matchingOption) {
              // If found, use the ID/Value from the option
              // Prefer value/id over the label
              defaultValue = matchingOption.value || (matchingOption as any).id || defaultValue;
            }
          }
        }

        newRow[fieldName] = defaultValue;
      }
    });

    const submoduleTableName = String(
      (config as any).tableName ||
        (config as any).TableName ||
        config.tableName ||
        ""
    );
    if (isPurchaseDetailsSubmodule(submoduleTableName)) {
      const taxOptions = lookupsState.lookups.tax_rates || [];
      const vatControl = config.childControls?.find(
        (control: any) =>
          (control.name || control.BindValue) === "drp_vat" ||
          control.Json_Attribute === "vat_id"
      ) as any;
      const staticDefault =
        vatControl?.defaultValue ?? vatControl?.Default_Value ?? "5%";
      Object.assign(
        newRow,
        applyPurchaseSupplierVatToRow(
          newRow,
          formData,
          taxOptions.length > 0
            ? taxOptions
            : [{ label: staticDefault, value: staticDefault }]
        )
      );
    }

    // Evaluate all ExpressionV2 fields for the new row
    const evaluatedRow = evaluateAllExpressionsForRow(newRow, data.length);



    // Clear any previous errors
    setError("");
    // Add new row at the beginning so it appears below the "Add New Row" button
    handleDataChange([evaluatedRow, ...data]);
  };

  const shouldSoftDeleteSubModuleRow = (row: Record<string, any>): boolean => {
    if (row.id || row.itemid) return true;
    if (
      isEditMode &&
      formData?.id &&
      isPersistedSubModuleRowForEdit(row, formData.id)
    ) {
      return true;
    }
    return false;
  };

  const removeRow = async (index: number) => {
    const activeRowsCount = data.filter((r) => r.soft_delete !== 1 && r.soft_delete !== "1").length;
    if (config.minRows && activeRowsCount <= config.minRows) return;

    // Get the row data to show in confirmation message
    const row = data[index];
    const firstField = config.childControls?.[0] as any;
    const fieldName = firstField?.name || firstField?.BindValue || "";
    const jsonAttribute = firstField?.Json_Attribute || "";
    const rowLabel = (fieldName && row[fieldName]) || (jsonAttribute && row[jsonAttribute]) || "this item";

    // Show confirmation dialog
    const confirmed = await showConfirmDialog(
      dispatch,
      `Are you sure you want to delete ?`,
      "Confirm Delete"
    );

    if (!confirmed) {
      return; // User cancelled
    }

    let newData;
    if (shouldSoftDeleteSubModuleRow(row)) {
      // Existing row - soft delete
      newData = [...data];
      newData[index] = { ...row, soft_delete: 1, act: "edit" };
    } else {
      // New row - hard delete
      newData = data.filter((_, i) => i !== index);
    }

    isUpdatingFromInternalRef.current = true;
    setData(newData);
    const validationError = validateData(newData);
    setError(validationError);
    lastValueRef.current = JSON.stringify(newData);
    publishSubmoduleRows(newData);

    // Hide table if all active rows are removed
    if (newData.filter((r) => r.soft_delete !== 1 && r.soft_delete !== "1").length === 0) {
      setShowTable(false);
    }
  };

  const removeAllRows = async () => {
    const currentActiveCount = data.filter(
      (r) => r.soft_delete !== 1 && r.soft_delete !== "1"
    ).length;
    if (currentActiveCount === 0 || config.disabled) return;
    if (config.minRows && currentActiveCount <= config.minRows) return;

    const sectionLabel =
      config.label || (config as any).Label || "this section";
    const confirmed = await showConfirmDialog(
      dispatch,
      `Remove all items in ${sectionLabel}?`,
      "Confirm Remove All"
    );
    if (!confirmed) return;

    const newData: Record<string, any>[] = [];
    for (const row of data) {
      if (row.soft_delete === 1 || row.soft_delete === "1") {
        newData.push(row);
        continue;
      }
      if (shouldSoftDeleteSubModuleRow(row)) {
        newData.push({ ...row, soft_delete: 1, act: "edit" });
      }
    }

    lookupAutoPopulateSuppressedRef.current = true;
    isUpdatingFromInternalRef.current = true;
    setData(newData);
    const validationError = validateData(newData);
    setError(validationError);
    lastValueRef.current = JSON.stringify(newData);
    lastPopulatedDataRef.current =
      newData.filter((r) => r.soft_delete !== 1 && r.soft_delete !== "1")
        .length === 0
        ? "USER_CLEARED"
        : JSON.stringify(newData);
    publishSubmoduleRows(newData);

    if (
      newData.filter((r) => r.soft_delete !== 1 && r.soft_delete !== "1")
        .length === 0
    ) {
      setShowTable(false);
    }
  };

  const updateRowField = (
    rowIndex: number,
    fieldName: string,
    fieldValue: any,
    control?: any
  ) => {
    // CRITICAL FIX: Ensure row exists before updating
    let newData = [...data];

    // Ensure the row exists
    if (!newData[rowIndex]) {
      newData[rowIndex] = {};
    }

    // CRITICAL FIX: Store the full selected option if available
    // This matches the old framework's SelectedJson.Data approach
    // The selected option should contain ALL properties from rawData for ExpressionV2 to work
    const selectedOptionKey = (control as any)?._tempSelectedOptionKey;
    const selectedOption = (control as any)?._tempSelectedOption;

    // CRITICAL FIX: Ensure fieldValue is properly stored (handle null/undefined/empty string)
    const normalizedValue =
      fieldValue !== undefined && fieldValue !== null ? fieldValue : "";

    // Always store under the visible field name (name/BindValue)
    newData[rowIndex] = {
      ...newData[rowIndex],
      [fieldName]: normalizedValue,
      ...(newData[rowIndex].id || newData[rowIndex].itemid ? { act: "edit" } : {})
    };

    // CRITICAL: Also mirror the value under Json_Attribute (API field name) if present.
    // This ensures subform payload builder can always find latest values by Json_Attribute
    // even if expressions or auto-population originally used a different key.
    const jsonAttributeKey =
      (control as any)?.Json_Attribute || (control as any)?.jsonAttribute;
    if (jsonAttributeKey && typeof jsonAttributeKey === "string") {
      newData[rowIndex][jsonAttributeKey] = normalizedValue;
    }

    // CRITICAL FIX: Log the update for debugging
    if (import.meta.env.DEV) {
      console.log(`[SubModule] updateRowField - Storing value:`, {
        rowIndex,
        fieldName,
        fieldValue: normalizedValue,
        valueType: typeof normalizedValue,
        selectedOptionKey,
        hasSelectedOption: !!selectedOption,
        currentRowData: newData[rowIndex],
      });
    }

    // Store the full selected option in row data for ExpressionV2 evaluation
    // This allows expressions like [drp_product.sale_rate] to access properties
    if (selectedOptionKey && selectedOption) {
      newData[rowIndex][selectedOptionKey] = selectedOption;
      // Clear temp values
      delete (control as any)._tempSelectedOption;
      delete (control as any)._tempSelectedOptionKey;
    } else if (
      (control as any)?.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
      (control as any)?.componentType === ComponentType.SELECT_DROPDOWN ||
      (control as any)?.componentType === "searchableDropdown" ||
      (control as any)?.componentType === "selectDropdown"
    ) {
      // If no selected option was provided, try to get it from rawData/lookups
      // This ensures ExpressionV2 can still access properties even if storage failed
      const controlAny = control as any;
      const lookupName = controlAny?.LookupName || controlAny?.lookupName;
      if (lookupName && fieldValue) {
        const parentFieldForLookup =
          controlAny?.Parent_Field ||
          controlAny?.parentField ||
          config.parentField;
        let lookupKey = lookupName;

        if (parentFieldForLookup) {
          const parentValue = formData[parentFieldForLookup];
          if (
            parentValue !== undefined &&
            parentValue !== null &&
            parentValue !== ""
          ) {
            lookupKey = `${lookupName}_${parentValue}`;
          }
        }

        // Try to get from rawData first (has all properties)
        const rawData = lookupsState.rawData[lookupKey] || [];
        const rawItem = rawData.find(
          (item: any) =>
            item.id === fieldValue ||
            String(item.id) === String(fieldValue) ||
            item.value === fieldValue ||
            String(item.value) === String(fieldValue) ||
            (item.product_id &&
              String(item.product_id) === String(fieldValue)) ||
            (item.codeid && String(item.codeid) === String(fieldValue))
        );

        if (rawItem) {
          const fallbackSelectedOptionKey = `_${fieldName}_selectedOption`;
          newData[rowIndex][fallbackSelectedOptionKey] = rawItem;
        } else {
          // CRITICAL: If rawData is empty, try to fetch it
          // This can happen if rawData wasn't fetched yet when user selects a value
          const isRawDataLoading = lookupsState.loading[lookupKey] || false;
          const requestKey = `rawdata_updateRowField_${lookupKey}`;
          if (!isRawDataLoading && rawData.length === 0 && !requestedLookupsRef.current.has(requestKey)) {
            requestedLookupsRef.current.add(requestKey);
            dispatch(
              fetchSubModuleRawData({
                lookupName,
                parentValue: parentFieldForLookup
                  ? formData[parentFieldForLookup] || ""
                  : "",
                lookuptype: "Select",
              })
            );
          }
        }
      }
    }

    // Handle BindParent auto-population
    // Check all childControls to see if any have BindParent pointing to this field
    if (fieldValue && config.childControls) {
      for (const childControl of config.childControls) {
        const childControlAny = childControl as any;
        const childBindParent =
          childControlAny.BindParent || childControlAny.bindParent;
        if (childBindParent && childBindParent[fieldName]) {
          // This child field should be populated from the selected lookup option
          const childFieldName =
            childControlAny.name || childControlAny.BindValue;
          const childFieldAttribute = childBindParent[fieldName]; // e.g., "item_name"

          // Get the lookup for the parent field (the one that was just changed)
          const controlAny = control as any;
          const lookupName = controlAny?.LookupName || controlAny?.lookupName;
          if (lookupName && childFieldName) {
            // Determine lookup key (handle dependent lookups)
            const parentFieldForLookup =
              controlAny?.Parent_Field ||
              controlAny?.parentField ||
              config.parentField;
            let lookupKey = lookupName;

            if (parentFieldForLookup) {
              const parentValue = formData[parentFieldForLookup];
              if (
                parentValue !== undefined &&
                parentValue !== null &&
                parentValue !== ""
              ) {
                lookupKey = `${lookupName}_${parentValue}`;
              }
            }

            // CRITICAL FIX: Use rawData first to access all properties (like item_name, purchase_rate, etc.)
            // rawData contains the full API response with all properties, while lookups only has label/value
            const rawData = lookupsState.rawData[lookupKey] || [];
            const lookupOptions = lookupsState.lookups[lookupKey] || [];

            // First try to find in rawData (has all properties)
            let rawItem = rawData.find(
              (item: any) =>
                item.id === fieldValue ||
                String(item.id) === String(fieldValue) ||
                item.value === fieldValue ||
                String(item.value) === String(fieldValue) ||
                (item.product_id &&
                  String(item.product_id) === String(fieldValue)) ||
                (item.codeid && String(item.codeid) === String(fieldValue)) ||
                // Match by label/name (for cases like "5%" being the label)
                item.label === fieldValue ||
                String(item.label) === String(fieldValue) ||
                item.name === fieldValue ||
                String(item.name) === String(fieldValue) ||
                item.description === fieldValue ||
                String(item.description) === String(fieldValue) ||
                // Match by tax_name or similar for tax_rates lookup
                item.tax_name === fieldValue ||
                String(item.tax_name) === String(fieldValue)
            );

            // If not found in rawData, try lookup options
            const selectedOption = rawItem
              ? null
              : lookupOptions.find(
                (opt: any) =>
                  opt.value === fieldValue ||
                  String(opt.value) === String(fieldValue) ||
                  opt.id === fieldValue ||
                  String(opt.id) === String(fieldValue)
              );

            // Get the attribute value - prefer rawData, fallback to lookup option
            let attributeValue = extractPropertyValue(rawItem, childFieldAttribute);
            if (attributeValue === null && selectedOption) {
              attributeValue = extractPropertyValue(selectedOption, childFieldAttribute) || (selectedOption.label || "");
            }
            attributeValue = attributeValue !== null ? String(attributeValue) : "";

            if (attributeValue) {
              newData[rowIndex] = {
                ...newData[rowIndex],
                [childFieldName]: attributeValue,
              };

              // CRITICAL FIX: Trigger ExpressionV2 evaluation after BindParent updates
              // This ensures calculated fields (like rate, amount, VAT) update when BindParent fields change
              // We mark that this field was updated by BindParent so ExpressionV2 knows to evaluate
              (childControlAny as any)._bindParentUpdated = true;
            }
          }
        }
      }
    }

    // CRITICAL FIX: After BindParent updates, trigger ExpressionV2 evaluation for fields that depend on BindParent-populated fields
    // This ensures cascading calculations work correctly (e.g., item_name → rate → amount → VAT → net)
    if (config.childControls) {
      const bindParentUpdatedFields = config.childControls
        .filter((c: any) => (c as any)._bindParentUpdated)
        .map((c: any) => {
          const fieldName = c.name || c.BindValue;
          delete (c as any)._bindParentUpdated; // Clean up flag
          return fieldName;
        });

      // Trigger ExpressionV2 evaluation for all fields that might depend on BindParent-populated fields
      // This will be handled by the ExpressionV2 evaluation logic below
    }

    // Handle ExpressionV2 evaluation for childControls (uses shared resolveFieldReference above)
    // Based on old framework: SetExpressions() method
    // Only evaluate expressions that depend on the changed field
    if (config.childControls) {
      for (const childControl of config.childControls) {
        const childControlAny = childControl as any;
        const expressionV2 = childControlAny.ExpressionV2;

        if (expressionV2 && typeof expressionV2 === "string") {
          const childFieldName =
            childControlAny.name || childControlAny.BindValue;

          // Step 2: Dependency Detection - Check if expression depends on the changed field
          // Also check if expression depends on fields that were updated by BindParent
          const dependsOnChangedField =
            expressionV2.includes(`[${fieldName}]`) ||
            expressionV2.includes(`[${fieldName}.`);

          // Check if this expression depends on any BindParent-updated fields
          const bindParentUpdatedFields =
            config.childControls
              ?.filter((c: any) => (c as any)._bindParentUpdated)
              .map((c: any) => c.name || c.BindValue) || [];
          const dependsOnBindParentField = bindParentUpdatedFields.some(
            (updatedField: string) =>
              expressionV2.includes(`[${updatedField}]`) ||
              expressionV2.includes(`[${updatedField}.`)
          );

          // Evaluate if expression depends on changed field OR BindParent-updated field
          if (!dependsOnChangedField && !dependsOnBindParentField) {
            continue; // Skip expressions that don't depend on the changed field or BindParent field
          }

          // Step 3: Pattern Matching - Find all field references
          // Regex matches: [field], [field.property], [(form).field]
          const fieldRefRegex = /\[[\w.()]*\]/g;
          const matches = expressionV2.match(fieldRefRegex) || [];

          if (matches.length === 0) continue;

          // Step 4: Value Resolution - Replace all references with actual values
          let expression = expressionV2;
          let canEvaluate = true;
          const resolvedValues: Record<string, string> = {};

          // CRITICAL: Use the current rowData which should have the stored selected option
          const currentRowData = newData[rowIndex];

          for (const match of matches) {
            const resolvedValue = resolveFieldReference(match, currentRowData);

            if (resolvedValue !== null) {
              resolvedValues[match] = resolvedValue;
              expression = expression.replace(match, resolvedValue);
            } else {
              // Can't evaluate if any reference is missing
              canEvaluate = false;
              break;
            }
          }

          // Step 5: Expression Evaluation - Only if all references resolved
          if (canEvaluate) {
            try {
              // Use JavaScript evaluation (like old framework's window.Evaluate)
              const evaluatedValue = Function(
                `"use strict"; return (${expression})`
              )();

              // Step 6: Result Update
              // Check if field is read-only or has existing value (like old framework)
              const isReadOnly =
                childControlAny.disabled || childControlAny.IsReadOnly;
              const currentValue = newData[rowIndex]?.[childFieldName];
              const hasExistingValue =
                currentValue !== undefined &&
                currentValue !== null &&
                currentValue !== "" &&
                Number(currentValue) !== 0;

              // Update if: read-only field OR no existing value OR force update
              // OR the changed field is the expression's source (e.g. Code changed -> update Rate from new product)
              const shouldUpdate =
                isReadOnly ||
                !hasExistingValue ||
                childControlAny.ForceExpressionUpdate ||
                dependsOnChangedField;

              if (
                shouldUpdate &&
                evaluatedValue !== null &&
                evaluatedValue !== undefined
              ) {
                const resultValue = formatExpressionV2Result(evaluatedValue);

                newData[rowIndex] = {
                  ...newData[rowIndex],
                  [childFieldName]: resultValue,
                };
                if (childControlAny.Json_Attribute) {
                  newData[rowIndex][childControlAny.Json_Attribute] = resultValue;
                }

                // CRITICAL FIX: Recursively evaluate other fields that might depend on this one
                // This handles chained expressions like rate -> amount -> vat_amt -> net_amt
                // Use synchronous recursive evaluation with dependency tracking to prevent infinite loops
                const evaluatedFields = new Set<string>(); // Track fields already evaluated in this cycle
                evaluatedFields.add(childFieldName);

                const evaluateDependentFields = (
                  fieldNameToCheck: string,
                  currentRowData: Record<string, any>
                ) => {
                  const dependentFields = config.childControls?.filter(
                    (c: any) => {
                      const expr = c.ExpressionV2;
                      if (!expr) return false;
                      const childFieldBindValue = c.name || c.BindValue;
                      const dependsOnField =
                        expr.includes(`[${fieldNameToCheck}]`) ||
                        expr.includes(`[${childFieldBindValue}]`);
                      // Only evaluate if not already evaluated in this cycle
                      return (
                        dependsOnField &&
                        !evaluatedFields.has(childFieldBindValue)
                      );
                    }
                  );

                  if (dependentFields && dependentFields.length > 0) {
                    // Evaluate each dependent field synchronously
                    dependentFields.forEach((depControl: any) => {
                      const depFieldName =
                        depControl.name || depControl.BindValue;

                      // Skip if already evaluated
                      if (evaluatedFields.has(depFieldName)) {
                        return;
                      }

                      evaluatedFields.add(depFieldName);

                      const depFieldValue = currentRowData[depFieldName];
                      const depExpression = depControl.ExpressionV2;

                      if (depExpression && typeof depExpression === "string") {
                        // Resolve all references in the expression
                        const fieldRefRegex = /\[[\w.()]*\]/g;
                        const matches =
                          depExpression.match(fieldRefRegex) || [];
                        let expression = depExpression;
                        let canEvaluate = true;

                        for (const match of matches) {
                          const resolvedValue = resolveFieldReference(
                            match,
                            currentRowData
                          );
                          if (resolvedValue !== null) {
                            expression = expression.replace(
                              match,
                              resolvedValue
                            );
                          } else {
                            canEvaluate = false;
                            break;
                          }
                        }

                        if (canEvaluate) {
                          try {
                            const evaluatedValue = Function(
                              `"use strict"; return (${expression})`
                            )();

                            if (
                              evaluatedValue !== null &&
                              evaluatedValue !== undefined
                            ) {
                              const resultValue =
                                formatExpressionV2Result(evaluatedValue);

                              // Update the row data
                              currentRowData[depFieldName] = resultValue;
                              if (depControl.Json_Attribute) {
                                currentRowData[depControl.Json_Attribute] = resultValue;
                              }

                              // Recursively evaluate fields that depend on this one
                              evaluateDependentFields(
                                depFieldName,
                                currentRowData
                              );
                            }
                          } catch (error) {
                            // Silently handle errors in recursive evaluation
                          }
                        }
                      }
                    });
                  }
                };

                // Start recursive evaluation
                evaluateDependentFields(childFieldName, newData[rowIndex]);
              }
            } catch (error) {
              // On error, set to default value (like old framework)
              if (childControlAny.defaultValue !== undefined) {
                newData[rowIndex] = {
                  ...newData[rowIndex],
                  [childFieldName]: childControlAny.defaultValue,
                };
                if (childControlAny.Json_Attribute) {
                  newData[rowIndex][childControlAny.Json_Attribute] = childControlAny.defaultValue;
                }
              }
            }


            // CRITICAL: If evaluation failed due to missing rawData, try to fetch it
            // This handles the case where user selects before rawData is loaded
            for (const match of matches) {
              const refValue = match.replace(/[\[\]]/g, "");
              if (refValue.includes(".")) {
                const [fieldName, property] = refValue.split(".");
                const refControl = config.childControls?.find(
                  (c: any) => (c.name || c.BindValue) === fieldName
                );

                if (refControl) {
                  const lookupName =
                    (refControl as any).LookupName ||
                    (refControl as any).lookupName;
                  if (lookupName) {
                    const parentField =
                      (refControl as any).Parent_Field ||
                      (refControl as any).parentField;
                    const actualParentField = parentField || config.parentField;
                    let lookupKey = lookupName;

                    if (actualParentField) {
                      const parentValue = formData[actualParentField];
                      if (
                        parentValue !== undefined &&
                        parentValue !== null &&
                        parentValue !== ""
                      ) {
                        lookupKey = `${lookupName}_${parentValue}`;
                      }
                    }

                    const rawData = lookupsState.rawData[lookupKey] || [];
                    const isRawDataLoading =
                      lookupsState.loading[lookupKey] || false;

                    const requestKey = `rawdata_evaluateExpression_${lookupKey}`;

                    if (rawData.length === 0 && !isRawDataLoading && !requestedLookupsRef.current.has(requestKey)) {
                      requestedLookupsRef.current.add(requestKey);
                      dispatch(
                        fetchSubModuleRawData({
                          lookupName,
                          parentValue: actualParentField
                            ? formData[actualParentField] || ""
                            : "",
                          lookuptype: "Select",
                        })
                      );
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    if (
      isBillWiseReleaseInvoiceSubmodule(config as any) &&
      ((config as any).billWiseReleaseInvoiceMode ||
        (config as any).vendorBillWiseReleaseInvoiceMode)
    ) {
      if (
        fieldName === BILL_WISE_RELEASE_RELEASE_FIELD ||
        fieldName === "txt_release_amount" ||
        fieldName === "txt_amount_paid" ||
        fieldName === BILL_WISE_RELEASE_ALLOCATE_FIELD ||
        fieldName === "amount"
      ) {
        newData = recalcBillWiseReleaseSubmoduleByConfig(
          config as any,
          newData,
          { changedRowIndex: rowIndex }
        );
        newData = newData.map((row, idx) =>
          evaluateAllExpressionsForRow(row, idx)
        );
        handleDataChange(newData);
        return;
      }
    }

    if (isBillAllocationInvoiceSubmodule(config as any)) {
      const receiptField = resolveBillAllocationReceiptField(config as any);
      if (
        fieldName === receiptField ||
        fieldName === BILL_ALLOC_RECEIPT_FIELD ||
        fieldName === VENDOR_BILL_ALLOC_PAYMENT_FIELD ||
        fieldName === "txt_amount" ||
        fieldName === "amount"
      ) {
        const moduleKeys = resolveBillAllocationModuleKeys(
          config as { name?: string; BindValue?: string },
          formData
        );
        const billAllocOpts = {
          formData,
          invoiceRows: newData,
          rowIndex,
          moduleKeys,
        };
        const previousLedgerId =
          fieldName === receiptField ||
          fieldName === BILL_ALLOC_RECEIPT_FIELD ||
          fieldName === VENDOR_BILL_ALLOC_PAYMENT_FIELD
            ? getLedgerIdFromInvoiceRow(data[rowIndex] ?? {})
            : "";
        if (
          fieldName === receiptField ||
          fieldName === BILL_ALLOC_RECEIPT_FIELD ||
          fieldName === VENDOR_BILL_ALLOC_PAYMENT_FIELD
        ) {
          const ledgerId = String(
            newData[rowIndex][fieldName] ?? ""
          ).trim();
          let rawReceipt: Record<string, any> | null =
            (newData[rowIndex][`_${fieldName}_selectedOption`] as Record<
              string,
              any
            >) || null;
          const selectedLedgerId = rawReceipt
            ? String(
                rawReceipt.id ?? rawReceipt.value ?? rawReceipt.ledger_unregistered_id ?? ""
              ).trim()
            : "";
          if (!rawReceipt || (ledgerId && selectedLedgerId !== ledgerId)) {
            rawReceipt =
              findUnregisteredBaseRow(ledgerId, formData, moduleKeys) ||
              (() => {
                const lookupName =
                  (control as any)?.lookupName ||
                  (control as any)?.LookupName ||
                  (config as any)?.vendorBillAllocationMode
                    ? "vendor_unregistered_payments"
                    : "customer_unregistered_receipts";
                let lookupKey = lookupName;
                const parentField =
                  (control as any)?.parentField ||
                  (control as any)?.Parent_Field ||
                  config.parentField;
                if (parentField && formData[parentField]) {
                  lookupKey = `${lookupName}_${formData[parentField]}`;
                }
                const rawData = lookupsState.rawData[lookupKey] || [];
                return (
                  (rawData.find(
                    (item: any) =>
                      String(item.id ?? item.value ?? "") === ledgerId
                  ) as Record<string, any>) || null
                );
              })();
          }
          if (!rawReceipt) {
            rawReceipt = resolveReceiptRawItem(newData[rowIndex], fieldName);
          }
          const rowBeforeApply = { ...newData[rowIndex] };
          delete rowBeforeApply.unregistered_amount_original;
          delete rowBeforeApply.txt_unregistered_amount_cap;
          delete rowBeforeApply.txt_max_allocate;
          delete rowBeforeApply.txt_remaining_unregistered;
          delete rowBeforeApply.txt_due_after_allocation;
          delete rowBeforeApply.txt_amount;
          delete rowBeforeApply.amount;
          newData[rowIndex] = applyReceiptSelectionToInvoiceRow(
            rowBeforeApply,
            rawReceipt && typeof rawReceipt === "object" ? rawReceipt : null,
            billAllocOpts
          );
          if (rawReceipt) {
            newData[rowIndex][`_${fieldName}_selectedOption`] = rawReceipt;
          }
        }
        newData = recalcBillAllocationInvoiceGrid(newData, formData, {
          changedRowIndex: rowIndex,
          previousLedgerId:
            previousLedgerId &&
            previousLedgerId !==
              getLedgerIdFromInvoiceRow(newData[rowIndex] ?? {})
              ? previousLedgerId
              : undefined,
        });
        newData = newData.map((row, idx) =>
          evaluateAllExpressionsForRow(row, idx)
        );
        syncBillAllocationUnregisteredPreview(
          newData,
          formData,
          onFormDataChangeRef.current,
          moduleKeys
        );
      }
    }

    if (isBillWiseReleaseReceiptSubmodule(config as any)) {
      const documentField = resolveBillWiseReleaseDocumentField(config as any);
      const invoiceBaseKey = (config as any)?.vendorBillWiseReleaseMode
        ? VENDOR_BILL_WISE_RELEASE_PURCHASE_BASE_KEY
        : BILL_WISE_RELEASE_INVOICE_BASE_KEY;
      const invoiceBind = (config as any)?.vendorBillWiseReleaseMode
        ? VENDOR_BILL_WISE_RELEASE_PURCHASE_BIND
        : BILL_WISE_RELEASE_INVOICE_BIND;
      if (
        fieldName === documentField ||
        fieldName === BILL_WISE_RELEASE_INVOICE_FIELD ||
        fieldName === VENDOR_BILL_WISE_RELEASE_PURCHASE_FIELD ||
        fieldName === BILL_WISE_RELEASE_ALLOCATE_FIELD ||
        fieldName === "amount"
      ) {
        const invoiceRowsBase =
          (formData[invoiceBaseKey] as Record<string, any>[]) ||
          (formData[invoiceBind] as Record<string, any>[]) ||
          [];
        if (
          fieldName === documentField ||
          fieldName === BILL_WISE_RELEASE_INVOICE_FIELD ||
          fieldName === VENDOR_BILL_WISE_RELEASE_PURCHASE_FIELD
        ) {
          const ledgerId = String(newData[rowIndex][fieldName] ?? "").trim();
          if (!ledgerId) {
            newData[rowIndex] = clearBillWiseReleaseInvoiceSelection(
              newData[rowIndex]
            );
            setError("");
            newData = recalcBillWiseReleaseReceiptGrid(
              newData,
              invoiceRowsBase,
              { changedRowIndex: rowIndex }
            );
            newData = newData.map((row, idx) =>
              evaluateAllExpressionsForRow(row, idx)
            );
            syncBillWiseReleaseInvoicePreview(
              newData,
              formData,
              onFormDataChangeRef.current
            );
            handleDataChange(newData);
            return;
          }
          let rawInvoice: Record<string, any> | null =
            (newData[rowIndex][`_${fieldName}_selectedOption`] as Record<
              string,
              any
            >) || null;
          const selectedId = rawInvoice
            ? String(rawInvoice.id ?? rawInvoice.value ?? "").trim()
            : "";
          if (!rawInvoice || (ledgerId && selectedId !== ledgerId)) {
            let lookupKey = (config as any)?.vendorBillWiseReleaseMode
              ? "vendor_purchase_details"
              : "customer_invoice_details";
            const parentField =
              (control as any)?.parentField ||
              (control as any)?.Parent_Field ||
              config.parentField;
            if (parentField && formData[parentField]) {
              lookupKey = `${lookupKey}_${formData[parentField]}`;
            }
            const rawData = lookupsState.rawData[lookupKey] || [];
            rawInvoice =
              (rawData.find(
                (item: any) =>
                  String(item.id ?? item.value ?? "") === ledgerId ||
                  String(item.invoice_no ?? item.purchase_no ?? "") === ledgerId
              ) as Record<string, any>) || null;
          }
          if (!rawInvoice) {
            rawInvoice = resolveInvoiceRawItem(newData[rowIndex], fieldName);
          }
          if (!rawInvoice && ledgerId) {
            const gridRow = findInvoiceRowInGrid(ledgerId, invoiceRowsBase);
            if (gridRow) {
              rawInvoice = {
                id: gridRow.id,
                invoice_no:
                  gridRow.txt_purchase_no ??
                  gridRow.purchase_no ??
                  gridRow.txt_invoice_no ??
                  gridRow.invoice_no,
                purchase_no:
                  gridRow.txt_purchase_no ?? gridRow.purchase_no,
                amount_paid:
                  gridRow.txt_amount_paid_original ??
                  gridRow.amount_paid_original ??
                  gridRow.txt_amount_paid ??
                  gridRow.amount_paid,
              };
            }
          }
          const invoiceIdToSelect = String(
            rawInvoice?.id ?? rawInvoice?.value ?? ledgerId ?? ""
          ).trim();
          if (
            invoiceIdToSelect &&
            newData.some((r, idx) => {
              if (idx === rowIndex) return false;
              return getInvoiceIdFromRow(r) === invoiceIdToSelect;
            })
          ) {
            setError(
              `Row ${rowIndex + 1}: Invoice already selected in another receipt row`
            );
            newData[rowIndex] = clearBillWiseReleaseInvoiceSelection(
              newData[rowIndex]
            );
            handleDataChange(newData);
            syncBillWiseReleaseInvoicePreview(
              newData,
              formData,
              onFormDataChangeRef.current
            );
            return;
          }
          newData[rowIndex] = applyInvoiceSelectionToReceiptRow(
            newData[rowIndex],
            rawInvoice && typeof rawInvoice === "object" ? rawInvoice : null,
            invoiceRowsBase,
            { receiptRows: newData, rowIndex }
          );
          if (rawInvoice) {
            newData[rowIndex][`_${fieldName}_selectedOption`] = rawInvoice;
          }
        } else if (
          fieldName === BILL_WISE_RELEASE_ALLOCATE_FIELD ||
          fieldName === "amount"
        ) {
          newData[rowIndex] = capAllocateAmountOnReceiptRow(
            newData[rowIndex],
            invoiceRowsBase,
            { receiptRows: newData, rowIndex }
          );
        }
        newData = recalcBillWiseReleaseReceiptGrid(newData, invoiceRowsBase, {
          changedRowIndex: rowIndex,
        });
        newData = newData.map((row, idx) =>
          evaluateAllExpressionsForRow(row, idx)
        );
        syncBillWiseReleaseInvoicePreview(
          newData,
          formData,
          onFormDataChangeRef.current
        );
      }
    }

    // Full row re-eval so dependent fields (amount, VAT, net) stay in sync when inputs are cleared
    if (newData[rowIndex]) {
      newData[rowIndex] = evaluateAllExpressionsForRow(
        newData[rowIndex],
        rowIndex
      );
    }

    const syncToParentField = (control as any)?.syncToParentField;
    if (syncToParentField) {
      onFormDataChangeRef.current?.(syncToParentField, normalizedValue);
    }

    handleDataChange(newData);
  };

  // Track parent field value for stable dependency
  const configAny = config as any;
  const purchaseDetailsTableName = String(
    configAny.tableName || configAny.TableName || config.tableName || ""
  );
  const purchaseSupplierVatKey = useMemo(() => {
    if (!isPurchaseDetailsSubmodule(purchaseDetailsTableName)) return "";
    const exempt = getSupplierVatExemptionFromFormData(formData) ? "1" : "0";
    return [
      formData?.sup_id ?? "",
      formData?.supplier_id ?? "",
      formData?.drp_purchase ?? "",
      exempt,
    ].join("|");
  }, [
    purchaseDetailsTableName,
    formData?.sup_id,
    formData?.supplier_id,
    formData?.drp_purchase,
    formData?.sup_id_selectedData,
    formData?.supplier_id_selectedData,
    formData?.drp_supplier_selectedData,
    formData?.drp_purchase_selectedData,
  ]);

  useEffect(() => {
    if (!purchaseSupplierVatKey) return;
    if (lastPurchaseSupplierVatKeyRef.current === purchaseSupplierVatKey) return;

    lastPurchaseSupplierVatKeyRef.current = purchaseSupplierVatKey;
    if (data.length === 0) return;

    const taxOptions = lookupsState.lookups.tax_rates || [];
    const updated = applyPurchaseSupplierVatToRows(data, formData, taxOptions);
    const withExpressions = updated.map((row, index) =>
      evaluateAllExpressionsForRow(
        normalizeSubModuleRowForDisplay({ ...row }),
        index
      )
    );

    if (JSON.stringify(withExpressions) === JSON.stringify(data)) return;
    handleDataChange(withExpressions);
  }, [purchaseSupplierVatKey]);

  const submoduleLookupName =
    configAny.LookupName || configAny.lookupName || config.lookupName;
  const actualParentField =
    configAny.Parent_Field ||
    configAny.parentField ||
    config.parentField ||
    (submoduleLookupName === "debit_note_ledgers_with_parent_id"
      ? "drp_supplier"
      : undefined) ||
    (submoduleLookupName === "credit_note_ledgers_with_parent_id"
      ? "drp_customer"
      : undefined);
  const parentFieldValueString = useMemo(() => {
    // Use the same utility as Report component to find parent value
    const submoduleLookupName =
      configAny.LookupName || configAny.lookupName || config.lookupName;
    let currentParentValue = resolveSubmoduleParentValue(
      formData,
      actualParentField,
      submoduleLookupName
    );

    return currentParentValue ? String(currentParentValue) : "";
  }, [
    formData,
    actualParentField,
    submoduleLookupName,
    submoduleLookupName === PURCHASE_PENDING_PARTS_LOOKUP
      ? JSON.stringify(formData.jobcard_details ?? [])
      : "",
  ]);

  // Clear requested lookups when parent field values change (for dependent lookups)
  useEffect(() => {
    // Clear dependent lookup requests when parent values change
    // This allows refetching when parent selection changes
    const submoduleLookupName =
      configAny.LookupName || configAny.lookupName || config.lookupName;
    const currentParentValue = resolveSubmoduleParentValue(
      formData,
      actualParentField,
      submoduleLookupName
    );
    const currentParentValueString = currentParentValue
      ? String(currentParentValue)
      : "";
    if (
      previousParentForLookupRequestsRef.current !== currentParentValueString
    ) {
      // Clear all dependent lookup requests
      const keysToRemove: string[] = [];
      requestedLookupsRef.current.forEach((key) => {
        if (
          key.startsWith("dependent_") ||
          key.startsWith("rawdata_") ||
          key.startsWith("submodule_") ||
          key.startsWith("rawdata_proactive_") ||
          key.startsWith("rawdata_updateRowField_") ||
          key.startsWith("rawdata_evaluateExpression_") ||
          key.startsWith("rawdata_reeval_")
        ) {
          keysToRemove.push(key);
        }
      });
      keysToRemove.forEach((key) => requestedLookupsRef.current.delete(key));
      processedRawDataKeysRef.current.clear();
      previousParentForLookupRequestsRef.current = currentParentValueString;
    }
  }, [parentFieldValueString, actualParentField, formData, configAny.LookupName, configAny.lookupName, config.lookupName]);

  // Fetch lookups for SubModule childControls
  useEffect(() => {
    // Create a stable string representation of childControls to detect actual changes
    const childControlsKey = config.childControls
      ? JSON.stringify(
        config.childControls.map((c: any) => ({
          lookupName: c.LookupName || c.lookupName,
          parentField: c.Parent_Field || c.parentField,
          componentType: c.componentType,
        }))
      )
      : "";

    // Only proceed if childControls actually changed (by content, not just reference)
    if (childControlsKey === previousChildControlsRef.current) {
      return;
    }
    previousChildControlsRef.current = childControlsKey;

    const fetchLookups = async () => {
      if (!config.childControls) return;

      for (const control of config.childControls) {
        const controlAny = control as any;
        const lookupName = controlAny.LookupName || controlAny.lookupName;
        if (!lookupName) continue;

        const controlParentField = childControlParentField(controlAny);

        const accountMasterKey = getAccountMasterKeyFromConfig(controlAny);

        if (controlParentField) {
          // Dependent lookup — column declares its own Parent_Field
          const parentValue = formData[controlParentField];
          if (
            parentValue !== undefined &&
            parentValue !== null &&
            parentValue !== ""
          ) {
            const lookupKey = childControlLookupKey(
              lookupName,
              controlAny,
              formData
            );
            const requestKey = `dependent_${lookupKey}`;

            const isLoading = lookupsState.loading[lookupKey] || false;
            const lookupData = lookupsState.lookups[lookupKey];
            const existingOptions = Array.isArray(lookupData) ? lookupData : [];

            // CRITICAL FIX: Check if data already exists first, before checking ref
            // This prevents re-fetching when Redux state updates after a successful fetch
            if (Array.isArray(existingOptions) && existingOptions.length > 0) {
              // Data already exists, mark as requested to prevent future fetches
              if (!requestedLookupsRef.current.has(requestKey)) {
                requestedLookupsRef.current.add(requestKey);
              }
              // Skip fetching lookup data, but still check rawData
            } else if (
              !requestedLookupsRef.current.has(requestKey) &&
              !isLoading
            ) {
              // Only fetch if we haven't requested it and it's not loading
              requestedLookupsRef.current.add(requestKey);
              dispatch(
                fetchDependentLookup({
                  lookupName,
                  parentValue,
                  lookuptype:
                    control.componentType === ComponentType.SEARCHABLE_DROPDOWN
                      ? "Search_Select"
                      : "Select",
                  ...(accountMasterKey ? { accountMasterKey } : {}),
                  ...(control.componentType === ComponentType.SEARCHABLE_DROPDOWN
                    ? { search_arg: "" }
                    : {}),
                })
              );
            }

            // Also fetch rawData for ExpressionV2 to access all properties
            const rawDataKey = lookupKey;
            const rawDataRequestKey = `rawdata_${rawDataKey}`;
            const rawDataLookup = lookupsState.rawData[rawDataKey];
            const existingRawData = Array.isArray(rawDataLookup)
              ? rawDataLookup
              : [];
            const isRawDataLoading = lookupsState.loading[rawDataKey] || false;

            // CRITICAL FIX: Check if rawData already exists first
            if (Array.isArray(existingRawData) && existingRawData.length > 0) {
              // Raw data already exists, mark as requested
              if (!requestedLookupsRef.current.has(rawDataRequestKey)) {
                requestedLookupsRef.current.add(rawDataRequestKey);
              }
            } else if (
              !requestedLookupsRef.current.has(rawDataRequestKey) &&
              !isRawDataLoading
            ) {
              // Only fetch if we haven't requested it and it's not loading
              requestedLookupsRef.current.add(rawDataRequestKey);
              dispatch(
                fetchSubModuleRawData({
                  lookupName,
                  parentValue,
                  lookuptype: "Select",
                  ...(accountMasterKey ? { accountMasterKey } : {}),
                })
              );
            }
          }
        } else {
          // Independent lookup (tax_rates, department, etc.) — not scoped by submodule parentField
          const lookupKey = childControlLookupKey(
            lookupName,
            controlAny,
            formData
          );
          const requestKey = `independent_${lookupKey}`;

          const isLoading = lookupsState.loading[lookupKey] || false;
          const lookupData = lookupsState.lookups[lookupKey];
          const existingOptions = Array.isArray(lookupData) ? lookupData : [];
          const lastFetched = lookupsState.lastFetched[lookupKey];

          // Log cache check for journal_ledgers


          // CRITICAL FIX: Check if data already exists first, before checking ref
          // This prevents re-fetching when Redux state updates after a successful fetch
          if (Array.isArray(existingOptions) && existingOptions.length > 0) {
            // Data already exists, mark as requested to prevent future fetches
            if (!requestedLookupsRef.current.has(requestKey)) {
              requestedLookupsRef.current.add(requestKey);
            }

            // Skip fetching lookup data, but still check rawData
          } else if (
            !requestedLookupsRef.current.has(requestKey) &&
            !isLoading
          ) {
            // Only fetch if we haven't requested it and it's not loading
            requestedLookupsRef.current.add(requestKey);
            dispatch(
              fetchLookup({
                lookupName,
                params: {
                  parentid: "",
                  lookuptype:
                    control.componentType === ComponentType.SEARCHABLE_DROPDOWN
                      ? "Search_Select"
                      : "Select",
                  ...(accountMasterKey ? { accountMasterKey } : {}),
                  ...(control.componentType === ComponentType.SEARCHABLE_DROPDOWN
                    ? { search_arg: "" }
                    : {}),
                },
              })
            );
          }

          // CRITICAL FIX: Also fetch rawData for independent lookups
          // This ensures ExpressionV2 can access properties like purchase_rate, sale_rate, etc.
          const rawDataRequestKey = `rawdata_${lookupKey}`;
          const rawDataLookup = lookupsState.rawData[lookupKey];
          const existingRawData = Array.isArray(rawDataLookup)
            ? rawDataLookup
            : [];
          const isRawDataLoading = lookupsState.loading[lookupKey] || false;

          // CRITICAL FIX: Check if rawData already exists first
          if (Array.isArray(existingRawData) && existingRawData.length > 0) {
            // Raw data already exists, mark as requested
            if (!requestedLookupsRef.current.has(rawDataRequestKey)) {
              requestedLookupsRef.current.add(rawDataRequestKey);
            }
          } else if (
            !requestedLookupsRef.current.has(rawDataRequestKey) &&
            !isRawDataLoading
          ) {
            // Only fetch if we haven't requested it and it's not loading
            requestedLookupsRef.current.add(rawDataRequestKey);
            // For independent lookups, parentValue is empty string
            dispatch(
              fetchSubModuleRawData({
                lookupName,
                parentValue: "", // Empty for independent lookups
                lookuptype: "Select",
                ...(accountMasterKey ? { accountMasterKey } : {}),
              })
            );
          }
        }
      }
    };

    fetchLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.childControls,
    actualParentField,
    dispatch,
    parentFieldValueString, // Use string value for stable comparison
    formData, // Also depend on formData to trigger when parent values are set
  ]);

  // CRITICAL: Also fetch lookups when data is populated (for prepopulated data)
  // This ensures dropdown options are available when rows are prepopulated from API or loaded in edit mode
  // This effect runs when data is loaded from value prop (edit mode) or when data state changes
  useEffect(() => {
    // Use value prop if available (edit mode), otherwise use data state
    // CRITICAL: Prefer value prop for edit mode, but also check data state in case value prop hasn't synced yet
    const dataToCheck = (value && Array.isArray(value) && value.length > 0) ? value : (data && Array.isArray(data) && data.length > 0 ? data : []);

    // Only trigger if we have data (prepopulated) and lookups might not be loaded yet
    if (!dataToCheck || dataToCheck.length === 0) {

      return;
    }



    const fetchLookupsForPopulatedData = async () => {
      if (!config.childControls) return;

      // CRITICAL: For edit mode, fetch ALL dropdown lookups, not just ones with values
      // This ensures all dropdowns have their options available when data is loaded
      const hasData = dataToCheck && dataToCheck.length > 0;

      for (const control of config.childControls) {
        const controlAny = control as any;
        const lookupName = controlAny.LookupName || controlAny.lookupName;
        if (!lookupName) continue;

        // Check if this is a dropdown component
        const controlType = (control as any).componentType;
        const isDropdown =
          controlType === ComponentType.SELECT_DROPDOWN ||
          controlType === ComponentType.SEARCHABLE_DROPDOWN ||
          controlType === "selectDropdown" ||
          controlType === "searchableDropdown";

        if (!isDropdown) continue; // Only fetch for dropdowns

        // Check if this control has values in any row
        // CRITICAL: Check both BindValue (for user input) and Json_Attribute (for API data in edit mode)
        let hasValueInRows = false;
        let foundValue: any = null;

        if (hasData) {
          for (const row of dataToCheck) {
            const fieldName = controlAny.name || controlAny.BindValue;
            const jsonAttribute = controlAny.Json_Attribute;

            // Try BindValue first (for add mode where user enters data)
            let fieldValue = row[fieldName];

            // If not found and Json_Attribute exists, try Json_Attribute (for edit mode backend data)
            if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && jsonAttribute) {
              fieldValue = row[jsonAttribute];
            }

            // Also try case-insensitive and underscore variations for robustness
            if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && jsonAttribute) {
              const possibleKeys = [
                jsonAttribute.toLowerCase(),
                jsonAttribute.toUpperCase(),
                jsonAttribute.replace(/_/g, ""),
                fieldName.toLowerCase(),
                fieldName.toUpperCase(),
              ];
              for (const key of possibleKeys) {
                if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
                  fieldValue = row[key];
                  break;
                }
              }
            }

            if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
              hasValueInRows = true;
              foundValue = fieldValue;
              break; // Found a value, no need to check other rows
            }
          }
        }

        // CRITICAL: In edit mode (hasData), always fetch lookup options for ALL dropdowns
        // This ensures dropdowns can display values even if we haven't found the value yet
        // (the value might be in a different format or we might have missed it)
        // Only skip if there's no data at all (not in edit mode)
        if (!hasData) {

          continue; // Skip if no data at all (not in edit mode)
        }

        // If we have data but didn't find a value, still fetch the lookup
        // The value might be there but in a format we didn't detect
        if (!hasValueInRows && hasData) {

          // Set foundValue to null so we still fetch, but don't check for value existence
          foundValue = null;
        }



        // CRITICAL: Only use parentField from the control itself, NOT from config.parentField
        // Config.parentField is for SubModule-level lookups, not individual control lookups
        // Using config.parentField as fallback causes independent lookups to be treated as dependent
        const parentField = controlAny.Parent_Field || controlAny.parentField;

        const accountMasterKey = getAccountMasterKeyFromConfig(controlAny);

        if (parentField) {
          // Dependent lookup - control has its own parentField
          const parentValue = formData[parentField];
          if (
            parentValue !== undefined &&
            parentValue !== null &&
            parentValue !== ""
          ) {
            const lookupKey = childControlLookupKey(
              lookupName,
              controlAny,
              formData
            );
            const requestKey = `dependent_prepop_${lookupKey}`;

            // CRITICAL: Get current state values inside the effect (not from dependencies)
            // This prevents infinite loops when lookupsState changes
            const currentLookupsState = lookupsState;
            const existingOptions = currentLookupsState.lookups[lookupKey] || [];
            const isLoading = currentLookupsState.loading[lookupKey] || false;
            const alreadyRequested = requestedLookupsRef.current.has(requestKey);

            // CRITICAL: Only fetch if:
            // 1. Options are empty
            // 2. Not currently loading
            // 3. Not already requested (prevents duplicate fetches)
            const shouldFetch = existingOptions.length === 0 && !isLoading && !alreadyRequested;

            // Also check if the value exists in the options - if not, might need to re-fetch
            const valueExists = existingOptions.length > 0 && foundValue ? existingOptions.some((opt: any) =>
              String(opt.value) === String(foundValue) ||
              String(opt.id) === String(foundValue)
            ) : false;

            const isSearchSelect =
              control.componentType === ComponentType.SEARCHABLE_DROPDOWN;
            const selectedRequestKey = foundValue
              ? `${requestKey}__selected__${String(foundValue)}`
              : requestKey;
            const needsSelectedFetch =
              isSearchSelect &&
              Boolean(foundValue) &&
              !valueExists &&
              !requestedLookupsRef.current.has(selectedRequestKey);

            if (shouldFetch || needsSelectedFetch) {

              requestedLookupsRef.current.add(requestKey);
              if (needsSelectedFetch) {
                requestedLookupsRef.current.add(selectedRequestKey);
              }
              dispatch(
                fetchDependentLookup({
                  lookupName,
                  parentValue: String(parentValue),
                  lookuptype: isSearchSelect ? "Search_Select" : "Select",
                  ...(accountMasterKey ? { accountMasterKey } : {}),
                  ...(isSearchSelect
                    ? {
                        search_arg: "",
                        ...(foundValue
                          ? { selectedid: String(foundValue) }
                          : {}),
                      }
                    : {}),
                })
              );

            }
          } else {
            // Independent lookup
            const lookupKey = childControlLookupKey(
              lookupName,
              controlAny,
              formData
            );
            const requestKey = `independent_prepop_${lookupKey}`;

            // CRITICAL: Get current state values inside the effect (not from dependencies)
            // This prevents infinite loops when lookupsState changes
            const currentLookupsState = lookupsState;
            const existingOptions = currentLookupsState.lookups[lookupKey] || [];
            const isLoading = currentLookupsState.loading[lookupKey] || false;
            const alreadyRequested = requestedLookupsRef.current.has(requestKey);

            // CRITICAL: Only fetch if:
            // 1. Options are empty
            // 2. Not currently loading
            // 3. Not already requested (prevents duplicate fetches)
            const shouldFetch = existingOptions.length === 0 && !isLoading && !alreadyRequested;

            // Also check if the value exists in the options - if not, might need to re-fetch
            const valueExists = existingOptions.length > 0 && foundValue ? existingOptions.some((opt: any) =>
              String(opt.value) === String(foundValue) ||
              String(opt.id) === String(foundValue)
            ) : false;

            const isSearchSelect =
              control.componentType === ComponentType.SEARCHABLE_DROPDOWN;
            const selectedRequestKey = foundValue
              ? `${requestKey}__selected__${String(foundValue)}`
              : requestKey;
            const needsSelectedFetch =
              isSearchSelect &&
              Boolean(foundValue) &&
              !valueExists &&
              !requestedLookupsRef.current.has(selectedRequestKey);

            if (shouldFetch || needsSelectedFetch) {

              requestedLookupsRef.current.add(requestKey);
              if (needsSelectedFetch) {
                requestedLookupsRef.current.add(selectedRequestKey);
              }
              dispatch(
                fetchLookup({
                  lookupName,
                  params: {
                    parentid: "",
                    lookuptype: isSearchSelect ? "Search_Select" : "Select",
                    ...(accountMasterKey ? { accountMasterKey } : {}),
                    ...(isSearchSelect
                      ? {
                          search_arg: "",
                          ...(foundValue
                            ? { selectedid: String(foundValue) }
                            : {}),
                        }
                      : {}),
                  },
                })
              );

            }
          }
        }
      }
    };

    fetchLookupsForPopulatedData();
    // CRITICAL: Do NOT include lookupsState.lookups or lookupsState.loading in dependencies
    // They cause infinite loops because the effect re-runs every time a lookup loads
    // Instead, check these values inside the effect using the current state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, value, formData, config.childControls, dispatch]);

  // CRITICAL: Force re-render when lookup options finish loading
  // This ensures dropdowns update when options become available
  useEffect(() => {
    // This effect doesn't do anything, but by depending on lookupsState.lookups,
    // it forces a re-render when lookups are loaded, which causes renderField to run again
    // and check if values can now be matched with the newly loaded options

    // CRITICAL: Do NOT include lookupsState.lookups in dependencies
    // It causes infinite loops because the effect re-runs every time any lookup loads
    // This effect only logs, so it doesn't need to re-run when lookups change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CRITICAL: Watch for when lookup options become available and re-check if we need to fetch
  // This ensures dropdowns get their options even if they load after the data
  useEffect(() => {
    // Use value prop if available (edit mode), otherwise use data state
    const dataToCheck = (value && Array.isArray(value) && value.length > 0) ? value : data;

    // Only trigger if we have data
    if (!dataToCheck || dataToCheck.length === 0) return;
    if (!config.childControls) return;

    // Check each dropdown field to see if options are now available
    for (const control of config.childControls) {
      const controlAny = control as any;
      const lookupName = controlAny.LookupName || controlAny.lookupName;
      if (!lookupName) continue;

      // Check if this is a dropdown component
      const controlType = (control as any).componentType;
      const isDropdown =
        controlType === ComponentType.SELECT_DROPDOWN ||
        controlType === ComponentType.SEARCHABLE_DROPDOWN ||
        controlType === "selectDropdown" ||
        controlType === "searchableDropdown";

      if (!isDropdown) continue;

      // Check if this control has values in any row
      let foundValue: any = null;
      for (const row of dataToCheck) {
        const fieldName = controlAny.name || controlAny.BindValue;
        const jsonAttribute = controlAny.Json_Attribute;

        let fieldValue = row[fieldName];
        if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && jsonAttribute) {
          fieldValue = row[jsonAttribute];
        }

        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
          foundValue = fieldValue;
          break;
        }
      }

      if (!foundValue) continue;

      const parentField = controlAny.Parent_Field || controlAny.parentField;
      const actualParentField = parentField || config.parentField;

      if (actualParentField) {
        const parentValue = formData[actualParentField];
        if (parentValue !== undefined && parentValue !== null && parentValue !== "") {
          const lookupKey = `${lookupName}_${parentValue}`;
          const existingOptions = lookupsState.lookups[lookupKey] || [];

          // If options are now available, log success

        }
      } else {
        const lookupKey = lookupName;
        const existingOptions = lookupsState.lookups[lookupKey] || [];

        // If options are now available, log success

      }
    }
    // CRITICAL: Do NOT include lookupsState.lookups in dependencies
    // It causes infinite loops because the effect re-runs every time any lookup loads
    // We check lookupsState.lookups inside the effect instead
    // Also removed data and value to prevent loops when they change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, config.childControls]);

  // CRITICAL: Re-evaluate ExpressionV2 when lookup rawData becomes available
  // This ensures fields like [drp_code.sale_rate] are calculated when lookup data loads
  // This is especially important in edit mode where data loads before lookup data
  useEffect(() => {
    // Only re-evaluate if we have data and ExpressionV2 fields that depend on lookup properties
    const hasData = (value && value.length > 0) || (data && data.length > 0);
    if (!hasData) {
      // Reset processed keys when data is cleared
      processedRawDataKeysRef.current.clear();
      return;
    }

    // Check if any childControl has ExpressionV2 with property lookups (e.g., [drp_code.sale_rate])
    const propertyLookupControls = config.childControls?.filter((control) => {
      const controlAny = control as any;
      const expression = controlAny.ExpressionV2;
      if (!expression || typeof expression !== "string") return false;
      // Check if expression contains property access (e.g., [field.property])
      return /\[[\w]+\.[\w]+\]/.test(expression);
    }) || [];

    if (propertyLookupControls.length === 0) return;

    // Get current rawData state inside the effect (not from dependencies)
    const currentRawData = lookupsState.rawData;

    // For each property lookup control, check if its referenced lookup's rawData has loaded
    let allLookupsLoaded = true;
    let needsReEvaluation = false;

    for (const control of propertyLookupControls) {
      const controlAny = control as any;
      const expression = controlAny.ExpressionV2;
      if (!expression || typeof expression !== "string") continue;

      // Extract field references with properties (e.g., [drp_code.sale_rate])
      const propertyRefRegex = /\[([\w]+)\.([\w]+)\]/g;
      const matches = expression.matchAll(propertyRefRegex);

      for (const match of matches) {
        const fieldName = match[1]; // e.g., "drp_code"
        const property = match[2]; // e.g., "sale_rate"

        // Find the control that this field references
        const refControl = config.childControls?.find(
          (c: any) => (c.name || c.BindValue) === fieldName
        );

        if (!refControl) continue;

        const lookupName = (refControl as any).LookupName || (refControl as any).lookupName;
        if (!lookupName) continue;

        const lookupKey = childControlLookupKey(
          lookupName,
          refControl as any,
          formData
        );
        let rawData = currentRawData[lookupKey] || [];
        if (rawData.length === 0 && lookupKey !== lookupName) {
          rawData = currentRawData[lookupName] || [];
        }
        const isLoading = lookupsState.loading[lookupKey] || false;
        const requestKey = `rawdata_reeval_${lookupKey}`;
        const alreadyRequested = requestedLookupsRef.current.has(requestKey);

        if (rawData.length === 0) {
          allLookupsLoaded = false;
          // Check if we need to fetch it
          // CRITICAL: Only fetch if not loading and not already requested
          if (!isLoading && !alreadyRequested) {

            requestedLookupsRef.current.add(requestKey);
            const controlParent = childControlParentField(refControl as any);
            dispatch(
              fetchSubModuleRawData({
                lookupName,
                parentValue: controlParent
                  ? (formData[controlParent] || "")
                  : "",
                lookuptype: "Select",
              })
            );
          }
        } else {
          // RawData is loaded, check if we've already processed this lookup key
          // This prevents re-evaluating when the same rawData loads multiple times
          const hasProcessedThisKey = processedRawDataKeysRef.current.has(lookupKey);

          // RawData is loaded, check if we have values in rows that need this property
          const dataSource = value && value.length > 0 ? value : data;
          const hasValuesNeedingProperty = dataSource.some((row: any) => {
            const fieldValue = row[fieldName] || row[(refControl as any).Json_Attribute];
            return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
          });

          // Only mark for re-evaluation if we haven't processed this key yet
          if (hasValuesNeedingProperty && !hasProcessedThisKey) {
            needsReEvaluation = true;

            // Also check if the current value in the row is different from what it should be
            // This helps detect if ExpressionV2 needs to be re-evaluated
            const controlWithExpression = propertyLookupControls.find((c: any) => {
              const expr = (c as any).ExpressionV2;
              return expr && typeof expr === "string" && expr.includes(`[${fieldName}.${property}]`);
            });

            if (controlWithExpression) {
              const expressionFieldName = (controlWithExpression as any).name || (controlWithExpression as any).BindValue;
              const expressionJsonAttr = (controlWithExpression as any).Json_Attribute;

              // Check if any row has a zero or empty value for the expression field
              // This indicates it might need recalculation
              const needsRecalc = dataSource.some((row: any) => {
                const currentValue = row[expressionFieldName] || row[expressionJsonAttr];
                return currentValue === undefined || currentValue === null || currentValue === "" || currentValue === "0" || currentValue === 0;
              });

              if (needsRecalc) {
                needsReEvaluation = true;
              }
            }
          }
        }
      }
    }

    // Only re-evaluate if all required lookups are loaded and we have data that needs it
    if (!allLookupsLoaded || !needsReEvaluation) {

      return;
    }

    // Re-evaluate ExpressionV2 for all rows
    const dataSource = value && value.length > 0 ? value : data;
    let rowsWithReEvaluatedExpressions = dataSource.map((row: any, index: number) => {
      return evaluateAllExpressionsForRow(row, index);
    });

    const lookupNameExpr = getSubmoduleLookupName();
    if (isSalesReturnInvoiceSubmoduleLookup(lookupNameExpr)) {
      const saved = getSavedSalesReturnRows();
      if (saved.length > 0) {
        rowsWithReEvaluatedExpressions = mergeSavedSalesReturnLines(
          rowsWithReEvaluatedExpressions,
          saved
        );
      }
    }

    // CRITICAL: Only update if values actually changed AND the new values are better (not "0" overwriting real values)
    // This prevents overwriting correct values that were loaded from backend
    let shouldUpdate = false;
    let hasImprovements = false;

    for (let i = 0; i < dataSource.length; i++) {
      const oldRow = dataSource[i];
      const newRow = rowsWithReEvaluatedExpressions[i];

      // Check each ExpressionV2 field
      for (const control of propertyLookupControls) {
        const controlAny = control as any;
        const expressionFieldName = controlAny.name || controlAny.BindValue;
        const expressionJsonAttr = controlAny.Json_Attribute;

        const oldValue = oldRow[expressionFieldName] || oldRow[expressionJsonAttr];
        const newValue = newRow[expressionFieldName] || newRow[expressionJsonAttr];

        // If old value exists and is not "0", and new value is "0", don't overwrite
        if (oldValue !== undefined && oldValue !== null && oldValue !== "" && oldValue !== "0" && oldValue !== 0) {
          if (newValue === "0" || newValue === 0 || newValue === "" || newValue === null || newValue === undefined) {
            // Preserve old value - don't overwrite with "0"
            newRow[expressionFieldName] = oldValue;
            if (expressionJsonAttr && !newRow[expressionFieldName]) {
              newRow[expressionJsonAttr] = oldValue;
            }

          } else if (oldValue !== newValue) {
            // New value is different and not "0", this is an improvement
            hasImprovements = true;
          }
        } else if (newValue !== oldValue && newValue !== "0" && newValue !== 0 && newValue !== "" && newValue !== null && newValue !== undefined) {
          // Old value was "0" or empty, new value is not - this is an improvement
          hasImprovements = true;
        }
      }
    }

    const currentDataString = JSON.stringify(dataSource);
    const newDataString = JSON.stringify(rowsWithReEvaluatedExpressions);
    shouldUpdate = currentDataString !== newDataString && hasImprovements;

    if (shouldUpdate) {


      // Mark referenced lookup keys as processed to prevent re-processing
      for (const control of propertyLookupControls) {
        const expression = (control as any).ExpressionV2;
        if (!expression || typeof expression !== "string") continue;
        const propertyRefRegex = /\[([\w]+)\.([\w]+)\]/g;
        for (const match of expression.matchAll(propertyRefRegex)) {
          const fieldName = match[1];
          const refControl = config.childControls?.find(
            (c: any) => (c.name || c.BindValue) === fieldName
          );
          if (!refControl) continue;
          const refLookupName =
            (refControl as any).LookupName || (refControl as any).lookupName;
          if (!refLookupName) continue;
          const lookupKey = childControlLookupKey(
            refLookupName,
            refControl as any,
            formData
          );
          processedRawDataKeysRef.current.add(lookupKey);
          if (lookupKey !== refLookupName) {
            processedRawDataKeysRef.current.add(refLookupName);
          }
        }
      }

      isUpdatingFromInternalRef.current = true;
      setData(rowsWithReEvaluatedExpressions);
      const validationError = validateData(rowsWithReEvaluatedExpressions);
      setError(validationError);
      onChangeRef.current?.(config.name || (config as any).BindValue, rowsWithReEvaluatedExpressions);
    }

    // CRITICAL: Do NOT include lookupsState.rawData in dependencies
    // It causes infinite loops because the object reference changes every time any lookup loads
    // We check rawData inside the effect and use processedRawDataKeysRef to prevent re-processing
    // Also do NOT include data and value - they cause loops when the effect updates data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, config.childControls, config.name, lookupsState.rawData["tax_rates"]?.length]);

  // Auto-populate SubModule rows from lookupName when parentField changes
  // Flow: User Selects Parent Field → SubModule detects change → GetLookupItems() → Backend API call → Returns list → SetLookupData() creates rows
  useEffect(() => {
    const lookupName = configAny.LookupName || configAny.lookupName || config.lookupName;
    const parentField = actualParentField;



    const tableName = String(
      configAny.tableName || configAny.TableName || config.tableName || ""
    ).toLowerCase();
    if (
      lookupName === "purchase_order_details" &&
      tableName === "voucher_details" &&
      isRecordEditScreen(formData)
    ) {
      return;
    }

    // Only proceed if both lookupName and parentField are configured
    if (!lookupName || !parentField) {

      return;
    }

    // Step 1: Get parent field value using the same utility as Report component
    // This handles all the edge cases and variations
    // CRITICAL: Check parent value FIRST before checking for existing data
    // This ensures parent change detection works even when data exists
    let parentValue: any = resolveSubmoduleParentValue(
      formData,
      parentField,
      lookupName
    );

    // Convert to string for consistency
    parentValue = parentValue ? String(parentValue) : parentValue;

    // Step 2: Check if parent value has changed (SubModule detected change)
    // CRITICAL: Check this BEFORE checking for existing data
    // This ensures we clear data when parent changes, even if data exists
    const parentValueChanged =
      previousParentValueRef.current !== parentValue &&
      previousParentValueRef.current !== undefined;

    // CRITICAL FIX: Always clear data when parent value changes to a different value
    // This ensures that when user changes jobcard in add mode, old data is cleared
    // and new data is fetched for the new jobcard
    // Only skip clearing on initial load (when previousParentValueRef is undefined)
    if (parentValueChanged) {
      lookupAutoPopulateSuppressedRef.current = false;
      // Edit mode: invoice parent is prefilled after saved submodule rows load — keep DB lines.
      if (hasPersistedSubModuleRows(value) || hasCreditDebitNoteSplitRows(value, formData?.id)) {
        previousParentValueRef.current = parentValue;
        return;
      }

      // Always clear when parent changes (user changed jobcard)
      // This prevents old data from persisting when switching between jobcards

      isUpdatingFromInternalRef.current = true;
      setData([]);
      setShowTable(false);
      lastPopulatedDataRef.current = "";
      lastValueRef.current = JSON.stringify([]);
      lastLookupDataRef.current = [];
      processedRawDataKeysRef.current.clear();
      // Clear the processed parent value ref to force re-fetch of lookup data
      // CRITICAL: Set to undefined to ensure fetch logic treats this as a new parent
      lastProcessedParentValueRef.current = undefined;
      // Clear any requested lookups for both old and new parent to allow re-fetching
      const oldLookupKey = `${lookupName}_${previousParentValueRef.current}`;
      const newLookupKey = `${lookupName}_${parentValue}`;
      const oldRequestKey = `submodule_${oldLookupKey}`;
      const newRequestKey = `submodule_${newLookupKey}`;
      // Clear all related lookup request flags
      requestedLookupsRef.current.delete(oldLookupKey);
      requestedLookupsRef.current.delete(`rawdata_${oldLookupKey}`);
      requestedLookupsRef.current.delete(oldRequestKey);
      requestedLookupsRef.current.delete(newLookupKey);
      requestedLookupsRef.current.delete(`rawdata_${newLookupKey}`);
      requestedLookupsRef.current.delete(newRequestKey);
      // Clear the lookup data from Redux store for old parent (optional, but helps)
      // Note: We can't directly modify Redux state here, but clearing the refs ensures fresh fetch
      publishSubmoduleRows([]);

    }

    // Update the ref to track previous value BEFORE checking for existing data
    // This ensures the next change is detected correctly
    previousParentValueRef.current = parentValue;

    // IMPORTANT: Don't auto-populate if data already exists (from value prop in edit mode)
    // BUT: Only skip if parent hasn't changed (edit mode with same parent)
    // If parent changed, we already cleared data above, so proceed with fetch
    const hasExistingData = value && Array.isArray(value) && value.length > 0;
    const salesReturnEditWithSaved =
      isSalesReturnInvoiceSubmoduleLookup(lookupName) &&
      isRecordEditScreen(formData) &&
      (hasPersistedSubModuleRows(value) || savedSalesReturnRowsRef.current.length > 0);
    // Purchase return edit: keep saved lines; do not replace with full purchase invoice template.
    const purchaseReturnEditWithSaved =
      isPurchaseReturnSubmoduleLookup(lookupName) &&
      isRecordEditScreen(formData) &&
      (hasPersistedSubModuleRows(value) ||
        savedPurchaseReturnRowsRef.current.length > 0 ||
        hasExistingData);
    const creditDebitNoteEditWithSaved =
      isCreditDebitNoteSplitSubmoduleLookup(lookupName) &&
      isRecordEditScreen(formData) &&
      (hasCreditDebitNoteSplitRows(value, formData?.id) ||
        savedCreditDebitNoteSplitRowsRef.current.length > 0 ||
        hasExistingData);
    const partyLedgerPlaceholderInValue =
      PARTY_LEDGER_SUBMODULE_LOOKUPS.has(lookupName || "") &&
      submoduleRowsAreOnlyPartyLedgerPlaceholders(
        Array.isArray(value) ? value : undefined
      );

    if (
      purchaseReturnEditWithSaved ||
      creditDebitNoteEditWithSaved ||
      (hasExistingData &&
        !parentValueChanged &&
        !salesReturnEditWithSaved &&
        !partyLedgerPlaceholderInValue) ||
      (hasPersistedSubModuleRows(value) &&
        !isSalesReturnInvoiceSubmoduleLookup(lookupName) &&
        !partyLedgerPlaceholderInValue) ||
      (hasCreditDebitNoteSplitRows(value, formData?.id) &&
        isCreditDebitNoteSplitSubmoduleLookup(lookupName))
    ) {
      // Data already exists from value prop and parent hasn't changed, skip auto-population
      // Sales-return edit still fetches invoice lines, then merges saved return qty/net on top.

      return;
    }

    // Only proceed if parent value exists
    if (
      parentValue === undefined ||
      parentValue === null ||
      parentValue === ""
    ) {
      // Clear data if parent value is cleared
      // Only preserve data if this is initial load with data from value prop (edit mode)
      const hasExistingDataFromValue =
        value && Array.isArray(value) && value.length > 0;
      const isInitialLoad = previousParentValueRef.current === undefined;

      if (data.length > 0 && !(isInitialLoad && hasExistingDataFromValue)) {

        isUpdatingFromInternalRef.current = true;
        setData([]);
        lastPopulatedDataRef.current = "";
        lastValueRef.current = JSON.stringify([]);
        lastLookupDataRef.current = [];
        onChangeRef.current?.(config.name, []);
      }
      return;
    }

    // Step 3: GetLookupItems("issued_Spareparts_details", IssueNoID)
    const lookupKey = `${lookupName}_${parentValue}`;
    // Use rawData for SubModule to access all fields without transformation
    // This contains the list returned from the backend API call
    const lookupData = lookupsState.rawData[lookupKey] || [];
    const isLoading = lookupsState.loading[lookupKey] || false;

    // Step 4: Check if lookup data has changed (SubModule.OnParametersSetAsync() detects change)
    const lookupDataChanged =
      JSON.stringify(lastLookupDataRef.current) !== JSON.stringify(lookupData);

    // Update ref to track lookup data
    if (lookupDataChanged) {
      lastLookupDataRef.current = lookupData;
    }

    // For SubModule, always fetch raw data directly from API to preserve all fields
    // Don't rely on transformed lookup data from Redux store
    const fetchRawLookupData = async () => {
      try {
        const endpoint = `/pabws/utility/${lookupName}`;
        const payload = {
          parentid: String(parentValue),
          lookuptype: "",
        };

        const response = await apiService.post<any>(endpoint, payload, {
          includeAuthToken: true,
          errorHandling: "return-empty",
        });

        // Extract raw data array from response
        let rawData: any[] = [];
        if (response) {
          if (response.data && Array.isArray(response.data)) {
            rawData = response.data;
          } else if (Array.isArray(response)) {
            rawData = response;
          } else if (response.success && response.data) {
            rawData = Array.isArray(response.data)
              ? response.data
              : [response.data];
          }
        }

        // Transform raw data into rows
        // CRITICAL: Clear data if lookup returns empty array (new vendor has no data)
        if (rawData.length === 0) {

          isUpdatingFromInternalRef.current = true;
          setData([]);
          lastPopulatedDataRef.current = "";
          lastValueRef.current = JSON.stringify([]);
          onChangeRef.current?.(config.name, []);
          return;
        }

        if (rawData.length > 0) {
          const transformedRows = rawData.map((item: any) => {
            const row: Record<string, any> = {};

            config.childControls?.forEach((control: any) => {
              const fieldName = control.name || control.BindValue;
              const jsonAttribute = control.Json_Attribute;

              if (fieldName && jsonAttribute) {
                // Try to get value from raw item using Json_Attribute (preserve numeric 0)
                let value: any =
                  item[jsonAttribute] ??
                  item[jsonAttribute.toLowerCase()] ??
                  item[jsonAttribute.toUpperCase()] ??
                  item[jsonAttribute.replace(/_/g, "")];

                if (value === undefined || value === null) {
                  value =
                    item[fieldName] ??
                    item[fieldName.toLowerCase()] ??
                    item[fieldName.toUpperCase()] ??
                    "";
                }

                value = applySubModuleDropdownValueFromLookupRow(
                  control,
                  jsonAttribute,
                  item,
                  value
                );

                // Apply default value if no value found (preserve numeric 0)
                if (
                  (value === undefined || value === null || value === "") &&
                  control.defaultValue !== undefined
                ) {
                  value = control.defaultValue;

                  // CRITICAL: If default value is a label (like "5%") and this is a lookup dropdown,
                  // convert it to the ID before storing
                  if (
                    (control.componentType === ComponentType.SELECT_DROPDOWN ||
                      control.componentType === ComponentType.SEARCHABLE_DROPDOWN) &&
                    (control.lookupName || control.LookupName)
                  ) {
                    const lookupName = control.lookupName || control.LookupName;
                    if (lookupName) {
                      const parentFieldForLookup =
                        control.Parent_Field ||
                        control.parentField ||
                        config.parentField;
                      let lookupKey = lookupName;

                      if (parentFieldForLookup) {
                        const parentValue = formData[parentFieldForLookup];
                        if (
                          parentValue !== undefined &&
                          parentValue !== null &&
                          parentValue !== ""
                        ) {
                          lookupKey = `${lookupName}_${parentValue}`;
                        }
                      }

                      const lookupOptions = lookupsState.lookups[lookupKey] || [];

                      // Check if the default value matches a label instead of an ID
                      const matchingOption = lookupOptions.find(
                        (opt: any) =>
                          opt.label === value ||
                          String(opt.label) === String(value) ||
                          (opt as any).name === value ||
                          String((opt as any).name) === String(value)
                      );

                      if (matchingOption) {
                        // Convert label to ID
                        value =
                          matchingOption.value || (matchingOption as any).id || value;


                      }
                    }
                  }
                }

                row[fieldName] = value;
                if (jsonAttribute && jsonAttribute !== fieldName) {
                  row[jsonAttribute] = value;
                }
              } else if (fieldName) {
                // If no Json_Attribute, try to match by fieldName
                row[fieldName] =
                  item[fieldName] ||
                  item[fieldName.toLowerCase()] ||
                  control.defaultValue ||
                  "";
              }
            });

            copyLookupMetadataToRow(row, item);
            return normalizeSubModuleRowForDisplay(row);
          });

          let rowsForVat = transformedRows;
          if (isPurchaseDetailsSubmodule(purchaseDetailsTableName)) {
            const taxOptions = lookupsState.lookups.tax_rates || [];
            rowsForVat = applyPurchaseSupplierVatToRows(
              transformedRows,
              formData,
              taxOptions
            );
          }

          // Evaluate all ExpressionV2 fields for each row after transformation
          let rowsWithExpressions = rowsForVat.map(
            (row: any, index: number) => {
              return evaluateAllExpressionsForRow(row, index);
            }
          );

          if (isSalesReturnInvoiceSubmoduleLookup(lookupName)) {
            const saved = getSavedSalesReturnRows();
            if (saved.length > 0) {
              rowsWithExpressions = mergeSavedSalesReturnLines(
                rowsWithExpressions,
                saved
              );
            } else if (hasPersistedSubModuleRows(rowsWithExpressions)) {
              rememberSavedSalesReturnRows(rowsWithExpressions);
            }
          }

          if (lookupName === "invoice_items" && parentValue) {
            rowsWithExpressions = await enrichInvoiceSpareRowsWithDiscounts(
              rowsWithExpressions,
              parentValue
            );
            rowsWithExpressions = rowsWithExpressions.map(
              (row: any, index: number) =>
                evaluateAllExpressionsForRow(row, index)
            );
          }

          // Only update if parent value changed, lookup data changed, or data is different (to avoid infinite loops)
          const newDataString = JSON.stringify(rowsWithExpressions);
          if (lastPopulatedDataRef.current !== newDataString) {

            isUpdatingFromInternalRef.current = true;
            setData(rowsWithExpressions);
            const validationError = validateData(rowsWithExpressions);
            setError(validationError);
            lastValueRef.current = newDataString;
            publishSubmoduleRows(rowsWithExpressions);
            lastPopulatedDataRef.current = newDataString;
          }
        } else {

          isUpdatingFromInternalRef.current = true;
          setData([]);
          lastValueRef.current = JSON.stringify([]);
          publishSubmoduleRows([]);
          lastPopulatedDataRef.current = "";
        }
      } catch (error) {

        isUpdatingFromInternalRef.current = true;
        setData([]);
        lastValueRef.current = JSON.stringify([]);
        publishSubmoduleRows([]);
      }
    };

    // Step 5: If lookup data is available and not loading, populate rows (SetLookupData() creates rows)
    // IMPORTANT: Only auto-populate from lookup if data doesn't already exist (from value prop)
    // In edit mode, data should come from value prop, not from lookup auto-population
    // Note: We already checked hasExistingData at the beginning and returned early if it exists
    // This check is for the lookupData path - only populate if no existing data in state
    const hasExistingDataInState =
      data && Array.isArray(data) && data.length > 0;
    const partyLedgerPlaceholderInState =
      PARTY_LEDGER_SUBMODULE_LOOKUPS.has(lookupName || "") &&
      submoduleRowsAreOnlyPartyLedgerPlaceholders(data);

    if (
      lookupData.length > 0 &&
      !isLoading &&
      !lookupAutoPopulateSuppressedRef.current &&
      (!hasExistingDataInState ||
        parentValueChanged ||
        partyLedgerPlaceholderInState ||
        (salesReturnEditWithSaved &&
          isSalesReturnInvoiceSubmoduleLookup(lookupName)))
    ) {
      // Step 6: Transform lookup data into rows based on childControls mapping
      // Each row populated with: Code, Item Name, Qty, Rate, etc.
      const transformedRows = lookupData.map((lookupItem: any) => {
        const row: Record<string, any> = {};

        config.childControls?.forEach((control: any) => {
          const fieldName = control.name || control.BindValue;
          const jsonAttribute = control.Json_Attribute;

          if (fieldName && jsonAttribute) {
            // Try to get value from lookup item using Json_Attribute
            let value: any =
              lookupItem[jsonAttribute] ??
              lookupItem[jsonAttribute.toLowerCase()] ??
              lookupItem[jsonAttribute.toUpperCase()] ??
              lookupItem[jsonAttribute.replace(/_/g, "")];

            if (value === undefined || value === null) {
              value =
                lookupItem[fieldName] ??
                lookupItem[fieldName.toLowerCase()] ??
                lookupItem[fieldName.toUpperCase()] ??
                "";
            }

            value = applySubModuleDropdownValueFromLookupRow(
              control,
              jsonAttribute,
              lookupItem,
              value
            );

            // Apply default value if no value found
            if (
              (value === undefined || value === null || value === "") &&
              control.defaultValue !== undefined
            ) {
              value = control.defaultValue;
            }

            row[fieldName] = value;
            if (jsonAttribute && jsonAttribute !== fieldName) {
              row[jsonAttribute] = value;
            }
          } else if (fieldName) {
            // If no Json_Attribute, try to match by fieldName
            row[fieldName] =
              lookupItem[fieldName] ??
              lookupItem[fieldName.toLowerCase()] ??
              control.defaultValue ??
              "";
          }
        });

        copyLookupMetadataToRow(row, lookupItem);
        const normalized = normalizeSubModuleRowForDisplay(row);
        return PARTY_LEDGER_SUBMODULE_LOOKUPS.has(lookupName || "")
          ? enrichPartyLedgerSubmoduleRow(normalized, lookupItem)
          : isPurchaseReturnSubmoduleLookup(lookupName)
            ? applyPurchaseReturnInvoiceTemplateRow(normalized, lookupItem)
            : normalized;
      });

      // Only update if parent value changed, lookup data changed, or data is different (to avoid infinite loops)
      const newDataString = JSON.stringify(transformedRows);
      const shouldUpdate =
        parentValueChanged ||
        lookupDataChanged ||
        partyLedgerPlaceholderInState ||
        lastPopulatedDataRef.current !== newDataString;

      if (shouldUpdate) {
        // Evaluate all ExpressionV2 fields for each row after transformation
        let rowsWithExpressions = transformedRows.map(
          (row: any, index: number) => {
            return evaluateAllExpressionsForRow(row, index);
          }
        );

        if (isSalesReturnInvoiceSubmoduleLookup(lookupName)) {
          const saved = getSavedSalesReturnRows();
          if (saved.length > 0) {
            rowsWithExpressions = mergeSavedSalesReturnLines(
              rowsWithExpressions,
              saved
            );
          } else if (hasPersistedSubModuleRows(rowsWithExpressions)) {
            rememberSavedSalesReturnRows(rowsWithExpressions);
          }
        }

        if (
          isBillWiseReleaseInvoiceSubmodule(config as any) &&
          ((config as any).billWiseReleaseInvoiceMode ||
            (config as any).vendorBillWiseReleaseInvoiceMode)
        ) {
          rowsWithExpressions = recalcBillWiseReleaseSubmoduleByConfig(
            config as any,
            rowsWithExpressions
          );
          rowsWithExpressions = rowsWithExpressions.map((row, idx) =>
            evaluateAllExpressionsForRow(row, idx)
          );
        }

        const evaluatedDataString = JSON.stringify(rowsWithExpressions);
        lastPopulatedDataRef.current = evaluatedDataString;
        // Force state update - always set the new data
        isUpdatingFromInternalRef.current = true;
        setData(rowsWithExpressions);
        const validationError = validateData(rowsWithExpressions);
        setError(validationError);
        lastValueRef.current = evaluatedDataString;
        publishSubmoduleRows(rowsWithExpressions);
        if (PARTY_LEDGER_SUBMODULE_LOOKUPS.has(lookupName || "")) {
          rowsWithExpressions.forEach((row) =>
            seedPartyLedgerDropdownOption(dispatch, config.childControls, row)
          );
        }
      } else {

      }
    } else if (!isLoading && lookupData.length === 0 && parentValue) {
      // Step 3: GetLookupItems() - Backend API call with parentid parameter
      // If lookup is not loading and has no data, fetch raw data
      // CRITICAL: Only fetch if parent value exists and we haven't already requested it
      const lookupKey = `${lookupName}_${parentValue}`;
      const requestKey = `submodule_${lookupKey}`;

      // CRITICAL: Only fetch if parent value actually changed OR this is the first time we see this parent value
      // This prevents infinite loops when rawData updates trigger the effect
      // But allows initial fetch when screen loads with parent value already set
      const parentValueChangedForFetch =
        lastProcessedParentValueRef.current !== parentValue;
      const isInitialFetch = lastProcessedParentValueRef.current === undefined;

      // Check if we have data for this lookup
      const hasDataForThisLookup = lookupsState.rawData[lookupKey] && lookupsState.rawData[lookupKey].length > 0;
      const isStillLoading = lookupsState.loading[lookupKey] || false;

      // CRITICAL: Only clear request flag if parent value changed (new parent = need new data)
      // Don't clear if same parent - this prevents infinite loops when API returns empty or fails
      // If API fails for same parent, we don't want to keep retrying
      const shouldClearRequestFlag =
        requestedLookupsRef.current.has(requestKey) &&
        !hasDataForThisLookup &&
        !isStillLoading &&
        parentValueChangedForFetch; // Only clear if parent changed

      if (shouldClearRequestFlag) {

        requestedLookupsRef.current.delete(requestKey);
      }

      // Check if we've already requested this lookup to prevent infinite loops
      // CRITICAL: Only fetch if parent value changed OR it's initial fetch OR we don't have data and parent hasn't changed
      // But don't fetch if we already requested for this parent value
      if (
        (parentValueChangedForFetch || isInitialFetch) &&
        !requestedLookupsRef.current.has(requestKey)
      ) {
        requestedLookupsRef.current.add(requestKey);
        lastProcessedParentValueRef.current = parentValue;

        // Clear data if it exists (new parent has no data yet)
        if (data.length > 0 && !hasExistingData) {

          isUpdatingFromInternalRef.current = true;
          setData([]);
          lastPopulatedDataRef.current = "";
          lastValueRef.current = JSON.stringify([]);
          onChangeRef.current?.(config.name, []);
        }

        // Fetch raw data for SubModule using dispatch (this will update Redux state)

        dispatch(
          fetchSubModuleRawData({
            lookupName,
            parentValue,
            lookuptype: "Select", // Default to Select for SubModule lookups
          })
        );
        // After this dispatch, the API will be called and rawData will be updated
        // The useEffect will re-run when dependencies change, but won't fetch again due to the guard above

      }
    }

    // REMOVED: The fetchRawLookupData() call was causing duplicate API calls
    // We only use fetchSubModuleRawData dispatch above to avoid infinite loops

    // CRITICAL FIX: Remove lookupsState.rawData and lookupsState.loading from dependencies
    // They cause infinite loops because the effect re-runs when these values change
    // Instead, we check these values inside the effect and use requestedLookupsRef to prevent duplicate calls
    // Also remove value from dependencies - it causes loops when data is updated
    // Only depend on the specific parent field value and config, not entire formData or lookup state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configAny.LookupName || configAny.lookupName || config.lookupName,
    actualParentField,
    parentFieldValueString, // CRITICAL: Include this to trigger when parent value is set in formData
    config.name,
    // Note: We intentionally exclude:
    // - lookupsState.rawData and lookupsState.loading (cause infinite loops)
    // - value (causes loops when data is updated)
    // The effect reads these values directly from the hook but doesn't depend on them
  ]);

  // Separate useEffect to watch for rawData changes and populate when data arrives
  // This ensures we populate rows when API response arrives, even if parent value hasn't changed
  useEffect(() => {
    const lookupName = configAny.LookupName || configAny.lookupName || config.lookupName;
    const parentField = actualParentField;

    const tableNameRaw = String(
      configAny.tableName || configAny.TableName || config.tableName || ""
    ).toLowerCase();
    if (
      lookupName === "purchase_order_details" &&
      tableNameRaw === "voucher_details" &&
      isRecordEditScreen(formData)
    ) {
      return;
    }

    // Only proceed if both lookupName and parentField are configured
    if (!lookupName || !parentField) return;

    // Don't auto-populate if data already exists (from value prop in edit mode)
    const hasExistingData = value && Array.isArray(value) && value.length > 0;
    const salesReturnEditWithSaved =
      isSalesReturnInvoiceSubmoduleLookup(lookupName) &&
      isRecordEditScreen(formData) &&
      (hasPersistedSubModuleRows(value) || savedSalesReturnRowsRef.current.length > 0);
    // Purchase return edit: preserve saved qty/net and avoid overwriting from purchase_details rawData.
    const purchaseReturnEditWithSaved =
      isPurchaseReturnSubmoduleLookup(lookupName) &&
      isRecordEditScreen(formData) &&
      (hasPersistedSubModuleRows(value) ||
        savedPurchaseReturnRowsRef.current.length > 0 ||
        hasExistingData);
    const creditDebitNoteEditWithSaved =
      isCreditDebitNoteSplitSubmoduleLookup(lookupName) &&
      isRecordEditScreen(formData) &&
      (hasCreditDebitNoteSplitRows(value, formData?.id) ||
        savedCreditDebitNoteSplitRowsRef.current.length > 0 ||
        hasExistingData);
    const partyLedgerPlaceholderOnly =
      PARTY_LEDGER_SUBMODULE_LOOKUPS.has(lookupName || "") &&
      submoduleRowsAreOnlyPartyLedgerPlaceholders(
        Array.isArray(value) ? value : undefined
      );

    if (
      purchaseReturnEditWithSaved ||
      creditDebitNoteEditWithSaved ||
      ((hasExistingData || hasPersistedSubModuleRows(value)) &&
        !salesReturnEditWithSaved &&
        !partyLedgerPlaceholderOnly) ||
      (hasCreditDebitNoteSplitRows(value, formData?.id) &&
        isCreditDebitNoteSplitSubmoduleLookup(lookupName))
    ) {
      return;
    }

    // Get parent value using the same utility as Report component
    let parentValue: any = resolveSubmoduleParentValue(
      formData,
      parentField,
      lookupName
    );

    // Convert to string for consistency
    parentValue = parentValue ? String(parentValue) : parentValue;

    // Only proceed if parent value exists
    if (!parentValue) return;

    const lookupKey = `${lookupName}_${parentValue}`;
    const lookupData = lookupsState.rawData[lookupKey] || [];
    const isLoading = lookupsState.loading[lookupKey] || false;

    // Repopulate when parent changes even if rows still exist (stale data from prior customer)
    const parentChangedForRawDataEffect =
      lastProcessedParentValueRef.current !== undefined &&
      lastProcessedParentValueRef.current !== parentValue;
    const hasExistingDataInState = data && Array.isArray(data) && data.length > 0;
    const partyLedgerPlaceholderInState =
      PARTY_LEDGER_SUBMODULE_LOOKUPS.has(lookupName || "") &&
      submoduleRowsAreOnlyPartyLedgerPlaceholders(data);

    if (
      parentChangedForRawDataEffect &&
      hasExistingDataInState &&
      !partyLedgerPlaceholderInState
    ) {
      isUpdatingFromInternalRef.current = true;
      setData([]);
      setShowTable(false);
      lastPopulatedDataRef.current = "";
      lastValueRef.current = JSON.stringify([]);
      lastLookupDataRef.current = [];
      processedRawDataKeysRef.current.clear();
      publishSubmoduleRows([]);
    }

    if (
      lookupData.length > 0 &&
      !isLoading &&
      !lookupAutoPopulateSuppressedRef.current
    ) {
      // Check if this data was already processed for this parent
      const lookupDataString = JSON.stringify(lookupData);
      const populateKey = `${parentValue}::${lookupDataString}`;
      if (lastRawDataPopulateKeyRef.current === populateKey) {
        return; // Already processed this data for this parent
      }



      // Transform lookup data into rows
      const transformedRows = lookupData.map((lookupItem: any) => {
        const row: Record<string, any> = {};

        config.childControls?.forEach((control: any) => {
          const fieldName = control.name || control.BindValue;
          const jsonAttribute = control.Json_Attribute;

          if (fieldName && jsonAttribute) {
            // Try to get value from lookup item using Json_Attribute
            let value: any =
              lookupItem[jsonAttribute] ??
              lookupItem[jsonAttribute.toLowerCase()] ??
              lookupItem[jsonAttribute.toUpperCase()] ??
              lookupItem[jsonAttribute.replace(/_/g, "")];

            if (value === undefined || value === null) {
              value =
                lookupItem[fieldName] ??
                lookupItem[fieldName.toLowerCase()] ??
                lookupItem[fieldName.toUpperCase()] ??
                "";
            }

            value = applySubModuleDropdownValueFromLookupRow(
              control,
              jsonAttribute,
              lookupItem,
              value
            );

            // Apply default value if no value found
            if (
              (value === undefined || value === null || value === "") &&
              control.Default_Value !== undefined
            ) {
              value = control.Default_Value;
            }

            row[fieldName] = value;
            if (jsonAttribute && jsonAttribute !== fieldName) {
              row[jsonAttribute] = value;
            }
          } else if (fieldName) {
            row[fieldName] =
              lookupItem[fieldName] ||
              lookupItem[fieldName.toLowerCase()] ||
              control.Default_Value ||
              "";
          }
        });

        copyLookupMetadataToRow(row, lookupItem);
        const normalized = normalizeSubModuleRowForDisplay(row);
        return PARTY_LEDGER_SUBMODULE_LOOKUPS.has(lookupName || "")
          ? enrichPartyLedgerSubmoduleRow(normalized, lookupItem)
          : isPurchaseReturnSubmoduleLookup(lookupName)
            ? applyPurchaseReturnInvoiceTemplateRow(normalized, lookupItem)
            : normalized;
      });

      const applyPopulatedRows = async () => {
        let rowsWithExpressions = transformedRows.map((row: any, index: number) =>
          evaluateAllExpressionsForRow(row, index)
        );

        if (isSalesReturnInvoiceSubmoduleLookup(lookupName)) {
          const saved = getSavedSalesReturnRows();
          if (saved.length > 0) {
            rowsWithExpressions = mergeSavedSalesReturnLines(
              rowsWithExpressions,
              saved
            );
          } else if (hasPersistedSubModuleRows(rowsWithExpressions)) {
            rememberSavedSalesReturnRows(rowsWithExpressions);
          }
        }

        if (isCreditDebitNoteSplitSubmoduleLookup(lookupName)) {
          const saved = getSavedCreditDebitNoteSplitRows();
          if (saved.length > 0) {
            rowsWithExpressions = mergeSavedCreditDebitNoteSplitLines(
              rowsWithExpressions,
              saved
            );
          } else if (hasCreditDebitNoteSplitRows(rowsWithExpressions, formData?.id)) {
            rememberSavedCreditDebitNoteSplitRows(rowsWithExpressions);
          }
        }

        if (lookupName === "invoice_items") {
          rowsWithExpressions = await enrichInvoiceSpareRowsWithDiscounts(
            rowsWithExpressions,
            parentValue
          );
          rowsWithExpressions = rowsWithExpressions.map((row: any, index: number) =>
            evaluateAllExpressionsForRow(row, index)
          );
        }

        if (
          isBillWiseReleaseInvoiceSubmodule(config as any) &&
          ((config as any).billWiseReleaseInvoiceMode ||
            (config as any).vendorBillWiseReleaseInvoiceMode)
        ) {
          rowsWithExpressions = recalcBillWiseReleaseSubmoduleByConfig(
            config as any,
            rowsWithExpressions
          );
          rowsWithExpressions = rowsWithExpressions.map((row, idx) =>
            evaluateAllExpressionsForRow(row, idx)
          );
        }

        lastRawDataPopulateKeyRef.current = `${parentValue}::${JSON.stringify(lookupData)}`;
        lastLookupDataRef.current = lookupData;
        lastProcessedParentValueRef.current = parentValue;
        lastPopulatedDataRef.current = JSON.stringify(rowsWithExpressions);
        isUpdatingFromInternalRef.current = true;
        setData(rowsWithExpressions);
        setShowTable(rowsWithExpressions.length > 0);
        publishSubmoduleRows(rowsWithExpressions);
        if (PARTY_LEDGER_SUBMODULE_LOOKUPS.has(lookupName || "")) {
          rowsWithExpressions.forEach((row) =>
            seedPartyLedgerDropdownOption(dispatch, config.childControls, row)
          );
        }
      };

      void applyPopulatedRows();

      // Mark that data was loaded so footer calculation can trigger
      dataLoadedRef.current = true;
    } else if (
      !isLoading &&
      lookupData.length === 0 &&
      parentValue &&
      PARTY_LEDGER_SUBMODULE_LOOKUPS.has(lookupName || "") &&
      requestedLookupsRef.current.has(`submodule_${lookupKey}`)
    ) {
      const fallbackKey = `party_ledger_fallback_${lookupName}_${parentValue}`;
      if (lastPopulatedDataRef.current !== fallbackKey) {
        const fallbackRow = buildPartyLedgerFallbackRow(
          String(parentValue),
          formData,
          lookupName || ""
        );
        const rowsWithExpressions = [
          evaluateAllExpressionsForRow(fallbackRow, 0),
        ];
        lastPopulatedDataRef.current = fallbackKey;
        lastRawDataPopulateKeyRef.current = fallbackKey;
        lastProcessedParentValueRef.current = parentValue;
        isUpdatingFromInternalRef.current = true;
        setData(rowsWithExpressions);
        setShowTable(true);
        publishSubmoduleRows(rowsWithExpressions);
        seedPartyLedgerDropdownOption(dispatch, config.childControls, fallbackRow);
      }
    }
  }, [
    // Watch for changes to rawData for this specific lookup
    lookupsState.rawData[`${configAny.LookupName || configAny.lookupName || config.lookupName}_${parentFieldValueString}`],
    lookupsState.loading[`${configAny.LookupName || configAny.lookupName || config.lookupName}_${parentFieldValueString}`],
    configAny.LookupName || configAny.lookupName || config.lookupName,
    actualParentField,
    parentFieldValueString,
    config.name,
    value,
    data,
    formData,
  ]);

  const containerStyles: React.CSSProperties = {
    backgroundColor: config.style?.backgroundColor || "transparent",
    padding: config.style?.padding || 0,
    borderRadius: config.style?.borderRadius || 8,
    marginBottom: config.style?.margin || 5,
    overflowX: "auto",
  };

  const renderField = (control: any, rowIndex: number, fieldName: string) => {
    // Try to get value using fieldName first, then try Json_Attribute as fallback
    const jsonAttribute = control.Json_Attribute;
    const row = data[rowIndex] || {};
    let fieldValue = row[fieldName];

    // If value not found by fieldName and Json_Attribute exists, try using Json_Attribute
    // CRITICAL: This is essential for edit mode where backend returns data with Json_Attribute field names
    // Numeric 0 is a valid value (e.g. return_qty) — do not treat it as empty
    if (subModuleCellValueEmpty(fieldValue) && jsonAttribute) {
      fieldValue = row[jsonAttribute];

      // Also try case-insensitive and underscore variations for robustness
      if (subModuleCellValueEmpty(fieldValue) && row) {
        const possibleKeys = [
          jsonAttribute.toLowerCase(),
          jsonAttribute.toUpperCase(),
          jsonAttribute.replace(/_/g, ""),
          fieldName.toLowerCase(),
          fieldName.toUpperCase(),
        ];
        for (const key of possibleKeys) {
          if (!subModuleCellValueEmpty(row[key])) {
            fieldValue = row[key];

            break;
          }
        }
      }
    }

    // Default to empty string if still no value
    if (fieldValue === undefined || fieldValue === null) {
      fieldValue = "";
    }

    // CRITICAL: For dropdowns, ensure value is properly formatted (string for matching)
    const isDropdown =
      control.componentType === ComponentType.SELECT_DROPDOWN ||
      control.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
      control.componentType === "selectDropdown" ||
      control.componentType === "searchableDropdown";

    if (isDropdown) {
      // Convert to string for consistent matching with option values
      if (fieldValue !== "" && fieldValue !== undefined && fieldValue !== null) {
        fieldValue = String(fieldValue);
      }

      // CRITICAL: Debug logging for dropdown value extraction - always log for dropdowns
      const lookupName = control.LookupName || control.lookupName;

    }
    // Handle both old format (PlaceHolder) and new format (placeholder)
    const placeholder = control.placeholder || control.PlaceHolder || "";
    const inputType = control.inputType || InputType.TEXT;
    // Allow editing unless explicitly disabled or locked by EnableEditForControl in edit mode
    const isReadOnly =
      control.IsReadOnly ||
      control.disabled ||
      config.disabled ||
      isSubModuleControlLockedInEditMode(control, row, isEditMode);

    // Determine if the field is mandatory
    const isStaticRequired = control.Required === true || control.IsMandatory === true || control.required === true;
    let isDynamicRequired = false;
    if (!isStaticRequired && control.validation && Array.isArray(control.validation)) {
      for (const rule of control.validation) {
        if (rule.rule === ValidationRule.CONDITIONAL_REQUIRED && rule.whenField) {
          if (isConditionallyRequired(rule, row, config.childControls)) {
            isDynamicRequired = true;
            break;
          }
        }
      }
    }
    const isMandatory = isStaticRequired || isDynamicRequired;

    // Handle field value change
    // SelectDropdown and SearchableDropdown call onChange with (name, value)
    // TextField and other components may call it with just (value)
    const handleFieldChange = (nameOrValue: any, value?: any) => {
      // If two parameters are provided, the first is name and second is value
      // If one parameter is provided, it's the value
      let actualValue = value !== undefined ? value : nameOrValue;

      // CRITICAL FIX: Ensure the field name is correctly identified
      // When SearchableDropdown/SelectDropdown calls onChange(config.name, optionValue),
      // nameOrValue is the field name and value is the selected value
      // But we should use the fieldName from the closure to ensure consistency
      const actualFieldName =
        value !== undefined && typeof nameOrValue === "string"
          ? nameOrValue
          : fieldName;

      // For dropdowns, also store the full selected option (like old framework's SelectedJson.Data)
      // This allows ExpressionV2 to access properties like purchase_rate directly from rowData
      if (
        control.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
        control.componentType === ComponentType.SELECT_DROPDOWN ||
        control.componentType === "searchableDropdown" ||
        control.componentType === "selectDropdown"
      ) {
        const lookupName = control.LookupName || control.lookupName;
        if (lookupName && actualValue) {
          const lookupKey = childControlLookupKey(lookupName, control, formData);

          // Find the selected option from Redux lookups (for label/value)
          let lookupOptions = lookupsState.lookups[lookupKey] || [];
          if (lookupOptions.length === 0 && lookupKey !== lookupName) {
            lookupOptions = lookupsState.lookups[lookupName] || [];
          }
          let selectedOption = lookupOptions.find(
            (opt: any) =>
              opt.value === actualValue ||
              String(opt.value) === String(actualValue) ||
              opt.id === actualValue ||
              String(opt.id) === String(actualValue)
          );

          // CRITICAL: If not found by value/id, check if actualValue is a label (like "5%")
          // This handles cases where default value is set as label instead of ID
          if (!selectedOption && typeof actualValue === "string") {
            selectedOption = lookupOptions.find(
              (opt: any) =>
                opt.label === actualValue ||
                String(opt.label) === String(actualValue) ||
                (opt as any).name === actualValue ||
                String((opt as any).name) === String(actualValue)
            );

            // If found by label, convert to ID
            if (selectedOption) {
              const idValue = selectedOption.value || (selectedOption as any).id || actualValue;

              if (idValue !== actualValue) {

                // Update actualValue to use the ID
                actualValue = idValue;
              }
            }
          }

          // CRITICAL FIX: Use rawData to get ALL properties (including purchase_rate)
          // The transformed lookup options only have label/value/disabled
          // IMPORTANT: Don't initialize from selectedOption - only set if we find rawItem
          const rawData =
            (lookupsState.rawData[lookupKey]?.length
              ? lookupsState.rawData[lookupKey]
              : lookupKey !== lookupName
              ? lookupsState.rawData[lookupName]
              : []) || [];
          let fullSelectedOption: any = null; // Only set if rawItem is found

          if (rawData.length > 0) {
            // Find the matching item in rawData (has all original properties from API)
            const rawItem = rawData.find(
              (item: any) =>
                item.id === actualValue ||
                String(item.id) === String(actualValue) ||
                item.value === actualValue ||
                String(item.value) === String(actualValue) ||
                item.codeid === actualValue ||
                String(item.codeid) === String(actualValue) ||
                (item.product_id &&
                  String(item.product_id) === String(actualValue))
            );

            if (rawItem) {
              // Merge: Use rawItem (has all properties) and override with selectedOption (has correct label/value)
              fullSelectedOption = {
                ...rawItem, // All original properties from API (including purchase_rate, sale_rate, etc.)
                ...(selectedOption || {}), // Override with transformed label/value if available
              };
            }
          }

          // Store the full selected option in row data (like old framework's SelectedJson.Data)
          // Use a special field name: _${fieldName}_selectedOption
          // CRITICAL: Only store if we have fullSelectedOption from rawData (has all properties)
          // Don't store if it only has label/value/disabled (which means rawData wasn't available)
          const selectedOptionKey = `_${actualFieldName}_selectedOption`;
          if (
            fullSelectedOption &&
            Object.keys(fullSelectedOption).length > 3
          ) {
            // Only store if it has more than just label/value/disabled (has properties from rawData)
            // Store the selected option in a temporary location so updateRowField can access it
            // updateRowField will handle storing it in the row data
            (control as any)._tempSelectedOption = fullSelectedOption;
            (control as any)._tempSelectedOptionKey = selectedOptionKey;
          }
          // If fullSelectedOption doesn't have properties, fallback in updateRowField will handle it
        }
      }

      // CRITICAL FIX: Log the update for debugging


      updateRowField(rowIndex, actualFieldName, actualValue, control);
    };

    // Normalize control config for field components
    // Don't render labels in field components since they're already in table headers
    const normalizedControl = {
      ...control,
      name: fieldName,
      label: "", // Empty label to prevent field components from rendering their own labels
      placeholder: placeholder,
      required: control.IsMandatory || control.required || false,
      disabled: isReadOnly,
      defaultValue: control.Default_Value || control.defaultValue || "",
      displayFallback: (() => {
        const descFromRow =
          row[`${fieldName}_desc`] ||
          (jsonAttribute ? row[`${jsonAttribute}_desc`] : "") ||
          (jsonAttribute === "ledger_id"
            ? row.bank_display || row.bank_name
            : "") ||
          "";
        if (descFromRow) return String(descFromRow);
        if (
          jsonAttribute === "to_ledger" &&
          (row.txn_type_label ||
            row.pdc_transaction_type ||
            (formData as any)?.txt_pdc_transaction_type)
        ) {
          return String(
            row.txn_type_label ||
              row.pdc_transaction_type ||
              (formData as any)?.txt_pdc_transaction_type ||
              ""
          );
        }
        return "";
      })(),
    };

    switch (control.componentType) {
      case ComponentType.SEARCHABLE_DROPDOWN:
      case "searchableDropdown": {
        const lookupName = control.LookupName || control.lookupName;
        const parentField = control.Parent_Field || control.parentField;
        let options = control.options || [];

        if (lookupName) {
          let lookupKey = resolveControlLookupKey(lookupName, control, formData);

          const lookupData = lookupsState.lookups[lookupKey];
          options = Array.isArray(lookupData) ? lookupData : [];

          if (fieldValue && fieldValue !== "" && options.length === 0) {
            const configParentField = config.parentField;
            if (configParentField && !parentField) {
              const configParentValue = formData[configParentField];
              if (configParentValue !== undefined && configParentValue !== null && configParentValue !== "") {
                const fallbackLookupKey = resolveControlLookupKey(
                  lookupName,
                  { ...control, parentField: configParentField },
                  formData
                );
                const fallbackOptions = lookupsState.lookups[fallbackLookupKey] || [];
                if (fallbackOptions.length > 0) {
                  options = fallbackOptions;
                  lookupKey = fallbackLookupKey;
                }
              }
            }
          }
        }

        const safeOptions = applyOptionLock(
          Array.isArray(options) ? options : [],
          getOptionLockKey(control)
        );

        // CRITICAL: Normalize fieldValue for dropdown matching
        // Convert to string if it's a number, and ensure it matches option value format
        let normalizedFieldValue = fieldValue;
        if (normalizedFieldValue !== undefined && normalizedFieldValue !== null && normalizedFieldValue !== "") {
          // Convert to string for consistent matching with option values
          normalizedFieldValue = String(normalizedFieldValue);
        } else {
          normalizedFieldValue = undefined;
        }

        // Handle refresh for Search_Select lookups when dropdown is opened / searched
        const accountMasterKey = getAccountMasterKeyFromConfig(control);
        const handleDropdownFocus = () => {
          if (!lookupName) return;
          const parentValueForKey = parentField
            ? formData[parentField]
            : undefined;
          if (
            parentField &&
            (parentValueForKey === undefined ||
              parentValueForKey === null ||
              parentValueForKey === "")
          ) {
            return;
          }
          const lookupKey = resolveControlLookupKey(
            lookupName,
            control,
            formData
          );
          const searchExtras = {
            search_arg: "",
            ...(normalizedFieldValue
              ? { selectedid: String(normalizedFieldValue) }
              : {}),
          };
          if (parentField && parentValueForKey != null && parentValueForKey !== "") {
            dispatch(
              fetchDependentLookup({
                lookupName,
                parentValue: parentValueForKey as string | number,
                lookuptype: "Search_Select",
                ...(accountMasterKey ? { accountMasterKey } : {}),
                ...searchExtras,
              })
            );
          } else {
            dispatch(
              fetchLookup({
                lookupName,
                params: {
                  parentid: "",
                  lookuptype: "Search_Select",
                  ...(accountMasterKey ? { accountMasterKey } : {}),
                  ...searchExtras,
                },
              })
            );
          }
        };

        const handleDropdownSearch = (searchTerm: string) => {
          if (!lookupName) return;
          const parentValueForKey = parentField
            ? formData[parentField]
            : undefined;
          if (
            parentField &&
            (parentValueForKey === undefined ||
              parentValueForKey === null ||
              parentValueForKey === "")
          ) {
            return;
          }
          const debounceKey = resolveControlLookupKey(
            lookupName,
            control,
            formData
          );
          const existingTimer = searchableSearchDebounceRef.current[debounceKey];
          if (existingTimer) clearTimeout(existingTimer);
          searchableSearchDebounceRef.current[debounceKey] = setTimeout(() => {
            const searchExtras = {
              search_arg: searchTerm || "",
              ...(normalizedFieldValue
                ? { selectedid: String(normalizedFieldValue) }
                : {}),
            };
            if (parentField && parentValueForKey != null && parentValueForKey !== "") {
              dispatch(
                fetchDependentLookup({
                  lookupName,
                  parentValue: parentValueForKey as string | number,
                  lookuptype: "Search_Select",
                  ...(accountMasterKey ? { accountMasterKey } : {}),
                  ...searchExtras,
                })
              );
            } else {
              dispatch(
                fetchLookup({
                  lookupName,
                  params: {
                    parentid: "",
                    lookuptype: "Search_Select",
                    ...(accountMasterKey ? { accountMasterKey } : {}),
                    ...searchExtras,
                  },
                })
              );
            }
          }, 300);
        };

        const dropdownInstanceId = `${config.name || "sub"}_${rowIndex}_${fieldName}`;

        return (
          <SearchableDropdown
            key={dropdownInstanceId}
            config={{
              ...normalizedControl,
              componentType: ComponentType.SEARCHABLE_DROPDOWN,
              options: safeOptions,
              lookupName: lookupName,
              parentField: parentField,
              required: isMandatory,
              isSubModule: true,
              dropdownInstanceId,
            }}
            value={normalizedFieldValue}
            onChange={handleFieldChange}
            onFocus={handleDropdownFocus}
            onSearch={handleDropdownSearch}
          />
        );
      }

      case ComponentType.SELECT_DROPDOWN:
      case "selectDropdown": {
        const lookupName = control.LookupName || control.lookupName;
        const parentField = control.Parent_Field || control.parentField;
        let options = control.options || [];

        if (lookupName) {
          let lookupKey = resolveControlLookupKey(lookupName, control, formData);

          const lookupData = lookupsState.lookups[lookupKey];
          options = Array.isArray(lookupData) ? lookupData : [];

          if (fieldValue && fieldValue !== "" && options.length === 0) {
            const configParentField = config.parentField;
            if (configParentField && !parentField) {
              const configParentValue = formData[configParentField];
              if (configParentValue !== undefined && configParentValue !== null && configParentValue !== "") {
                const fallbackLookupKey = resolveControlLookupKey(
                  lookupName,
                  { ...control, parentField: configParentField },
                  formData
                );
                const fallbackOptions = lookupsState.lookups[fallbackLookupKey] || [];
                if (fallbackOptions.length > 0) {
                  options = fallbackOptions;
                  lookupKey = fallbackLookupKey;
                }
              }
            }
          }
        }

        // CRITICAL: Normalize fieldValue for dropdown matching
        // Convert to string if it's a number, and ensure it matches option value format
        let normalizedValue: string | number | boolean | undefined = fieldValue;
        if (normalizedValue !== undefined && normalizedValue !== null && normalizedValue !== "") {
          // Convert to string for consistent matching with option values
          normalizedValue = String(normalizedValue);
        } else {
          normalizedValue = undefined;
        }

        // Ensure options is always an array
        const safeOptions = Array.isArray(options) ? options : [];

        // Debug logging for edit mode dropdown values


        // Handle refresh for SelectDropdown in SubModule
        // Note: The SelectDropdown component itself handles refresh for independent lookups
        // This handler is for dependent lookups
        const handleSelectDropdownFocus = () => {
          // Same as SearchableDropdown in submodule — avoid global lookup refresh on focus.
        };

        return (
          <SelectDropdown
            config={{
              ...normalizedControl,
              componentType: ComponentType.SELECT_DROPDOWN,
              options: safeOptions,
              lookupName: lookupName,
              parentField: parentField,
              multiple: control.multiple || false,
              required: isMandatory,
              isSubModule: true,
            }}
            value={normalizedValue}
            onChange={handleFieldChange}
            onFocus={handleSelectDropdownFocus}
          />
        );
      }

      case ComponentType.DATE_FIELD:
      case "dateField":
        return (
          <DateField
            config={{
              ...normalizedControl,
              componentType: ComponentType.DATE_FIELD,
              required: isMandatory,
              isSubModule: true,
            }}
            value={fieldValue}
            onChange={handleFieldChange}
          />
        );

      case ComponentType.TEXT_AREA:
      case "textArea":
        return (
          <AutoResizeTextArea
            className="w-full px-3 py-2 border border-gray-400 dark:border-gray-600 rounded-md text-sm outline-none transition-colors duration-200 resize-none overflow-hidden focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            style={{
              minHeight: "100px",
              width: "100%",
              maxWidth: "100%",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "break-word"
            }}
            value={fieldValue}
            onChange={(e) => handleFieldChange(e.target.value)}
            placeholder={placeholder}
            disabled={isReadOnly}
            rows={control.rows || 4}
          />
        );

      case ComponentType.BATCH_ALLOCATION:
      case "batchAllocation": {
        const handleBatchAllocChange = (
          allocFieldName: string,
          allocValue: any,
          rateValue?: number
        ) => {
          const newData = [...data];
          if (!newData[rowIndex]) newData[rowIndex] = {};

          newData[rowIndex] = {
            ...newData[rowIndex],
            [allocFieldName]: allocValue,
            ...(newData[rowIndex].id || newData[rowIndex].itemid
              ? { act: "edit" }
              : {}),
          };

          const jsonAttr = (control as any)?.Json_Attribute;
          if (jsonAttr && typeof jsonAttr === "string") {
            newData[rowIndex][jsonAttr] = allocValue;
          }

          if (rateValue !== undefined && (control as any).rateField) {
            const rf = (control as any).rateField as string;
            newData[rowIndex][rf] = rateValue;
            const rateCtrl = config.childControls?.find(
              (c: any) => (c.name || c.BindValue) === rf
            );
            const rateJsonAttr = (rateCtrl as any)?.Json_Attribute;
            if (rateJsonAttr && typeof rateJsonAttr === "string") {
              newData[rowIndex][rateJsonAttr] = rateValue;
            }
          }

          handleDataChange(newData);
        };

        return (
          <BatchAllocationField
            config={control}
            value={fieldValue}
            onChange={handleBatchAllocChange}
            rowData={data[rowIndex] || {}}
            rowIndex={rowIndex}
            formData={formData}
            isReadOnly={isReadOnly}
          />
        );
      }

      case ComponentType.TEXT_FIELD:
      case "textField":
      default: {
        // Determine input type
        const htmlInputType =
          inputType === InputType.NUMBER
            ? "number"
            : inputType === InputType.EMAIL
              ? "email"
              : inputType === InputType.PASSWORD
                ? "password"
                : inputType === InputType.PHONE
                  ? "tel"
                  : inputType === InputType.URL
                    ? "url"
                    : "text";

        if (isReadOnly) {
          return (
            <input
              type={htmlInputType}
              className="w-full h-9 px-3 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-100 cursor-not-allowed"
              value={fieldValue}
              readOnly
              placeholder={placeholder}
            />
          );
        }

        // CRITICAL FIX: Ensure TextField in SubModule has proper width when rendered in table cells
        // Override TextField's wrapper to ensure full width in table cells
        const isEmpty = fieldValue === undefined || fieldValue === null || String(fieldValue).trim() === "";
        const showMandatoryGlow = isMandatory && !isReadOnly && isEmpty;
        const mandatoryClass = showMandatoryGlow 
          ? "border-danger-500 dark:border-danger-400 ring-2 ring-danger-500/40 dark:ring-danger-400/40 focus:border-danger-600 dark:focus:border-danger-500 focus-visible:ring-danger-600 dark:focus-visible:ring-danger-500" 
          : "border-gray-300 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-400 focus-visible:ring-primary-500";
        const allowNegative = allowsNegativeNumericInput(control);
        const numericMin =
          htmlInputType === "number" ? resolveNumericInputMin(control) : undefined;
        const numericMax =
          htmlInputType === "number" ? resolveNumericInputMax(control) : undefined;
        const handleNumericInputChange = (rawValue: string) => {
          if (htmlInputType === "number") {
            if (allowNegative) {
              if (rawValue !== "" && !/^-?\d*\.?\d*$/.test(rawValue)) return;
            } else if (rawValue.includes("-")) {
              return;
            }
            if (
              rawValue !== "" &&
              rawValue !== "." &&
              rawValue !== "-" &&
              rawValue !== "-."
            ) {
              const num = parseFloat(rawValue);
              if (!isNaN(num)) {
                if (numericMin !== undefined && num < numericMin) return;
                if (numericMax !== undefined && num > numericMax) return;
              }
            }
          }
          handleFieldChange(rawValue);
        };
        const handleNumericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (htmlInputType !== "number") return;
          if (e.key === "e" || e.key === "E") {
            e.preventDefault();
            return;
          }
          if (e.key === "-" && !allowNegative) {
            if (numericMin === undefined || numericMin >= 0) {
              e.preventDefault();
            }
          }
        };
        return (
          <div className="w-full min-w-0" style={{ minWidth: '80px' }}>
            <div className="w-full px-0">
              <input
                type={htmlInputType}
                className={`w-full h-9 px-3 border rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus-visible:ring-2 focus-visible:outline-none transition-colors duration-200 ${mandatoryClass}`}
                value={fieldValue}
                onChange={(e) => handleNumericInputChange(e.target.value)}
                onKeyDown={handleNumericKeyDown}
                placeholder={placeholder}
                disabled={isReadOnly}
                min={numericMin}
                max={numericMax}
                style={{ minWidth: '80px' }}
              />
            </div>
          </div>
        );
      }
    }
  };

  // Calculate SUM expression for footer controls
  // Always use local data state as primary source — it reflects the latest user edits
  // (including deletions) immediately, whereas the value prop can be stale.
  // Only fall back to value prop if local data is genuinely absent (null/undefined).
  const calculateSum = (fieldName: string): number => {
    // CRITICAL FIX: Always prefer local `data` regardless of length so that
    // deletions (which reduce data.length to 0 or leave only soft-deleted rows)
    // are reflected immediately instead of falling back to a stale `value` prop.
    const dataSource = data !== null && data !== undefined ? data : (value || []);
    if (!dataSource || dataSource.length === 0) {
      return 0;
    }

    // Find the Json_Attribute for this BindValue in childControls
    // In edit mode, backend returns data with Json_Attribute field names
    // Example: BindValue "txt_amount" → Json_Attribute "amount"
    let jsonAttribute: string | undefined;
    if (config.childControls && Array.isArray(config.childControls)) {
      const control = config.childControls.find((ctrl: any) =>
        (ctrl.BindValue || ctrl.name) === fieldName
      );
      jsonAttribute = control?.Json_Attribute || (control as any)?.json_Attribute;


    }

    let totalSum = 0;
    const rowValues: number[] = [];

    const sumResult = dataSource.reduce((sum, row, index) => {
      // CRITICAL FIX: Skip soft-deleted rows from sum calculation
      if (row.soft_delete === 1 || row.soft_delete === "1") {
        return sum;
      }

      // Try BindValue first (for add mode where user enters data)
      let fieldValue = row[fieldName];

      // If not found and Json_Attribute exists, try Json_Attribute (for edit mode backend data)
      // This handles the case where backend returns "amount" but expression uses "txt_amount"
      if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && jsonAttribute) {
        fieldValue = row[jsonAttribute];


      }

      // Also try case-insensitive and underscore variations if still not found
      if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && jsonAttribute) {
        const variations = [
          jsonAttribute.toLowerCase(),
          jsonAttribute.toUpperCase(),
          jsonAttribute.replace(/_/g, ""),
          fieldName.toLowerCase(),
          fieldName.toUpperCase(),
        ];

        for (const variation of variations) {
          if (row[variation] !== undefined && row[variation] !== null && row[variation] !== "") {
            fieldValue = row[variation];

            break;
          }
        }
      }

      // Handle both string and number values
      const numValue =
        fieldValue !== undefined && fieldValue !== null && fieldValue !== ""
          ? parseFloat(String(fieldValue)) || 0
          : 0;

      rowValues.push(numValue);
      return sum + numValue;
    }, 0);

    totalSum = sumResult;



    return totalSum;
  };

  // Resolve a footer/form field reference for expression evaluation
  const resolveFooterFormFieldValue = (fieldRef: string): number => {
    for (const footerControl of config.FooterControls || []) {
      const fc = footerControl as {
        BindValue?: string;
        name?: string;
        Json_Attribute?: string;
        json_Attribute?: string;
      };
      const bindValue = fc.BindValue || fc.name;
      const jsonAttribute = fc.Json_Attribute || fc.json_Attribute;
      if (bindValue === fieldRef || jsonAttribute === fieldRef) {
        const raw =
          (jsonAttribute ? formData[jsonAttribute] : undefined) ??
          (bindValue ? formData[bindValue] : undefined);
        if (raw !== undefined && raw !== null && raw !== "") {
          return parseFloat(String(raw)) || 0;
        }
        return 0;
      }
    }
    const raw = formData[fieldRef];
    if (raw !== undefined && raw !== null && raw !== "") {
      return parseFloat(String(raw)) || 0;
    }
    return 0;
  };

  // Evaluate footer control expression (SUM, arithmetic, and parent form field refs)
  const evaluateFooterExpression = (expression: string): number => {
    if (!expression || typeof expression !== "string") return 0;

    let expr = expression;

    // Replace [SUM:fieldName] aggregates first
    expr = expr.replace(/\[SUM:(\w+)\]/g, (_match, fieldName: string) =>
      String(calculateSum(fieldName))
    );

    // Replace remaining [fieldName] references from parent form data
    expr = expr.replace(/\[(\w+)\]/g, (match, fieldRef: string) =>
      String(resolveFooterFormFieldValue(fieldRef))
    );

    try {
      const result = Function(`"use strict"; return (${expr})`)();
      if (typeof result === "number" && !isNaN(result)) {
        return result;
      }
    } catch {
      // Fall through to 0
    }

    return 0;
  };

  // Track when data is loaded in edit mode to force footer recalculation
  const dataLoadedRef = useRef(false);
  const previousDataLengthRef = useRef(0);
  const previousValueStringRef = useRef<string>("");

  // Detect when edit mode data is loaded
  useEffect(() => {
    const currentDataLength = (value && value.length > 0 ? value : data).length;
    const currentValueString = value ? JSON.stringify(value) : "";
    const hadNoData = previousDataLengthRef.current === 0;
    const hasDataNow = currentDataLength > 0;
    const valueChanged = currentValueString !== previousValueStringRef.current;

    // CRITICAL: Set dataLoadedRef when:
    // 1. Data goes from empty to having data (edit mode load)
    // 2. Value prop changes and has data (edit mode data update)
    if ((hadNoData && hasDataNow) || (valueChanged && hasDataNow)) {
      // Data was just loaded (edit mode)
      dataLoadedRef.current = true;

    }

    previousDataLengthRef.current = currentDataLength;
    previousValueStringRef.current = currentValueString;
  }, [data, value, config.name]);

  // Calculate footer values and update parent form
  // CRITICAL: This should work for ALL SubModules, regardless of whether they have lookupName/parentField
  useEffect(() => {
    if (!config.FooterControls || !onFormDataChangeRef.current) return;

    // CRITICAL FIX: Always prefer local `data` so that row deletions (both hard and soft)
    // are reflected immediately.  The `value` prop is only used when `data` is genuinely
    // absent (null/undefined), NOT when it is simply empty (length === 0).
    // Removing the "length === 0" early-return is intentional: when all rows are deleted
    // we must still fire the footer update so parent aggregate fields are zeroed out.
    const dataSource = data !== null && data !== undefined ? data : (value || []);
    // NOTE: We intentionally do NOT return early when dataSource.length === 0 here.
    // calculateSum will return 0 for each field, which correctly zeroes out the parent totals.

    // CRITICAL: For SubModules with lookupName/parentField, ensure we wait for ExpressionV2 to be evaluated
    // Check if any row has calculated fields (ExpressionV2 results) - if not, wait a bit longer
    const hasCalculatedFields = dataSource.some((row: any) => {
      // Check if row has fields that should be calculated (like amount, vat_amt, net_amt)
      return row.txt_amount !== undefined || row.amount !== undefined ||
        row.txt_vat_amt !== undefined || row.vat_amt !== undefined ||
        row.txt_net_amt !== undefined || row.net_amt !== undefined;
    });

    // Use a longer delay if:
    // 1. Data was just loaded (edit mode)
    // 2. SubModule has lookupName/parentField (might need more time for ExpressionV2)
    // 3. Calculated fields are not yet present
    const hasLookupConfig = !!(config as any).lookupName || !!(config as any).LookupName;
    const needsMoreTime = dataLoadedRef.current || hasLookupConfig || !hasCalculatedFields;
    const delay = needsMoreTime ? 500 : 100; // Increased delay for SubModules with lookupName/parentField



    const timeoutId = setTimeout(() => {


      config.FooterControls!.forEach((footerControl: any) => {
        const expression = footerControl.ExpressionV2;
        const jsonAttribute = footerControl.Json_Attribute;

        if (expression && jsonAttribute) {
          const calculatedValue = evaluateFooterExpression(expression);
          // Format using GLOBAL_DECIMAL_PLACES
          const formattedValue = roundNumericDecimal(calculatedValue);

          // Always update in edit mode when data is first loaded, otherwise check if changed
          const previousValue = previousFooterValuesRef.current[jsonAttribute];
          const shouldUpdate = dataLoadedRef.current || previousValue !== formattedValue;

          if (shouldUpdate) {
            previousFooterValuesRef.current[jsonAttribute] = formattedValue;
            // Update parent form data using Json_Attribute
            onFormDataChangeRef.current?.(jsonAttribute, formattedValue);

            // Debug logging

          }
        }
      });

      // Reset flag after first calculation
      if (dataLoadedRef.current) {
        dataLoadedRef.current = false;
      }
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [data, value, config.FooterControls, config.name, formData]);

  // Render footer control field
  const renderFooterField = (footerControl: any, index: number) => {
    const expression = footerControl.ExpressionV2;
    const bindValue = footerControl.BindValue;
    const jsonAttribute =
      footerControl.Json_Attribute || footerControl.json_Attribute;
    const label = footerControl.Label || footerControl.label || "";
    const isReadOnly = footerControl.IsReadOnly !== false;
    const isHidden = footerControl.style?.display === "none";
    const isVerticalView = footerControl.IsVerticalView === true;
    const inputType = footerControl.inputType || InputType.NUMBER;
    const defaultValue =
      footerControl.Default_Value || footerControl.defaultValue || "0";

    let fieldValue = defaultValue;
    if (expression && isReadOnly) {
      const calculatedValue = evaluateFooterExpression(expression);
      fieldValue = formatNumericDecimal(calculatedValue);
      fieldValue = parseFloat(fieldValue).toString();
    } else if (!isReadOnly) {
      const raw =
        (jsonAttribute ? formData[jsonAttribute] : undefined) ??
        (bindValue ? formData[bindValue] : undefined);
      fieldValue =
        raw !== undefined && raw !== null && raw !== ""
          ? String(raw)
          : defaultValue;
    }

    const allowNegative = allowsNegativeNumericInput(footerControl);
    const isNumericFooter =
      inputType === InputType.NUMBER || footerControl.inputType === "number";
    const numericMin = isNumericFooter
      ? resolveNumericInputMin(footerControl)
      : undefined;
    const numericMax = isNumericFooter
      ? resolveNumericInputMax(footerControl)
      : undefined;

    const handleFooterInputChange = (rawInput: string) => {
      if (isReadOnly || !onFormDataChangeRef.current) return;
      if (isNumericFooter) {
        if (allowNegative) {
          if (rawInput !== "" && !/^-?\d*\.?\d*$/.test(rawInput)) return;
        } else if (rawInput.includes("-")) {
          return;
        }
        if (
          rawInput !== "" &&
          rawInput !== "." &&
          rawInput !== "-" &&
          rawInput !== "-."
        ) {
          const num = parseFloat(rawInput);
          if (!isNaN(num)) {
            if (numericMin !== undefined && num < numericMin) return;
            if (numericMax !== undefined && num > numericMax) return;
          }
        }
      }
      const updateKey = jsonAttribute || bindValue;
      if (updateKey) {
        onFormDataChangeRef.current(updateKey, rawInput);
      }
    };

    const handleFooterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isNumericFooter) return;
      if (e.key === "e" || e.key === "E") {
        e.preventDefault();
        return;
      }
      if (e.key === "-" && !allowNegative) {
        if (numericMin === undefined || numericMin >= 0) {
          e.preventDefault();
        }
      }
    };

    const readOnlyInputClass =
      "w-full min-h-[36px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-100 font-semibold cursor-not-allowed";
    const editableInputClass =
      "w-full min-h-[36px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500";

    // If hidden, return empty cell
    if (isHidden) {
      return <td key={index} className="p-3 border-b border-gray-200"></td>;
    }

    const footerCellStyle = getSubModuleColumnCellStyle(footerControl);

    const htmlInputType =
      isNumericFooter && allowNegative ? "text" : isNumericFooter ? "number" : "text";
    const inputMode =
      isNumericFooter && allowNegative ? ("decimal" as const) : undefined;
    const inputClassName = isReadOnly ? readOnlyInputClass : editableInputClass;

    const footerInputProps = {
      type: htmlInputType,
      className: inputClassName,
      value: fieldValue,
      readOnly: isReadOnly,
      onChange: isReadOnly
        ? undefined
        : (e: React.ChangeEvent<HTMLInputElement>) =>
            handleFooterInputChange(e.target.value),
      onKeyDown: isReadOnly ? undefined : handleFooterKeyDown,
      min: isNumericFooter && !allowNegative ? numericMin : undefined,
      max: isNumericFooter ? numericMax : undefined,
      inputMode,
      style: {
        height: "auto",
        minHeight: "36px",
        overflow: "visible",
        textOverflow: "clip",
      } as React.CSSProperties,
    };

    if (isVerticalView && label) {
      // Vertical view: label above value
      return (
        <td
          key={index}
          className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
          style={footerCellStyle}
        >
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
              {label}
            </label>
            <input {...footerInputProps} />
          </div>
        </td>
      );
    } else {
      // Horizontal view or no label
      return (
        <td
          key={index}
          className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
          style={footerCellStyle}
        >
          <input {...footerInputProps} placeholder={label} />
        </td>
      );
    }
  };

  // Count active rows (not soft deleted)
  const activeRowsCount = useMemo(() => {
    return data.filter((r) => r.soft_delete !== 1 && r.soft_delete !== "1").length;
  }, [data]);

  // Filter data to remove soft_deleted rows and apply search term (if enabled)
  const filteredData = useMemo(() => {
    // First, filter out any rows marked for soft deletion
    const activeData = data.filter((row) => row.soft_delete !== 1 && row.soft_delete !== "1");

    // If search is not enabled or no search term, return active data
    if (!config.enableSearch || !searchTerm.trim()) {
      return activeData;
    }

    const searchLower = searchTerm.toLowerCase();
    return activeData.filter((row) => {
      // Search through all fields in the row
      return config.childControls?.some((control: any) => {
        const fieldName = control.name || control.BindValue;
        const jsonAttribute = control.Json_Attribute;
        const fieldValue = row[fieldName] || row[jsonAttribute] || "";
        return String(fieldValue).toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchTerm, config.childControls, config.enableSearch]);



  // Function to view report - opens side drawer (HTML) or PDF modal based on viewAction config
  const handleViewInvoice = async (row: Record<string, any>, index: number) => {
    if (!config.viewAction) return;

    const drawerReportName = config.viewAction.drawerReportName;
    const pdfSettings = config.viewAction.pdfSettings || config.viewAction.reportApi;

    if (!drawerReportName && !pdfSettings) {
      console.error("[SubModule View] No drawerReportName or pdfSettings configured");
      return;
    }

    // Get record ID from row - try multiple field names (supports invoice, purchase, etc.)
    const idField = config.viewAction.idField || "id";
    let recordId =
      row[idField] ||
      row.id ||
      row.invoice_id ||
      row.purchase_id ||
      row.invoice_no ||
      row.purchase_no ||
      row.txt_invoice_no ||
      row.txt_purchase_no ||
      "";

    // If no ID in row, try to get from parent formData (for purchase line items, etc.)
    if (!recordId && formData) {
      recordId =
        formData.id ||
        formData.purchase_id ||
        formData.invoice_id ||
        formData.purchase_no ||
        formData.invoice_no ||
        formData.txt_purchaseno ||
        "";
    }

    if (!recordId) {
      console.error("[SubModule View] No record ID found in row or formData:", { row, formDataKeys: formData ? Object.keys(formData) : [] });
      return;
    }

    // --- Side drawer (HTML report) - same as Jobcard Summary ---
    if (drawerReportName) {
      try {
        dispatch(setHTMLDrawerLoading(true));
        dispatch(openHTMLDrawer({ htmlContent: "", reportName: drawerReportName }));

        const payload = {
          ...row,
          ...formData,
          id: recordId,
          invoice_id: recordId,
          purchase_id: recordId,
          invoice_no: row.invoice_no || row.txt_invoice_no || formData?.invoice_no || recordId,
          purchase_no: row.purchase_no || row.txt_purchase_no || formData?.purchase_no || formData?.txt_purchaseno || recordId,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        const response = await apiService.request<string | {
          success?: boolean;
          data?: string;
          [key: string]: any;
        }>({
          endpoint: "/pabreport/report",
          method: "POST",
          params: { name: drawerReportName },
          payload,
          includeAuthToken: true,
          errorHandling: "throw",
          responseType: "json",
        });

        let htmlContent: string | null = null;
        if (typeof response === "string" && response.includes("<html")) {
          htmlContent = response;
        } else if (response && typeof response === "object") {
          const data = response.data ?? response.content ?? response.html ?? response.body;
          if (typeof data === "string" && data.length > 0) {
            htmlContent = data;
          }
        }
        if (htmlContent) {
          dispatch(openHTMLDrawer({ htmlContent, reportName: drawerReportName }));
        } else {
          throw new Error("No HTML content found in response");
        }
      } catch (error) {
        console.error("[SubModule View] Error opening HTML drawer:", error);
        dispatch(
          setHTMLDrawerError(
            error instanceof Error ? error.message : "Failed to load report"
          )
        );
      }
      return;
    }

    // --- PDF viewer (existing behavior) ---
    if (!pdfSettings) return;

    try {
      dispatch(setPDFViewerLoading(true));

      const encryptedUserData =
        typeof window !== "undefined"
          ? localStorage.getItem("encryptedUserData")
          : null;
      if (!encryptedUserData) {
        throw new Error("User not authenticated");
      }

      const decryptedData = authService.decryptData(encryptedUserData);
      if (!decryptedData) {
        throw new Error("Failed to decrypt user data");
      }

      const username =
        decryptedData.empName ||
        decryptedData.userName ||
        decryptedData.loginName ||
        "";
      const logined_userid =
        decryptedData.empId || decryptedData.userId || decryptedData.id || "1";

      const payload = {
        ...row,
        ...formData,
        id: recordId,
        invoice_id: recordId,
        purchase_id: recordId,
        invoice_no: row.invoice_no || row.txt_invoice_no || formData?.invoice_no || recordId,
        purchase_no: row.purchase_no || row.txt_purchase_no || formData?.purchase_no || formData?.txt_purchaseno || recordId,
        username: username,
        logined_userid: logined_userid,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const response = await apiService.request<{
        success?: boolean;
        data?: string;
        [key: string]: any;
      }>({
        endpoint: "/pabreport/pdf",
        method: "POST",
        params: { name: pdfSettings },
        payload,
        includeAuthToken: true,
        errorHandling: "throw",
        responseType: "json",
      });

      let pdfBlob: Blob | null = null;
      if (response instanceof Blob) {
        pdfBlob = response;
      } else if (response && typeof response === "object") {
        const base64Data = response.data || response.content || response.pdf;
        if (typeof base64Data === "string" && base64Data.length > 0) {
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          pdfBlob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
        }
      }

      if (pdfBlob) {
        const pdfUrl = URL.createObjectURL(pdfBlob);
        // Open in new tab to avoid content policy / CSP errors with embedded viewer
        if (config.viewAction?.openPdfInNewTab) {
          const newWindow = window.open(pdfUrl, "_blank", "noopener,noreferrer");
          if (newWindow) {
            const revokeWhenClosed = () => {
              const interval = setInterval(() => {
                if (newWindow.closed) {
                  clearInterval(interval);
                  URL.revokeObjectURL(pdfUrl);
                }
              }, 500);
            };
            revokeWhenClosed();
          } else {
            URL.revokeObjectURL(pdfUrl);
            dispatch(setPDFViewerError("Popup blocked. Please allow popups for this site to view the PDF."));
          }
        } else {
          dispatch(openPDFViewer({ pdfUrl, reportName: pdfSettings }));
        }
      } else {
        throw new Error("No PDF data found in response");
      }
    } catch (error) {
      console.error("[SubModule View] Error opening PDF:", error);
      dispatch(
        setPDFViewerError(
          error instanceof Error ? error.message : "Failed to load PDF"
        )
      );
      dispatch(setPDFViewerLoading(false));
    }
  };



  // Determine button position (default: bottom)
  const addButtonPosition = config.addButtonPosition || "bottom";

  // Determine if data is loading for this submodule
  const lookupName = configAny.LookupName || configAny.lookupName || config.lookupName;
  const isLoading = useMemo(() => {
    if (!lookupName || !actualParentField) return false;

    // Show saved rows immediately on edit — do not mask with skeleton while invoice lookup runs
    if (
      isSalesReturnInvoiceSubmoduleLookup(lookupName) &&
      (hasPersistedSubModuleRows(value) ||
        hasPersistedSubModuleRows(data) ||
        savedSalesReturnRowsRef.current.length > 0)
    ) {
      return false;
    }

    // Check loading state for the current parent value
    const parentValue = parentFieldValueString;
    if (!parentValue) return false;

    const lookupKey = `${lookupName}_${parentValue}`;
    return lookupsState.loading[lookupKey] || false;
  }, [
    lookupName,
    actualParentField,
    parentFieldValueString,
    lookupsState.loading,
    value,
    data,
  ]);

  const submoduleAddDisabled =
    (config as any).EnableAddActionForSubModule === false;
  const canAddRows =
    !isReadOnlyView &&
    !submoduleAddDisabled &&
    config.enableAddAction !== false &&
    !config.DisableAddNewButton &&
    !config.DisableAddNewRow;
  const showEmptyState =
    activeRowsCount === 0 && !isLoading && !showTable;
  const defaultReadOnlyEmptyMessage =
    config.emptyMessage ||
    "No records added for this section yet.";
  const defaultAddEmptyMessage =
    config.emptyMessage ||
    "No records found here yet. Click the button below to add a new entry.";
  const removeAllDisabled =
    config.disabled ||
    !!(config.minRows && activeRowsCount <= config.minRows);

  const renderSubmoduleActionButtons = (extraClassName = "") => {
    if (!canAddRows && !(showRemoveAllAction && activeRowsCount > 0)) {
      return null;
    }
    return (
      <div
        className={`flex flex-row items-center justify-between gap-3 w-full ${extraClassName}`}
      >
        {showRemoveAllAction && activeRowsCount > 0 ? (
          <button
            type="button"
            className="border border-red-300 dark:border-red-700 bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-70 disabled:cursor-not-allowed"
            onClick={() => void removeAllRows()}
            disabled={removeAllDisabled}
          >
            {config.removeAllLabel || "Remove All"}
          </button>
        ) : (
          <span className="flex-shrink-0" />
        )}
        {canAddRows ? (
          <button
            type="button"
            className="w-[38%] min-w-[160px] border-2 border-dotted border-primary-600 dark:border-primary-500 bg-white dark:!bg-gray-800 text-primary-600 dark:text-primary-400 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-70 disabled:cursor-not-allowed"
            onClick={addRow}
            disabled={!!(config.maxRows && activeRowsCount >= config.maxRows)}
          >
            + Add New Row
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className={`border overflow-hidden ${(error || externalError) ? "border-2 border-danger-500 dark:border-danger-500" : "border-gray-300 dark:border-gray-600"
        }`}
      style={containerStyles}
      data-field-name={config.name}
    >
      {config.label && (
        <label className="block mb-3 bg-gray-100 dark:bg-gray-700 p-2 text-gray-900 dark:text-gray-100">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      {showEmptyState && (
        <SubModuleEmptyState
          title={canAddRows ? "No Data Found" : "No records found"}
          message={canAddRows ? defaultAddEmptyMessage : defaultReadOnlyEmptyMessage}
          showAddButton={canAddRows}
          onAdd={canAddRows ? addRow : undefined}
          addDisabled={!!(config.maxRows && activeRowsCount >= config.maxRows)}
        />
      )}

      {/* Show table structure when showTable is true or data exists OR when loading */}
      {(showTable || activeRowsCount > 0 || isLoading) && (
        <div className="overflow-x-auto overflow-y-visible p-0 mb-4">
          {/* Add New Row button and Search at the top (if configured) */}
          {addButtonPosition === "top" && (
            <div className="flex flex-col gap-3 mb-3">
              {renderSubmoduleActionButtons()}

              {/* Search input - only show if search is enabled */}
              {config.enableSearch && data.length > 0 && (
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400 dark:text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none transition-colors"
                      title="Clear search"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Search input at top when button is at bottom */}
          {addButtonPosition === "bottom" && config.enableSearch && activeRowsCount > 0 && (
            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400 dark:text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none transition-colors"
                  title="Clear search"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}

          <table className="w-full min-w-[800px] border-collapse text-sm" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                {config.childControls?.map((control: any, index: number) => {
                  // Handle both old format (Label) and new format (label)
                  const label =
                    control.label ||
                    control.Label ||
                    control.name ||
                    control.BindValue;

                  // Check if column should be hidden
                  const isHidden = control.IsHidden === true;

                  const headerStyle = getSubModuleColumnCellStyle(control);

                  return (
                    <th
                      key={index}
                      className={`${isHidden ? "hidden" : ""} bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 py-1 px-2 text-left font-semibold text-sm text-white border-b-0 border-primary-500/30`}
                      style={headerStyle}
                    >
                      {label}
                    </th>
                  );
                })}
                {showActionsColumn && (
                  <th
                    className={`${SUBMODULE_ACTIONS_COLUMN_CLASS} bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 font-semibold text-sm text-white border-b-0 border-primary-500/30`}
                    aria-label="Actions"
                  />
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                // Show loading skeleton rows
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={`loading-${i}`} className="animate-pulse">
                    {config.childControls?.map((control: any, index: number) => (
                      <td key={index} className="p-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                      </td>
                    ))}
                    {showActionsColumn && (
                      <td
                        className={`${SUBMODULE_ACTIONS_COLUMN_CLASS} border-b border-gray-200 dark:border-gray-700`}
                      >
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-8 mx-auto"></div>
                      </td>
                    )}
                  </tr>
                ))
              ) : filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan={(config.childControls?.length || 0) + (showActionsColumn ? 1 : 0)}
                    className="p-0 border-b border-gray-200 dark:border-gray-700"
                  >
                    <div className="py-10 px-4 flex flex-col items-center justify-center text-center">
                      <SubModuleEmptyIcon />
                      <h3 className="text-gray-900 dark:text-gray-100 font-medium text-base">
                        {config.enableSearch && searchTerm
                          ? "No results found"
                          : "No records found"}
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 max-w-sm">
                        {config.enableSearch && searchTerm
                          ? "No results found matching your search."
                          : config.emptyMessage ||
                            "No records added for this section yet."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredData.map((row, filteredIndex) => {
                  // Find the actual index in the original data array
                  const actualRowIndex = data.findIndex((r) => r === row);

                  return (
                    <tr key={actualRowIndex} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:bg-gray-800 bg-transparent`}>
                      {config.childControls?.map(
                        (control: any, fieldIndex: number) => {
                          // Handle both old format (BindValue) and new format (name)
                          const fieldName = control.name || control.BindValue;

                          // Check if column should be hidden
                          const isHidden = control.IsHidden === true;

                          const cellStyle = getSubModuleColumnCellStyle(control);

                          return (
                            <td
                              key={fieldIndex}
                              className={`${isHidden ? "hidden" : ""} p-1 border-b border-gray-200 dark:border-gray-700 align-middle overflow-visible`}
                              style={cellStyle}
                            >
                              <div className="overflow-visible w-full">
                                {renderField(control, actualRowIndex, fieldName)}
                              </div>
                            </td>
                          );
                        }
                      )}
                      {showActionsColumn && (
                        <td
                          className={`${SUBMODULE_ACTIONS_COLUMN_CLASS} border-b border-gray-200 dark:border-gray-700 align-middle`}
                        >
                          <div className="flex items-center justify-center gap-1">
                            {showViewAction && (
                              <button
                                className="bg-transparent dark:bg-transparent text-green-600 dark:text-green-400 p-2 rounded-md border-none cursor-pointer transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center hover:!bg-green-600 dark:hover:!bg-green-600 hover:!text-white dark:hover:!text-white"
                                onClick={() => handleViewInvoice(row, actualRowIndex)}
                                disabled={config.disabled}
                                aria-label="View report"
                                title="View report"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                            )}
                            {showDeleteAction && (
                              <button
                                className="bg-transparent dark:bg-transparent text-gray-700 dark:text-gray-300 p-1 rounded-md border-none cursor-pointer transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center hover:!bg-red-600 dark:hover:!bg-red-600 hover:!text-white dark:hover:!text-white"
                                onClick={() => removeRow(actualRowIndex)}
                                disabled={
                                  config.disabled ||
                                  !!(config.minRows && activeRowsCount <= config.minRows)
                                }
                                aria-label="Remove row"
                                title="Delete"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M3 6h18" />
                                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
            {config.FooterControls &&
              config.FooterControls.length > 0 &&
              activeRowsCount > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
                    {config.FooterControls.map(
                      (footerControl: any, index: number) =>
                        renderFooterField(footerControl, index)
                    )}
                    {showActionsColumn && (
                      <td
                        className={`${SUBMODULE_ACTIONS_COLUMN_CLASS} border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800`}
                      />
                    )}
                  </tr>
                </tfoot>
              )}
          </table>

          {/* Add New Row / Remove All buttons at the bottom (if configured) */}
          {addButtonPosition === "bottom" && renderSubmoduleActionButtons("mt-2")}
        </div>
      )}

      {(error || externalError) && (
        <div className="px-4 py-2 mt-2 mx-2 mb-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900 dark:text-red-100">
              Validation issues detected
            </p>
            <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
              Please check the validation popup for details
            </p>
          </div>
        </div>
      )}

      {config.helperText && !error && (
        <div className="text-gray-500 dark:text-gray-400 text-sm mt-2">{config.helperText}</div>
      )}
    </div>
  );
};

